import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { bookingsService, addonsService, bookingAddonsService, itemsService, coursesService, availabilityService, invoiceService, emailService, superAdminService, auditService, customPriceBreakdownService } from '../../services/api';
import API_CONFIG from '../../config/api';
import { validateAndCompressImage } from '../../utils/imageCompression';
import { sanitizeFileNamePart, downloadUrlAsFile, fetchUrlAsBytes, createStoredZip, triggerBlobDownload } from '../../utils/zipDownload';
import { format } from 'date-fns';
import AddPaymentModal from './AddPaymentModal';
import AddRefundModal from './AddRefundModal';
import PaymentHistory from '../PaymentHistory';
import { CounterInput } from '../ui';
import OtherEditsTab from '../booking/OtherEditsTab';
import EmailInvoiceModal from './EmailInvoiceModal';
import { useIsMobile } from '../../hooks';
import { useAgentPageContext } from '../../agent/AgentProvider';
import {
  getPrimaryRoom,
  getPrimaryBed,
  getPrimaryCourse,
  getAllRooms,
  getAllBeds,
  getAllCourses,
  getAccommodationDisplay,
  getCourseCount
} from '../../utils/bookingHelpers';
import { AlarmClock as AlarmClockIcon, AlertTriangle as AlertTriangleIcon, Ban as BanIcon, Banknote as BanknoteIcon, BarChart3 as BarChart3Icon, BedDouble as BedDoubleIcon, Building2 as Building2Icon, Calendar as CalendarIcon, Contact as ContactIcon, CreditCard as CreditCardIcon, Crown as CrownIcon, DoorOpen as DoorOpenIcon, Flower2 as Flower2Icon, Gift as GiftIcon, Home as HomeIcon, Hotel as HotelIcon, Hourglass as HourglassIcon, Info as InfoIcon, Lightbulb as LightbulbIcon, MessageSquare as MessageSquareIcon, Package as PackageIcon, RefreshCw as RefreshCwIcon, Save as SaveIcon, ScrollText as ScrollTextIcon, ShoppingBag as ShoppingBagIcon, Target as TargetIcon, Trash2 as Trash2Icon, Upload as UploadIcon, User as UserIcon, Wallet as WalletIcon, XCircle as XCircleIcon, History as HistoryIcon } from 'lucide-react';

/**
 * Reusable EditBookingModal component
 * Used across AdminDashboard, AvailabilityCalendar, and other booking management components
 */
function EditBookingModal({ booking, user, onClose, onUpdate, canCheckIn, canCheckOut }) {
  // Tell the agent which booking is open (no-op outside the dashboard)
  useAgentPageContext(booking?.id ? { entity: { type: 'booking', id: booking.id, label: booking.bookingNumber }, hint: 'The booking edit window is open' } : null);

  // Extract data using helper functions for backward compatibility
  const primaryCourse = getPrimaryCourse(booking);
  const primaryRoom = getPrimaryRoom(booking);
  const primaryBed = getPrimaryBed(booking);
  const courseCount = getCourseCount(booking);
  
  // Mobile detection
  const isMobile = useIsMobile();
  // Mobile vertical tab state - which section is open in accordion mode
  const [mobileOpenSection, setMobileOpenSection] = useState('overview');
  
  const [activeSection, setActiveSection] = useState('overview');
  const [bookingDetailsResponse, setBookingDetailsResponse] = useState({ data: null });
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [existingBookingAddons, setExistingBookingAddons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(primaryCourse || null);
  const [editableCourseCount, setEditableCourseCount] = useState(courseCount || 1);
  const [loading, setLoading] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [totalPrice, setTotalPrice] = useState(booking.totalPrice || 0);
  const [originalPrice, setOriginalPrice] = useState(booking.totalPrice || 0);
  // Booking status state
  const [selectedStatus, setSelectedStatus] = useState(booking.status || 'PENDING');
  // Payment-related state
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showAddRefundModal, setShowAddRefundModal] = useState(false);
  const [paymentHistoryKey, setPaymentHistoryKey] = useState(0); // For refreshing payment history
  
  // Guest count state for editing
  const [editableGuestCount, setEditableGuestCount] = useState(booking.guestCount || 1);
  const [isEditingGuestCount, setIsEditingGuestCount] = useState(false);
  
  // User details editing state (for super admin)
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editableUserData, setEditableUserData] = useState({
    name: booking.user?.name || booking.guestName || '',
    email: booking.user?.email || booking.guestEmail || '',
    phone: booking.user?.phone || '',
    gender: booking.user?.gender || '',
    address: booking.user?.address || '',
    city: booking.user?.city || '',
    state: booking.user?.state || '',
    country: booking.user?.country || '',
    pincode: booking.user?.pincode || ''
  });
  
  // Payment amount editing state (for super admin)
  const [isEditingPaymentAmount, setIsEditingPaymentAmount] = useState(false);
  const [editableTotalPrice, setEditableTotalPrice] = useState(booking.totalPrice || 0);
  const [editableFinalAmount, setEditableFinalAmount] = useState(booking.finalAmount || booking.totalPrice || 0);
  
  // Super admin update state
  const [updatingBooking, setUpdatingBooking] = useState(false);
  
  // Delete booking state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Check if user is superadmin
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const isPropertyAdmin = user?.role === 'PROPERTYADMIN';
  
  // Extend booking state
  const [newCheckOutDate, setNewCheckOutDate] = useState(booking.checkOut?.split('T')[0] || '');
  const [extendPrice, setExtendPrice] = useState(0);
  const [calculatingExtendPrice, setCalculatingExtendPrice] = useState(false);
  const [extendPriceError, setExtendPriceError] = useState('');
  const [availabilityCalendar, setAvailabilityCalendar] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  
  // Multi-entity extend state
  const [extendMode, setExtendMode] = useState('bulk'); // 'bulk' or 'individual'
  const [selectedRoomsToExtend, setSelectedRoomsToExtend] = useState([]);
  const [selectedBedsToExtend, setSelectedBedsToExtend] = useState([]);
  const [entityAvailability, setEntityAvailability] = useState({ rooms: {}, beds: {} });
  const [extensionPreview, setExtensionPreview] = useState(null);

  // Room exchange state
  const [availableRooms, setAvailableRooms] = useState([]);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [selectedNewRoom, setSelectedNewRoom] = useState(null);
  const [selectedNewBed, setSelectedNewBed] = useState(null);
  const [calculatingExchangePrice, setCalculatingExchangePrice] = useState(false);
  const [exchangePriceError, setExchangePriceError] = useState('');
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingBeds, setLoadingBeds] = useState(false);
  const [roomExchangeDifference, setRoomExchangeDifference] = useState(0);

  // Early checkout state
  const [earlyCheckOutDate, setEarlyCheckOutDate] = useState('');
  const [processingEarlyCheckOut, setProcessingEarlyCheckOut] = useState(false);

  // Multi-entity early check-in state
  const [earlyCheckinMode, setEarlyCheckinMode] = useState('bulk'); // 'bulk' or 'individual'
  const [selectedRoomsForEarlyCheckin, setSelectedRoomsForEarlyCheckin] = useState([]);
  const [selectedBedsForEarlyCheckin, setSelectedBedsForEarlyCheckin] = useState([]);
  const [earlyCheckinEntityAvailability, setEarlyCheckinEntityAvailability] = useState({ rooms: {}, beds: {} });
  const [earlyCheckinPreview, setEarlyCheckinPreview] = useState(null);
  const [newCheckInDate, setNewCheckInDate] = useState('');
  const [earlyCheckinPrice, setEarlyCheckinPrice] = useState(0);
  const [calculatingEarlyCheckinPrice, setCalculatingEarlyCheckinPrice] = useState(false);
  const [loadingEarlyCheckinAvailability, setLoadingEarlyCheckinAvailability] = useState(false);
  const [earlyCheckinError, setEarlyCheckinError] = useState('');
  const [processingEarlyCheckin, setProcessingEarlyCheckin] = useState(false);

  // Flag to track initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Exchange type state
  const [exchangeType, setExchangeType] = useState('private');

  // Web check-in link copy state
  const [copiedLink, setCopiedLink] = useState(false);

  const mobileButtonRowStyle = {
    display: 'flex',
    gap: isMobile ? '8px' : '12px',
    flexWrap: isMobile ? 'wrap' : 'nowrap',
    width: '100%',
    alignItems: 'stretch'
  };

  const mobileActionButtonStyle = {
    flex: isMobile ? '1 1 160px' : '0 0 auto',
    minWidth: isMobile ? '140px' : 'auto',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    justifyContent: 'center',
    textAlign: 'center',
    lineHeight: 1.2
  };

  // Details tab expandable sections state
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    guestInfo: false,
    accommodation: false,
    pricing: false,
    payments: false,
    courses: false,
    addons: false,
    metadata: false,
    rawJson: false
  });

  // Addon removal confirmation state
  const [addonToRemove, setAddonToRemove] = useState(null);
  const [showAddonRemovalConfirm, setShowAddonRemovalConfirm] = useState(false);
  const [removingAddon, setRemovingAddon] = useState(false);

  // Email invoice state
  const [showEmailInvoiceModal, setShowEmailInvoiceModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // WhatsApp invoice state
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  // Audit Trail state
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState('');

  // Custom Price Breakdowns state
  const [customBreakdowns, setCustomBreakdowns] = useState(null);
  const [loadingCustomBreakdowns, setLoadingCustomBreakdowns] = useState(false);
  const [showAddBreakdownModal, setShowAddBreakdownModal] = useState(false);
  const [selectedBreakdownTarget, setSelectedBreakdownTarget] = useState(null); // { type: 'BOOKED_ROOM', id: 'xxx', name: 'Room 101' }
  const [newBreakdown, setNewBreakdown] = useState({
    name: '',
    amount: '',
    breakdownType: 'DISCOUNT',
    calculationType: 'FIXED',
    value: '',
    reason: ''
  });
  const [savingBreakdown, setSavingBreakdown] = useState(false);

  // Recalculate Price state
  const [recalculateLoading, setRecalculateLoading] = useState(false);
  const [recalculateResult, setRecalculateResult] = useState(null);

  // Recalculate Dates state
  const [recalculateDatesLoading, setRecalculateDatesLoading] = useState(false);
  const [recalculateDatesResult, setRecalculateDatesResult] = useState(null);

  // ID Proof state
  const [idProofs, setIdProofs] = useState([]);
  const [loadingIdProofs, setLoadingIdProofs] = useState(false);
  const [uploadingIdProof, setUploadingIdProof] = useState(false);
  const [selectedIdProofFile, setSelectedIdProofFile] = useState(null);
  const [selectedIdType, setSelectedIdType] = useState('AADHAAR');
  const [idProofDescription, setIdProofDescription] = useState('');
  const [idProofError, setIdProofError] = useState('');
  const [deletingIdProof, setDeletingIdProof] = useState(null);
  const [downloadingIdProof, setDownloadingIdProof] = useState(null);
  const [downloadingAllIdProofs, setDownloadingAllIdProofs] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Debug log
  console.log('EditBookingModal loaded with booking:', booking.id, 'Status:', booking.status, 'Selected:', selectedStatus);

  // Default date validation functions if not provided as props
  const defaultIsSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  const defaultCanCheckIn = (booking) => {
    // SUPERADMIN and PROPERTYADMIN can check-in anytime
    if (user.role === 'SUPERADMIN' || user.role === 'PROPERTYADMIN') {
      return true;
    }
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const checkInDate = new Date(booking.checkIn);
    return defaultIsSameDay(today, checkInDate) || defaultIsSameDay(yesterday, checkInDate);
  };

  const defaultCanCheckOut = (booking) => {
    // SUPERADMIN and PROPERTYADMIN can check-out anytime
    if (user.role === 'SUPERADMIN' || user.role === 'PROPERTYADMIN') {
      return true;
    }
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const checkOutDate = new Date(booking.checkOut);
    return defaultIsSameDay(today, checkOutDate) || defaultIsSameDay(yesterday, checkOutDate);
  };

  // Use provided validation functions or defaults
  const validateCheckIn = canCheckIn || defaultCanCheckIn;
  const validateCheckOut = canCheckOut || defaultCanCheckOut;

  useEffect(() => {
    const loadInitialData = async () => {
      await loadBookingDetails();
      await loadAddons();
      await loadCourses();
      await loadExistingBookingAddons();
      
      // Mark initial load as complete after all data is loaded
      setIsInitialLoad(false);
    };

    loadInitialData();
    
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Load booking details from API
  const loadBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await bookingsService.getById(booking.id);
      console.log('Loaded booking details - Full Response:', response);
      console.log('Response data:', response.data);
      console.log('Guest data:', response.data?.guest);
      console.log('Booked Rooms:', response.data?.bookedRooms?.length);
      console.log('Booked Beds:', response.data?.bookedBeds?.length);
      console.log('Add-ons:', response.data?.addOns?.length);
      setBookingDetailsResponse(response);
    } catch (error) {
      console.error('Error loading booking details:', error);
      toast.error('Failed to load booking details');
      // Set empty data structure to prevent errors
      setBookingDetailsResponse({ 
        data: { 
          bookedRooms: [], 
          bookedBeds: [], 
          bookedCourses: [],
          addOns: [],
          guest: null,
          booking: {},
          pricing: {}
        } 
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAddons = async () => {
    try {
      setLoading(true);
      const response = await addonsService.getAll();
      const addonsData = response.data?.addOns || response.addOns || response.data || response || [];
      setAddons(Array.isArray(addonsData) ? addonsData : []);
    } catch (error) {
      console.error('Error loading addons:', error);
      toast.error('Failed to load addon services');
      setAddons([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      setLoadingCourses(true);
      const response = await coursesService.getAll({ 
        upcoming: 'true',
        limit: 50 
      });
      const coursesData = response.courses || response.data?.courses || [];
      setCourses(Array.isArray(coursesData) ? coursesData : []);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Failed to load courses');
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  const loadExistingBookingAddons = async () => {
    try {
      const response = await bookingAddonsService.getByBooking(booking.id);
      const bookingAddonsData = response.data?.bookingAddOns || response.bookingAddOns || response.data || response || [];
      setExistingBookingAddons(Array.isArray(bookingAddonsData) ? bookingAddonsData : []);
      
      // Convert existing booking add-ons to selected format for editing
      const existingAsSelected = bookingAddonsData.map(bookingAddon => ({
        id: bookingAddon.addOnServiceId,
        name: bookingAddon.addOnService?.name || 'Unknown Service',
        price: bookingAddon.price,
        quantity: bookingAddon.quantity,
        bookingAddonId: bookingAddon.id, // Store the booking addon ID for updates/deletes
        totalPrice: bookingAddon.totalPrice,
        taxAmount: bookingAddon.taxAmount,
        totalWithTax: bookingAddon.totalWithTax,
        itemId: bookingAddon.itemId || null,
        item: bookingAddon.item || null,
        addOnService: bookingAddon.addOnService || null
      }));
      setSelectedAddons(existingAsSelected);
    } catch (error) {
      console.error('Error loading existing booking add-ons:', error);
      // Don't show error toast as this might be normal for bookings without add-ons
      setExistingBookingAddons([]);
    }
  };

  const calculateTotal = useCallback(() => {
    // Separate existing addons (already in original price) from new addons
    const existingAddonIds = existingBookingAddons.map(addon => addon.addOnServiceId);
    const selectedAddonIds = selectedAddons.map(addon => addon.id);
    
    const newAddons = selectedAddons.filter(addon => !existingAddonIds.includes(addon.id));
    const modifiedExistingAddons = selectedAddons.filter(addon => {
      const existingAddon = existingBookingAddons.find(existing => existing.addOnServiceId === addon.id);
      return existingAddon && (existingAddon.quantity !== addon.quantity || existingAddon.price !== addon.price);
    });
    
    // Find removed existing addons (were in existingBookingAddons but not in selectedAddons)
    const removedExistingAddons = existingBookingAddons.filter(existing => 
      !selectedAddonIds.includes(existing.addOnServiceId)
    );

    // Calculate totals
    const newAddonsTotal = newAddons.reduce((sum, addon) => sum + (addon.price * addon.quantity), 0);
    const modifiedAddonsTotal = modifiedExistingAddons.reduce((sum, addon) => {
      const existingAddon = existingBookingAddons.find(existing => existing.addOnServiceId === addon.id);
      const existingTotal = existingAddon ? (existingAddon.price * existingAddon.quantity) : 0;
      const newTotal = addon.price * addon.quantity;
      return sum + (newTotal - existingTotal);
    }, 0);
    
    // Calculate total of removed addons (subtract from original price)
    const removedAddonsTotal = removedExistingAddons.reduce((sum, addon) => sum + (addon.price * addon.quantity), 0);
    
    console.log('Addon calculation debug:', {
      existingAddons: existingBookingAddons.length,
      selectedAddons: selectedAddons.length,
      newAddons: newAddons.length,
      modifiedAddons: modifiedExistingAddons.length,
      removedAddons: removedExistingAddons.length,
      newAddonsTotal,
      modifiedAddonsTotal,
      removedAddonsTotal
    });

    const bookingCourse = getPrimaryCourse(booking);
    const bookingCourseCount = getCourseCount(booking);
    const courseTotal = selectedCourse && selectedCourse.id !== bookingCourse?.id ? (selectedCourse.fee || 0) * editableCourseCount : 0;
    
    // Handle course count changes for existing course
    let courseCountAdjustment = 0;
    if (bookingCourse && selectedCourse && selectedCourse.id === bookingCourse.id) {
      // Same course, but count might have changed
      const originalCourseTotal = (bookingCourse.fee || 0) * bookingCourseCount;
      const newCourseTotal = (bookingCourse.fee || 0) * editableCourseCount;
      courseCountAdjustment = newCourseTotal - originalCourseTotal;
    }
    
    // If replacing an existing course, subtract the original course fee (including original count)
    let adjustedOriginalPrice = originalPrice;
    if (bookingCourse && selectedCourse && selectedCourse.id !== bookingCourse.id) {
      adjustedOriginalPrice = originalPrice - ((bookingCourse.fee || 0) * bookingCourseCount);
    }
    
    // If removing course entirely (selectedCourse is null but there was an original course)
    if (bookingCourse && !selectedCourse) {
      adjustedOriginalPrice = originalPrice - ((bookingCourse.fee || 0) * bookingCourseCount);
    }
    
    // Add extend price if booking is being extended
    const extendTotal = extendPrice || 0;
    
    // Add room exchange difference if room is being changed
    const exchangeTotal = roomExchangeDifference || 0;
    
    setTotalPrice(adjustedOriginalPrice + newAddonsTotal + modifiedAddonsTotal - removedAddonsTotal + courseTotal + courseCountAdjustment + extendTotal + exchangeTotal);
  }, [selectedAddons, existingBookingAddons, selectedCourse, extendPrice, roomExchangeDifference, booking, originalPrice, editableCourseCount]);

  // Recalculate total when dependencies change (but not on initial load)
  useEffect(() => {
    if (!isInitialLoad) {
      calculateTotal();
    }
  }, [calculateTotal, isInitialLoad]);

  const handleCourseSelect = (course) => {
    console.log('Course Selection Debug:', {
      clickedCourse: course ? { id: course.id, name: course.name } : null,
      currentSelectedCourse: selectedCourse ? { id: selectedCourse.id, name: selectedCourse.name } : null,
      originalBookingCourse: booking.course ? { id: booking.course.id, name: booking.course.name } : null
    });

    if (selectedCourse && selectedCourse.id === course.id) {
      // Deselect if clicking the same course - revert to original course
      console.log('Deselecting course, reverting to original');
      setSelectedCourse(booking.course || null);
      // Reset course count to original when reverting
      setEditableCourseCount(booking.courseCount || 1);
    } else {
      // Select new course
      console.log('Selecting new course:', course.name);
      setSelectedCourse(course);
      // When selecting a new course, maintain current course count if reasonable, otherwise reset to 1
      if (!booking.course) {
        // If there was no original course, start with 1
        setEditableCourseCount(1);
      }
      // If there was an original course, keep the current editable count (user might have modified it)
    }
  };

  const handleAddonToggle = (addon) => {
    const existingIndex = selectedAddons.findIndex(a => a.id === addon.id);
    if (existingIndex >= 0) {
      // Show confirmation modal before removing
      setAddonToRemove(addon);
      setShowAddonRemovalConfirm(true);
    } else {
      // For add-ons without default price, prompt for custom price
      let price = addon.price;
      if (!addon.price) {
        const customPrice = prompt(`Enter price for "${addon.name}":`);
        if (customPrice === null) return; // User cancelled
        price = parseFloat(customPrice);
        if (isNaN(price) || price < 0) {
          toast.error('Please enter a valid price');
          return;
        }
      }
      setSelectedAddons([...selectedAddons, { ...addon, price, quantity: 1 }]);
    }
  };

  const confirmAddonRemoval = async () => {
    if (addonToRemove) {
      try {
        setRemovingAddon(true);
        // If this is an existing booking addon (has bookingAddonId), call the delete API
        if (addonToRemove.bookingAddonId) {
          await bookingAddonsService.delete(addonToRemove.bookingAddonId);
          // Remove from existingBookingAddons state so save doesn't try to delete again
          setExistingBookingAddons(prev => prev.filter(ea => ea.id !== addonToRemove.bookingAddonId));
          // Remove from selectedAddons by bookingAddonId for proper isolation
          setSelectedAddons(prev => prev.filter(a => a.bookingAddonId !== addonToRemove.bookingAddonId));
        } else {
          // Newly added addon (not yet saved), just remove from local state
          setSelectedAddons(prev => prev.filter(a => a.id !== addonToRemove.id));
        }
        toast.success(`${addonToRemove.name} removed from booking`);
      } catch (error) {
        console.error('Error deleting booking addon:', error);
        toast.error(`Failed to remove ${addonToRemove.name}`);
      } finally {
        setRemovingAddon(false);
      }
    }
    setShowAddonRemovalConfirm(false);
    setAddonToRemove(null);
  };

  const cancelAddonRemoval = () => {
    setShowAddonRemovalConfirm(false);
    setAddonToRemove(null);
  };

  const handleAddonQuantityChange = (uniqueKey, quantity) => {
    setSelectedAddons(prev => prev.map(addon =>
      (addon.bookingAddonId === uniqueKey || (!addon.bookingAddonId && addon.id === uniqueKey))
        ? { ...addon, quantity: Math.max(1, parseInt(quantity) || 1) }
        : addon
    ));
  };

  const handleAddonPriceChange = (uniqueKey, price) => {
    const numPrice = parseFloat(price);
    if (isNaN(numPrice) || numPrice < 0) return;

    setSelectedAddons(prev => prev.map(addon =>
      (addon.bookingAddonId === uniqueKey || (!addon.bookingAddonId && addon.id === uniqueKey))
        ? { ...addon, price: numPrice }
        : addon
    ));
  };

  // Payment handlers
  const handleAddPayment = () => {
    setShowAddPaymentModal(true);
  };

  const handleAddRefund = () => {
    setShowAddRefundModal(true);
  };

  const handlePaymentAdded = (newPayment) => {
    // Refresh payment history by updating the key
    setPaymentHistoryKey(prev => prev + 1);
    // Optionally update booking status if needed
    if (onUpdate) {
      onUpdate();
    }
  };

  const handleRefundAdded = (newRefund) => {
    // Refresh payment history by updating the key
    setPaymentHistoryKey(prev => prev + 1);
    // Optionally update booking status if needed
    if (onUpdate) {
      onUpdate();
    }
  };

  const handleRefundPayment = async (payment) => {
    // This would typically open a refund modal or confirmation
    const confirmed = window.confirm(`Are you sure you want to refund ₹${payment.amount}?`);
    if (confirmed) {
      try {
        // Call refund API (implementation depends on your needs)
        toast.info('Refund functionality would be implemented here');
      } catch (error) {
        toast.error('Failed to process refund');
      }
    }
  };

  const updateBookingAddons = async () => {
    try {
      // Get current booking add-ons
      const currentAddons = existingBookingAddons.map(ba => ba.id);
      const newAddons = selectedAddons.filter(sa => !sa.bookingAddonId);
      const updatedAddons = selectedAddons.filter(sa => sa.bookingAddonId);
      const deletedAddons = existingBookingAddons.filter(ea => 
        !selectedAddons.find(sa => sa.bookingAddonId === ea.id)
      );

      console.log('Addon changes:', {
        newAddons: newAddons.length,
        updatedAddons: updatedAddons.length,
        deletedAddons: deletedAddons.length,
        currentTotalPrice: totalPrice
      });

      // Delete removed add-ons
      for (const deletedAddon of deletedAddons) {
        console.log(`Deleting addon ${deletedAddon.id}`);
        await bookingAddonsService.delete(deletedAddon.id);
      }

      // Create new add-ons
      for (const newAddon of newAddons) {
        console.log(`Creating new addon ${newAddon.id}`);
        await bookingAddonsService.create({
          bookingId: booking.id,
          addOnServiceId: newAddon.id,
          quantity: newAddon.quantity,
          price: newAddon.price
        });
      }

      // Update existing add-ons
      for (const updatedAddon of updatedAddons) {
        console.log(`Updating addon ${updatedAddon.bookingAddonId}`);
        await bookingAddonsService.update(updatedAddon.bookingAddonId, {
          quantity: updatedAddon.quantity,
          price: updatedAddon.price
        });
      }

      // Note: Booking totals are already handled in the main handleSave function
      // The totalPrice state already includes addon changes, so no need to update booking again
      if (deletedAddons.length > 0 || newAddons.length > 0 || updatedAddons.length > 0) {
        console.log(`Addon operations completed. Booking totals already updated in main save flow with totalPrice = ${totalPrice}`);
      }
    } catch (error) {
      console.error('Error updating booking add-ons:', error);
      toast.error('Failed to update add-on services');
      throw error;
    }
  };

  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      await invoiceService.downloadInvoice(booking.id);
      toast.success('Invoice downloaded successfully!');
    } catch (error) {
      console.error('Failed to download invoice:', error);
      toast.error('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleEmailInvoice = () => {
    setShowEmailInvoiceModal(true);
  };

  const handleSendEmail = async (emailData) => {
    setSendingEmail(true);
    try {
      let response;
      
      // Check if custom email or using booking email
      const bookingEmail = booking?.user?.email || booking?.guestEmail || '';
      const isCustomEmail = emailData.email !== bookingEmail;
      
      if (isCustomEmail || emailData.subject || emailData.htmlContent) {
        // Use custom invoice email endpoint
        response = await emailService.sendCustomInvoiceEmail({
          bookingId: booking.id,
          email: emailData.email,
          subject: emailData.subject,
          htmlContent: emailData.htmlContent
        });
      } else {
        // Use standard invoice email endpoint
        response = await emailService.sendInvoiceEmail(booking.id);
      }
      
      if (response.success) {
        toast.success(`Invoice email sent successfully to ${emailData.email}!`);
        setShowEmailInvoiceModal(false);
        
        // Log audit action
        try {
          await auditService.logAction({
            bookingId: booking.id,
            action: 'EMAIL_INVOICE_SENT',
            metadata: {
              recipientEmail: emailData.email,
              queueId: response.queueId,
              customEmail: isCustomEmail,
              timestamp: new Date().toISOString()
            }
          });
        } catch (auditError) {
          console.error('Failed to log audit action:', auditError);
          // Don't fail the operation if audit logging fails
        }
      } else {
        throw new Error(response.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send invoice email:', error);
      toast.error(error.message || 'Failed to send invoice email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCloseEmailModal = () => {
    setShowEmailInvoiceModal(false);
  };

  const handleWhatsAppInvoice = () => {
    const userPhone = booking?.user?.phone || booking?.guestPhone || '';
    // Strip any non-digit characters for autofill
    const cleanPhone = userPhone.replace(/\D/g, '');
    setWhatsappNumber(cleanPhone);
    setShowWhatsAppModal(true);
  };

  const handleSendWhatsAppInvoice = async () => {
    const cleaned = whatsappNumber.replace(/\D/g, '');
    if (!cleaned) {
      toast.error('Please enter a valid WhatsApp number.');
      return;
    }
    setSendingWhatsApp(true);
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/whatsapp/generate-and-send-invoice`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          bookingId: booking.id,
          whatsappNumber: cleaned
        })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Invoice sent via WhatsApp to ${cleaned}!`);
        setShowWhatsAppModal(false);
      } else {
        throw new Error(data?.message || 'Failed to send WhatsApp invoice');
      }
    } catch (error) {
      console.error('Failed to send WhatsApp invoice:', error);
      toast.error(error.message || 'Failed to send invoice via WhatsApp. Please try again.');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Date validation for status changes
      if (selectedStatus !== booking.status) {
        if (selectedStatus === 'CHECKEDIN') {
          if (!validateCheckIn(booking)) {
            const checkInDate = new Date(booking.checkIn).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
            toast.error(`Check-in status can only be set on the scheduled check-in date: ${checkInDate}`);
            setSaving(false);
            return;
          }
        }
        
        if (selectedStatus === 'CHECKEDOUT') {
          if (!validateCheckOut(booking)) {
            const checkOutDate = new Date(booking.checkOut).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
            toast.error(`Check-out status can only be set on the scheduled check-out date: ${checkOutDate}`);
            setSaving(false);
            return;
          }
        }
      }

      // Prepare update data - only include fields allowed by updateBookingSchema
      const updateData = {
        totalPrice: totalPrice,
        // Update status if changed
        ...(selectedStatus !== booking.status && { status: selectedStatus }),
        // Update course count if changed, or reset to 0 when removing course
        ...((editableCourseCount !== booking.courseCount || (!selectedCourse && booking.course)) && {
          courseCount: selectedCourse ? editableCourseCount : 0
        }),
        // Update guest count if changed
        ...(editableGuestCount !== booking.guestCount && {
          guestCount: editableGuestCount
        }),
        // Update checkout date if extended (convert to ISO datetime format)
        ...(newCheckOutDate && newCheckOutDate !== booking.checkOut?.split('T')[0] && { 
          checkOut: new Date(newCheckOutDate + 'T11:00:00.000Z').toISOString() 
        }),
        // Update room/bed if exchanged - check both roomId and room.id for compatibility
        ...(selectedNewRoom && selectedNewRoom.id !== (booking.roomId || booking.room?.id) && { 
          roomId: selectedNewRoom.id 
        }),
        ...(selectedNewBed && selectedNewBed.id !== (booking.bedId || booking.bed?.id) && { 
          bedId: selectedNewBed.id 
        }),
      };

      // Handle course removal separately using the dedicated endpoint first
      let courseRemovedSeparately = false;
      if (booking.course && !selectedCourse) {
        console.log('Course removal detected - using dedicated deallocation endpoint');
        try {
          await bookingsService.deallocateCourse(booking.id);
          courseRemovedSeparately = true;
          console.log('Course deallocated successfully');
        } catch (deallocateError) {
          console.warn('Deallocation endpoint failed, will try including courseId in update:', deallocateError.message);
          // If dedicated endpoint fails, try including courseId in the main update
          updateData.courseId = null;
        }
      } else if (selectedCourse && selectedCourse.id !== booking.course?.id) {
        // Adding or changing course
        updateData.courseId = selectedCourse.id;
      }

      // Validate price calculation for course removal
      if (booking.course && !selectedCourse) {
        const expectedPriceReduction = (booking.course.fee || 0) * (booking.courseCount || 1);
        const calculatedReduction = originalPrice - totalPrice;
        console.log('Price validation:', {
          originalPrice,
          currentCalculatedPrice: totalPrice,
          expectedReduction: expectedPriceReduction,
          actualReduction: calculatedReduction,
          isCorrect: Math.abs(calculatedReduction - expectedPriceReduction) < 0.01,
          courseRemovedSeparately
        });
        
        if (Math.abs(calculatedReduction - expectedPriceReduction) > 0.01) {
          console.warn('Price calculation mismatch detected');
        }
      }

      console.log('Booking Update Debug:', {
        selectedCourseId: selectedCourse?.id,
        originalCourseId: booking.course?.id,
        courseChanged: selectedCourse?.id !== booking.course?.id,
        updateData: updateData,
        willUpdateCourse: updateData.hasOwnProperty('courseId')
      });

      console.log('Room Exchange Debug:', {
        selectedNewRoom: selectedNewRoom,
        selectedNewBed: selectedNewBed,
        bookingRoomId: booking.roomId,
        bookingRoomObjId: booking.room?.id,
        bookingBedId: booking.bedId,
        bookingBedObjId: booking.bed?.id,
        roomWillUpdate: selectedNewRoom && selectedNewRoom.id !== (booking.roomId || booking.room?.id),
        bedWillUpdate: selectedNewBed && selectedNewBed.id !== (booking.bedId || booking.bed?.id)
      });

      console.log('Sending update data:', updateData);

      // API Validation checks to prevent 400 errors
      if (updateData.roomId && updateData.bedId) {
        console.error('Validation Error: Cannot specify both roomId and bedId in the same request');
        throw new Error('Cannot update both room and bed in the same operation');
      }

      if (updateData.bedId && (updateData.courseCount > 1 || updateData.guestCount > 1)) {
        console.error('Validation Error: Shared accommodations (bedId) must have courseCount and guestCount = 1');
        throw new Error('Shared accommodations can only have 1 course and 1 guest');
      }

      // Debug: Log the exact JSON being sent to help diagnose 400 errors
      console.log('DEBUG: Update request details:', {
        bookingId: booking.id,
        endpoint: `/api/bookings/${booking.id}`,
        method: 'PUT',
        bodyKeys: Object.keys(updateData),
        bodyData: JSON.stringify(updateData, null, 2)
      });

      // Apply the update to booking (single API call)
      console.log('Sending single update request...');
      const response = await bookingsService.update(booking.id, updateData);
      
      if (!response.success && !response.data) {
        throw new Error('Booking update failed');
      }
      
      console.log('Booking update completed successfully:', response);
      
      // Update booking add-ons
      await updateBookingAddons();
      
      // Fetch updated booking data to get recalculated prices from backend
      if (newCheckOutDate && newCheckOutDate !== booking.checkOut?.split('T')[0]) {
        console.log('Fetching updated booking data with recalculated prices...');
        try {
          const updatedBookingResponse = await bookingsService.getById(booking.id);
          if (updatedBookingResponse.data) {
            // Update local booking state with backend's calculated prices
            const updatedBooking = updatedBookingResponse.data;
            console.log('Updated prices from backend:', {
              totalPrice: updatedBooking.totalPrice,
              finalAmount: updatedBooking.finalAmount,
              checkOut: updatedBooking.checkOut
            });
            
            // Update booking details response
            setBookingDetailsResponse(updatedBookingResponse);
            
            // Update the booking prop with fresh data (this will trigger parent re-render)
            // Note: This assumes the parent component will refresh its data
            // If you need to update local state immediately, you can set:
            setOriginalPrice(updatedBooking.totalPrice || booking.totalPrice);
            setEditableTotalPrice(updatedBooking.totalPrice || booking.totalPrice);
            setEditableFinalAmount(updatedBooking.finalAmount || updatedBooking.totalPrice || booking.finalAmount);
            
            // Reset extend booking state
            setExtendPrice(0);
            setExtendPriceError('');
            setNewCheckOutDate(updatedBooking.checkOut?.split('T')[0] || '');
          }
        } catch (fetchError) {
          console.warn('Could not fetch updated booking data:', fetchError);
          // Continue anyway - the parent refresh will update the data
        }
      }
      
      toast.success('Booking updated successfully');
      // Call the update callback instead of refreshing the page
      onUpdate();
    } catch (error) {
      console.error('Error updating booking:', error);
      
      // Provide specific error messages based on error type
      if (error.message && error.message.includes('Room is not available')) {
        toast.error(
          'Cannot update booking: The selected room/bed is not available for the booking dates due to conflicting bookings. ' +
          'Please choose a different room or dates that don\'t overlap with existing bookings.',
          { autoClose: 8000 }
        );
      } else if (error.message && error.message.includes('conflict')) {
        toast.error(
          'Cannot update booking due to scheduling conflicts. Please check availability and try again.',
          { autoClose: 6000 }
        );
      } else if (error.status === 409 || (error.response && error.response.status === 409)) {
        toast.error(
          'Booking update conflict: The selected room/bed is no longer available. Please refresh and try again.',
          { autoClose: 6000 }
        );
      } else {
        toast.error('Failed to update booking. Please try again or contact support if the issue persists.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Super admin update function
  const handleSuperAdminUpdate = async () => {
    if (!isSuperAdmin && !isPropertyAdmin) {
      toast.error('You do not have permission to perform this action');
      return;
    }

    setUpdatingBooking(true);
    
    try {
      const updateData = {};
      
      // Check if user data has changed
      const userChanged =
        editableUserData.name !== (booking.user?.name || booking.guestName || '') ||
        editableUserData.email !== (booking.user?.email || booking.guestEmail || '') ||
        editableUserData.phone !== (booking.user?.phone || '') ||
        editableUserData.gender !== (booking.user?.gender || '') ||
        editableUserData.address !== (booking.user?.address || '') ||
        editableUserData.city !== (booking.user?.city || '') ||
        editableUserData.state !== (booking.user?.state || '') ||
        editableUserData.country !== (booking.user?.country || '') ||
        editableUserData.pincode !== (booking.user?.pincode || '');
        
      // Check if payment data has changed
      const paymentChanged = 
        editableTotalPrice !== (booking.totalPrice || 0) ||
        editableFinalAmount !== (booking.finalAmount || booking.totalPrice || 0) ||
        editableGuestCount !== (booking.guestCount || 1);
      
      if (!userChanged && !paymentChanged) {
        toast.info('No changes detected');
        return;
      }
      
      // Add user updates if changed
      if (userChanged) {
        updateData.user = {
          name: editableUserData.name,
          email: editableUserData.email,
          phone: editableUserData.phone,
          gender: editableUserData.gender,
          address: editableUserData.address || null,
          city: editableUserData.city || null,
          state: editableUserData.state || null,
          country: editableUserData.country || null,
          pincode: editableUserData.pincode || null
        };
      }
      
      // Add booking updates if changed
      if (paymentChanged) {
        updateData.booking = {
          totalPrice: editableTotalPrice,
          finalAmount: editableFinalAmount,
          guestCount: editableGuestCount
        };
      }
      
      // Add reason for the update
      updateData.reason = 'Super admin manual update via booking modal';
      updateData.adminNote = `Updated by ${user.name} (${user.email})`;
      
      console.log('Updating booking with super admin service:', updateData);
      
      const response = await superAdminService.updateBookingAndUser(booking.id, updateData);
      
      toast.success('Booking and user data updated successfully');
      
      // Reset editing states
      setIsEditingUser(false);
      setIsEditingPaymentAmount(false);
      setIsEditingGuestCount(false);
      
      // Call update callback to refresh data
      if (onUpdate) {
        onUpdate();
      }
      
    } catch (error) {
      console.error('Error updating booking via super admin:', error);
      
      // Handle specific error cases
      if (error.error) {
        toast.error(error.error);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update booking. Please try again.');
      }
    } finally {
      setUpdatingBooking(false);
    }
  };

  // Handle click outside modal to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  const handlePermanentDelete = async () => {
    if (!isSuperAdmin) {
      toast.error('Insufficient permissions. Only Super Admins can permanently delete bookings.');
      return;
    }

    setDeleting(true);
    try {
      const response = await bookingsService.permanentDelete(booking.id);
      
      toast.success(`Booking ${booking.bookingNumber || booking.id} permanently deleted successfully`);
      
      // Log the deletion details
      console.log('Permanent deletion completed:', response.data);
      
      if (onUpdate) {
        onUpdate(); // Refresh the booking list
      }
      onClose(); // Close the modal
      
    } catch (error) {
      console.error('Error permanently deleting booking:', error);
      
      if (error.error === 'Booking not found') {
        toast.error('Booking not found or already deleted');
      } else if (error.error === 'Insufficient permissions.') {
        toast.error('Insufficient permissions. Only Super Admins can permanently delete bookings.');
      } else {
        toast.error(`Failed to delete booking: ${error.error || error.message || 'Unknown error'}`);
      }
    } finally {
      setDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateNights = () => {
    const checkIn = new Date(booking.checkIn);
    const checkOut = new Date(booking.checkOut);
    return Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  };

  // Calculate additional cost for extending booking
  const calculateExtendPrice = async (newCheckOut) => {
    if (!newCheckOut) {
      setExtendPrice(0);
      setExtendPriceError('');
      return;
    }

    const currentCheckOut = new Date(booking.checkOut);
    const proposedCheckOut = new Date(newCheckOut + 'T11:00:00.000Z');
    
    // Validate that new checkout is after current checkout
    if (proposedCheckOut <= currentCheckOut) {
      setExtendPriceError('New checkout date must be after current checkout date');
      setExtendPrice(0);
      return;
    }

    // Validate that new checkout is not too far in the future (MAX 7 DAYS)
    const maxExtendDays = 7; // Maximum 7 days extension
    const daysDifference = Math.ceil((proposedCheckOut - currentCheckOut) / (1000 * 60 * 60 * 24));
    if (daysDifference > maxExtendDays) {
      setExtendPriceError(`Cannot extend more than ${maxExtendDays} days at a time`);
      setExtendPrice(0);
      return;
    }

    try {
      setCalculatingExtendPrice(true);
      setExtendPriceError('');

      // Check day-by-day availability for each night of the extension
      console.log('Checking day-by-day availability for extension...');
      
      const unavailableDates = [];
      
      // Check each day in the extension period
      for (let i = 0; i < daysDifference; i++) {
        const dayStart = new Date(currentCheckOut);
        dayStart.setDate(dayStart.getDate() + i);
        
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);
        
        const availabilityParams = {
          checkIn: dayStart.toISOString(),
          checkOut: dayEnd.toISOString(),
          excludeBookingId: booking.id
        };

        try {
          let availabilityResponse;
          // Use helper functions to get room/bed IDs from new booking format
          if (primaryBed?.id) {
            availabilityResponse = await fetch(`${API_CONFIG.BASE_URL}/api/availability/bed/${primaryBed.id}?${new URLSearchParams(availabilityParams)}`);
          } else if (primaryRoom?.id) {
            availabilityResponse = await fetch(`${API_CONFIG.BASE_URL}/api/availability/room/${primaryRoom.id}?${new URLSearchParams(availabilityParams)}`);
          } else {
            throw new Error('No room or bed information found for availability check');
          }

          if (availabilityResponse?.ok) {
            const availabilityData = await availabilityResponse.json();
            
            // Check both the direct isAvailable flag and the data.isAvailable
            const isAvailable = availabilityData.data?.isAvailable !== false && availabilityData.isAvailable !== false;
            
            if (!isAvailable) {
              unavailableDates.push(dayStart.toISOString().split('T')[0]);
            }
          }
        } catch (dayError) {
          console.warn(`Failed to check availability for ${dayStart.toISOString().split('T')[0]}:`, dayError);
        }
      }

      // If any dates are unavailable, show error with specific dates
      if (unavailableDates.length > 0) {
        const datesList = unavailableDates.map(d => new Date(d).toLocaleDateString()).join(', ');
        setExtendPriceError(
          `Cannot extend to ${new Date(newCheckOut).toLocaleDateString()} because the following dates are not available: ${datesList}. ` +
          'Please select a different checkout date.'
        );
        setExtendPrice(0);
        return;
      }

      // All dates are available, calculate price for the extended period
      // Use helper functions to get room/bed IDs from new booking format
      const priceData = {
        checkIn: booking.checkOut, // Start from current checkout (already in ISO format)
        checkOut: proposedCheckOut.toISOString(), // Convert to ISO format
        roomId: primaryRoom?.id || null,
        bedId: primaryBed?.id || null,
        courseId: null, // Don't include course price for extensions - only charge accommodation
        addOns: [], // No addons for extension calculation
        isInterState: false
      };

      console.log('Calculating extend price with data:', priceData);

      const response = await bookingsService.calculatePrice(priceData);
      
      console.log('Price calculation response:', response);
      
      if (response.success && response.data) {
        const additionalCost = response.data.totalPrice || response.data.totalAmount || 0;
        setExtendPrice(additionalCost);
        setExtendPriceError('');
      } else {
        throw new Error('Failed to calculate extension price');
      }
    } catch (error) {
      console.error('Error calculating extend price:', error);
      
      // Provide specific error messages based on error type
      if (error.message && error.message.includes('Room is not available')) {
        setExtendPriceError(
          'The room/bed is not available for the selected extension period due to conflicting bookings. ' +
          'Please choose a different checkout date.'
        );
      } else if (error.message && error.message.includes('conflict')) {
        setExtendPriceError(
          'Cannot extend to the selected date due to scheduling conflicts. ' +
          'Please choose a different checkout date.'
        );
      } else {
        setExtendPriceError('Failed to calculate extension price. Please try again or contact support if the issue persists.');
      }
      setExtendPrice(0);
    } finally {
      setCalculatingExtendPrice(false);
    }
  };

  // Handle extend booking date change
  const handleExtendDateChange = (date) => {
    setNewCheckOutDate(date);
    if (date) {
      calculateExtendPrice(date);
    } else {
      setExtendPrice(0);
      setExtendPriceError('');
    }
  };

  // Generate calendar options for extend booking (max 7 days)
  const generateDateOptions = () => {
    const options = [];
    const today = new Date();
    const currentCheckOut = new Date(booking.checkOut);
    
    // Start from the day after current checkout
    const startDate = new Date(currentCheckOut.getTime() + 24*60*60*1000);
    
    for (let i = 0; i < 7; i++) { // Show next 7 days only
      const date = new Date(startDate.getTime() + i * 24*60*60*1000);
      const dateStr = date.toISOString().split('T')[0];
      
      // Check if this specific date is available from the day-by-day availability check
      const dayAvailability = availabilityCalendar.find(av => av.date === dateStr);
      const isAvailable = dayAvailability ? dayAvailability.isAvailable : true;
      const conflicts = dayAvailability ? dayAvailability.conflicts : [];
      
      // Format the date nicely
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dayName = dayNames[date.getDay()];
      const monthName = monthNames[date.getMonth()];
      const dayNum = date.getDate();
      
      options.push({
        date: dateStr,
        formatted: `${dayName}, ${monthName} ${dayNum}`,
        available: isAvailable,
        conflicts: conflicts,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isPast: date < today
      });
    }
    
    return options;
  };

  // Load availability calendar for next 7 days (day-by-day check with progressive rendering)
  const loadAvailabilityCalendar = useCallback(async () => {
    try {
      setLoadingAvailability(true);
      setAvailabilityCalendar([]); // Clear existing data
      
      const startDate = new Date(booking.checkOut);
      const maxDays = 7; // Check next 7 days only
      
      // Check availability for each day individually (in series)
      for (let i = 0; i < maxDays; i++) {
        const dayStart = new Date(startDate);
        dayStart.setDate(dayStart.getDate() + i);
        
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);
        
        const availabilityParams = {
          checkIn: dayStart.toISOString(),
          checkOut: dayEnd.toISOString(),
          excludeBookingId: booking.id
        };

        try {
          let availabilityResponse;
          // Use helper functions to get room/bed IDs from new booking format
          if (primaryBed?.id) {
            availabilityResponse = await fetch(`${API_CONFIG.BASE_URL}/api/availability/bed/${primaryBed.id}?${new URLSearchParams(availabilityParams)}`);
          } else if (primaryRoom?.id) {
            availabilityResponse = await fetch(`${API_CONFIG.BASE_URL}/api/availability/room/${primaryRoom.id}?${new URLSearchParams(availabilityParams)}`);
          }

          const dayData = {
            date: dayStart.toISOString().split('T')[0],
            isAvailable: false,
            conflicts: []
          };

          if (availabilityResponse?.ok) {
            const availabilityData = await availabilityResponse.json();
            dayData.isAvailable = availabilityData.data?.isAvailable !== false && availabilityData.isAvailable !== false;
            dayData.conflicts = availabilityData.conflicts || [];
          }

          // Update calendar progressively - add one day at a time
          setAvailabilityCalendar(prev => [...prev, dayData]);
          
        } catch (error) {
          console.warn(`Failed to check availability for ${dayStart.toISOString().split('T')[0]}:`, error);
          // Still add the day but mark as unavailable
          setAvailabilityCalendar(prev => [...prev, {
            date: dayStart.toISOString().split('T')[0],
            isAvailable: false,
            conflicts: []
          }]);
        }
      }
      
    } catch (error) {
      console.error('Error loading availability calendar:', error);
      setAvailabilityCalendar([]);
    } finally {
      setLoadingAvailability(false);
    }
  }, [booking.id, booking.checkOut, primaryBed?.id, primaryRoom?.id]);

  // Load availability calendar when component mounts or section changes to 'extend'
  useEffect(() => {
    if (activeSection === 'extend') {
      loadAvailabilityCalendar();
    }
  }, [activeSection, booking.id]);

  // Load audit logs when audit trail tab is activated
  useEffect(() => {
    if (activeSection === 'audit-trail') {
      loadAuditLogs();
    }
  }, [activeSection, booking.id]);

  // Load custom price breakdowns when custom breakdowns tab is activated
  useEffect(() => {
    if (activeSection === 'custom-breakdowns' && isSuperAdmin) {
      loadCustomBreakdowns();
    }
  }, [activeSection, booking.id, isSuperAdmin]);

  // Load ID proofs when ID proof tab is activated
  useEffect(() => {
    if (activeSection === 'id-proof') {
      loadIdProofs();
    }
  }, [activeSection, booking.id]);

  // Function to load audit logs for this booking
  const loadAuditLogs = async () => {
    if (!booking?.id) return;
    
    setLoadingAuditLogs(true);
    setAuditLogsError('');
    
    try {
      const response = await auditService.getByBooking(booking.id, {
        limit: 100,
        sortOrder: 'desc'
      });
      
      if (response.data?.auditLogs) {
        setAuditLogs(response.data.auditLogs);
      } else {
        setAuditLogs([]);
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setAuditLogsError('Failed to load audit logs. Please try again.');
      setAuditLogs([]);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  // ===== ID PROOF FUNCTIONS =====

  // Function to load ID proofs for this booking
  const loadIdProofs = async () => {
    if (!booking?.id) return;
    
    setLoadingIdProofs(true);
    setIdProofError('');
    
    try {
      const response = await bookingsService.getIdProofs(booking.id);
      
      if (response.success && response.data?.idProofImages) {
        setIdProofs(response.data.idProofImages);
      } else {
        setIdProofs([]);
      }
    } catch (error) {
      console.error('Error loading ID proofs:', error);
      setIdProofError('Failed to load ID proofs. Please try again.');
      setIdProofs([]);
    } finally {
      setLoadingIdProofs(false);
    }
  };

  // Function to upload ID proof
  const handleUploadIdProof = async () => {
    if (!selectedIdProofFile) {
      setIdProofError('Please select a file');
      return;
    }

    setUploadingIdProof(true);
    setIdProofError('');

    try {
      // Validate and compress the image (1MB limit to match server configuration)
      const { file: processedFile, wasCompressed, error: validationError } = await validateAndCompressImage(
        selectedIdProofFile,
        {
          maxSizeMB: 0.9, // 0.9MB to stay under 1MB server limit
          maxWidthOrHeight: 1920,
          quality: 0.8
        }
      );

      if (validationError || !processedFile) {
        setIdProofError(validationError || 'Failed to process image');
        setUploadingIdProof(false);
        return;
      }

      // Show compression notification
      if (wasCompressed) {
        toast.info(`Image compressed from ${(selectedIdProofFile.size / 1024).toFixed(0)}KB to ${(processedFile.size / 1024).toFixed(0)}KB`);
      }

      const formData = new FormData();
      formData.append('idProof', processedFile);
      formData.append('idType', selectedIdType);
      if (idProofDescription) {
        formData.append('description', idProofDescription);
      }

      const response = await bookingsService.uploadIdProof(booking.id, formData);
      
      if (response.success) {
        toast.success('ID proof uploaded successfully!');
        // Reset form
        setSelectedIdProofFile(null);
        setIdProofDescription('');
        setSelectedIdType('AADHAAR');
        // Clear file input
        const fileInput = document.getElementById('id-proof-file-input');
        if (fileInput) fileInput.value = '';
        // Reload ID proofs list
        loadIdProofs();
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
      setIdProofError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploadingIdProof(false);
    }
  };

  // Function to handle file selection
  const handleIdProofFileChange = (e) => {
    const file = e.target.files?.[0];
    
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setIdProofError('Only image files are allowed');
        setSelectedIdProofFile(null);
        return;
      }

      // Check if file is larger than 1MB and show warning
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 0.9) {
        setIdProofError(`File is ${fileSizeMB.toFixed(2)}MB. It will be automatically compressed before upload.`);
      } else {
        setIdProofError('');
      }

      setSelectedIdProofFile(file);
    }
  };

  // Function to delete ID proof
  const handleDeleteIdProof = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this ID proof image? This action cannot be undone.')) {
      return;
    }

    setDeletingIdProof(imageId);
    setIdProofError('');

    try {
      const response = await bookingsService.deleteIdProof(booking.id, imageId);
      
      if (response.success) {
        toast.success('ID proof deleted successfully');
        // Remove from state
        setIdProofs((prev) => prev.filter((proof) => proof.id !== imageId));
      }
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete ID proof';
      setIdProofError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDeletingIdProof(null);
    }
  };

  // Helpers for download file/folder naming
  const getIdProofGuestName = () => sanitizeFileNamePart(
    booking.user?.name || booking.guestName || 'Guest',
    'Guest'
  );
  const getIdProofBookingRef = () => sanitizeFileNamePart(
    booking.bookingNumber || booking.id || 'booking',
    'booking'
  );
  const getIdProofFileName = (proof) => sanitizeFileNamePart(
    proof?.fileName || `${proof?.idType || 'id-proof'}.jpg`,
    'id-proof'
  );

  // Function to download a single ID proof image
  // Saved as "<GuestName>_<original image name>"
  const handleDownloadIdProof = async (proof) => {
    if (!proof?.url) {
      toast.error('Image URL is not available for download');
      return;
    }

    const fileName = `${getIdProofGuestName()}_${getIdProofFileName(proof)}`;
    setDownloadingIdProof(proof.id);

    try {
      await downloadUrlAsFile(proof.url, fileName);
      toast.success('Image downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Direct download was blocked by the image host. Opened the image instead — right-click and choose Save.');
      window.open(proof.url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloadingIdProof(null);
    }
  };

  // Function to download all ID proof images as a single ZIP containing
  // one folder named "<GuestName>_<BookingNumber>"
  const handleDownloadAllIdProofs = async () => {
    if (idProofs.length === 0 || downloadingAllIdProofs) return;

    const guestName = getIdProofGuestName();
    const folderName = `${guestName}_${getIdProofBookingRef()}`;
    setDownloadingAllIdProofs(true);

    try {
      const usedNames = new Set();
      const entries = [];
      let failedCount = 0;

      for (const proof of idProofs) {
        let fileName = `${guestName}_${getIdProofFileName(proof)}`;
        if (usedNames.has(fileName)) {
          const dotIndex = fileName.lastIndexOf('.');
          let counter = 2;
          let candidate = fileName;
          while (usedNames.has(candidate)) {
            candidate = dotIndex > 0
              ? `${fileName.slice(0, dotIndex)}_(${counter})${fileName.slice(dotIndex)}`
              : `${fileName}_(${counter})`;
            counter++;
          }
          fileName = candidate;
        }
        usedNames.add(fileName);

        try {
          const data = await fetchUrlAsBytes(proof.url);
          entries.push({ name: `${folderName}/${fileName}`, data });
        } catch (fetchError) {
          console.error(`Failed to fetch ID proof ${proof.id}:`, fetchError);
          failedCount++;
        }
      }

      if (entries.length === 0) {
        toast.error('Could not download any image. The image host may be blocking downloads — try opening each image with View instead.');
        return;
      }

      triggerBlobDownload(createStoredZip(entries), `${folderName}_IDProofs.zip`);

      if (failedCount > 0) {
        toast.success(`Downloaded ${entries.length} of ${idProofs.length} images (${failedCount} blocked by the image host)`);
      } else {
        toast.success(`Downloaded ${entries.length} image${entries.length === 1 ? '' : 's'}`);
      }
    } finally {
      setDownloadingAllIdProofs(false);
    }
  };

  // ===== CUSTOM PRICE BREAKDOWN FUNCTIONS =====

  // Load custom price breakdowns for this booking
  const loadCustomBreakdowns = async () => {
    if (!booking?.id) return;
    
    setLoadingCustomBreakdowns(true);
    
    try {
      console.log('Loading custom price breakdowns for booking:', booking.id);
      const response = await customPriceBreakdownService.getByBooking(booking.id);
      
      if (response.success) {
        setCustomBreakdowns(response.data.customBreakdowns);
        console.log('Loaded custom breakdowns:', response.data.customBreakdowns);
      }
    } catch (error) {
      console.error('Error loading custom price breakdowns:', error);
      toast.error('Failed to load custom price breakdowns');
      setCustomBreakdowns(null);
    } finally {
      setLoadingCustomBreakdowns(false);
    }
  };

  // Handle opening the add breakdown modal
  const handleOpenAddBreakdown = (targetType, targetId, targetName) => {
    setSelectedBreakdownTarget({ type: targetType, id: targetId, name: targetName });
    setNewBreakdown({
      name: '',
      amount: '',
      breakdownType: 'DISCOUNT',
      calculationType: 'FIXED',
      value: '',
      reason: ''
    });
    setShowAddBreakdownModal(true);
  };

  // Handle creating a new custom breakdown
  const handleCreateBreakdown = async (e) => {
    e.preventDefault();
    
    if (!selectedBreakdownTarget) {
      toast.error('No target selected');
      return;
    }

    if (!newBreakdown.name || !newBreakdown.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingBreakdown(true);
    
    try {
      const breakdownData = {
        bookingId: booking.id,
        targetType: selectedBreakdownTarget.type,
        targetId: selectedBreakdownTarget.id,
        breakdowns: [{
          name: newBreakdown.name,
          amount: parseFloat(newBreakdown.amount),
          breakdownType: newBreakdown.breakdownType,
          calculationType: newBreakdown.calculationType,
          value: parseFloat(newBreakdown.value || newBreakdown.amount),
          isActive: true
        }],
        replaceExisting: false,
        reason: newBreakdown.reason || `Custom breakdown added for ${selectedBreakdownTarget.name}`
      };

      console.log('Creating custom breakdown:', breakdownData);
      const response = await customPriceBreakdownService.create(breakdownData);
      
      if (response.success) {
        toast.success(`Custom breakdown created for ${selectedBreakdownTarget.name}`);
        setShowAddBreakdownModal(false);
        loadCustomBreakdowns();
        
        // Refresh booking details to get updated prices
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (error) {
      console.error('Error creating custom breakdown:', error);
      toast.error(error.message || 'Failed to create custom breakdown');
    } finally {
      setSavingBreakdown(false);
    }
  };

  // Handle deleting a custom breakdown
  const handleDeleteBreakdown = async (breakdownId, breakdownName) => {
    if (!window.confirm(`Are you sure you want to delete "${breakdownName}"? This will recalculate the booking total.`)) {
      return;
    }

    try {
      console.log('Deleting custom breakdown:', breakdownId);
      const response = await customPriceBreakdownService.delete(breakdownId);
      
      if (response.success) {
        toast.success('Custom breakdown deleted successfully');
        loadCustomBreakdowns();
        
        // Refresh booking details to get updated prices
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (error) {
      console.error('Error deleting custom breakdown:', error);
      toast.error(error.message || 'Failed to delete custom breakdown');
    }
  };

  // ===== RECALCULATE PRICE FUNCTION =====

  // Handle recalculating the booking price
  const handleRecalculatePrice = async () => {
    if (!booking?.id) {
      toast.error('No booking ID available');
      return;
    }

    setRecalculateLoading(true);
    setRecalculateResult(null);
    
    try {
      console.log('Recalculating price for booking:', booking.id);
      const response = await bookingsService.recalculatePrice(booking.id);
      
      console.log('Raw API response:', response);
      
      if (response.success) {
        // Extract data - response structure is { success, data: { booking: {...}, breakdown: {...} }, message }
        // Price fields are in data.booking
        const bookingData = response.data?.booking || response.data || response;
        const breakdownData = response.data?.breakdown || null;
        
        console.log('Extracted booking data:', bookingData);
        console.log('Extracted breakdown data:', breakdownData);
        
        // Validate that we have the required fields
        if (!bookingData.oldTotalPrice && bookingData.oldTotalPrice !== 0) {
          throw new Error('Invalid response: missing oldTotalPrice');
        }
        
        // Combine booking data and breakdown for display
        const resultData = {
          ...bookingData,
          breakdown: breakdownData
        };
        
        setRecalculateResult(resultData);
        console.log('Price recalculated:', resultData);
        
        // Show success message with difference
        const difference = bookingData.difference || 0;
        const diffText = difference > 0 
          ? `increased by ₹${difference.toFixed(2)}` 
          : difference < 0 
          ? `decreased by ₹${Math.abs(difference).toFixed(2)}`
          : 'remained the same';
        
        toast.success(`Price ${diffText}. Old: ₹${bookingData.oldTotalPrice.toFixed(2)}, New: ₹${bookingData.newTotalPrice.toFixed(2)}`);
        
        // Refresh booking details to show updated prices
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (error) {
      console.error('Error recalculating price:', error);
      toast.error(error.message || 'Failed to recalculate price');
      setRecalculateResult(null);
    } finally {
      setRecalculateLoading(false);
    }
  };

  // ===== RECALCULATE DATES FUNCTION =====

  // Handle recalculating the booking dates
  const handleRecalculateDates = async () => {
    if (!booking?.id) {
      toast.error('No booking ID available');
      return;
    }

    setRecalculateDatesLoading(true);
    setRecalculateDatesResult(null);
    
    try {
      console.log('Recalculating dates for booking:', booking.id);
      const response = await bookingsService.recalculateDates(booking.id);
      
      console.log('Raw API response:', response);
      
      if (response.success) {
        // Extract data from response
        const resultData = response.data;
        
        setRecalculateDatesResult(resultData);
        console.log('Dates recalculated:', resultData);
        
        // Show success message
        if (resultData.updated) {
          toast.success(`Dates updated! Check-in: ${new Date(resultData.newDates.checkIn).toLocaleDateString()}, Check-out: ${new Date(resultData.newDates.checkOut).toLocaleDateString()}`);
        } else {
          toast.info('Booking dates are already correct. No update needed.');
        }
        
        // Refresh booking details to show updated dates
        if (onUpdate && resultData.updated) {
          onUpdate();
        }
      }
    } catch (error) {
      console.error('Error recalculating dates:', error);
      toast.error(error.message || 'Failed to recalculate dates');
      setRecalculateDatesResult(null);
    } finally {
      setRecalculateDatesLoading(false);
    }
  };

  // ===== MULTI-ENTITY EXTEND FUNCTIONS =====

  // Check availability for all rooms and beds for extension
  const checkMultiEntityExtendAvailability = useCallback(async (newCheckOut) => {
    if (!newCheckOut) {
      setEntityAvailability({ rooms: {}, beds: {} });
      return;
    }

    const proposedCheckOut = new Date(newCheckOut + 'T11:00:00.000Z');
    
    // Find the earliest checkout date among all entities
    const roomCheckouts = bookingDetailsResponse.data?.bookedRooms?.map(r => new Date(r.checkOut || booking.checkOut)) || [];
    const bedCheckouts = bookingDetailsResponse.data?.bookedBeds?.map(b => new Date(b.checkOut || booking.checkOut)) || [];
    const allCheckouts = [...roomCheckouts, ...bedCheckouts];
    
    if (allCheckouts.length === 0) {
      toast.error('No entities found to extend');
      return;
    }
    
    const earliestCheckout = new Date(Math.min(...allCheckouts));
    
    // Note: We DON'T validate against earliestCheckout here because:
    // - Individual entities may have different checkout dates
    // - Some entities might already extend beyond selected date (marked as alreadyExtended)
    // - We need to check each entity individually and let user proceed if ANY entity needs extension
    
    try {
      setLoadingAvailability(true);
      
      const roomAvailability = {};
      const bedAvailability = {};

      // Check each booked room
      if (bookingDetailsResponse.data?.bookedRooms?.length > 0) {
        for (const bookedRoom of bookingDetailsResponse.data.bookedRooms) {
          const roomId = bookedRoom.room?.id || bookedRoom.roomId;
          if (!roomId) continue;

          // Get this room's specific checkout date
          const roomCheckOut = new Date(bookedRoom.checkOut || booking.checkOut);
          
          // Only check availability if this room needs extension (checkout is before proposed date)
          if (roomCheckOut >= proposedCheckOut) {
            console.log(`[AVAILABILITY] Room ${bookedRoom.id} already extends to or beyond selected date. No extension needed.`);
            roomAvailability[bookedRoom.id] = {
              available: false,
              unavailableDates: [],
              roomName: bookedRoom.room?.name || bookedRoom.roomName || `Room ${roomId}`,
              alreadyExtended: true,
              currentCheckout: roomCheckOut.toISOString().split('T')[0]
            };
            continue;
          }

          // Calculate days needed for THIS room's extension
          const roomDaysDifference = Math.ceil((proposedCheckOut - roomCheckOut) / (1000 * 60 * 60 * 24));
          
          let isAvailable = true;
          const unavailableDates = [];

          // Check day-by-day availability from this room's checkout date
          for (let i = 0; i < roomDaysDifference; i++) {
            const dayStart = new Date(roomCheckOut);
            dayStart.setDate(dayStart.getDate() + i);
            
            const dayEnd = new Date(dayStart);
            dayEnd.setDate(dayEnd.getDate() + 1);
            
            const params = {
              checkIn: dayStart.toISOString(),
              checkOut: dayEnd.toISOString(),
              excludeBookingId: booking.id
            };

            try {
              const response = await fetch(`${API_CONFIG.BASE_URL}/api/availability/room/${roomId}?${new URLSearchParams(params)}`);
              if (response?.ok) {
                const data = await response.json();
                const dayAvailable = data.data?.isAvailable !== false && data.isAvailable !== false;
                
                if (!dayAvailable) {
                  isAvailable = false;
                  unavailableDates.push(dayStart.toISOString().split('T')[0]);
                }
              }
            } catch (error) {
              console.warn(`Failed to check room ${roomId} for ${dayStart.toISOString().split('T')[0]}:`, error);
              isAvailable = false;
            }
          }

          roomAvailability[bookedRoom.id] = {
            available: isAvailable,
            unavailableDates,
            roomName: bookedRoom.room?.name || bookedRoom.roomName || `Room ${roomId}`
          };
        }
      }

      // Check each booked bed
      if (bookingDetailsResponse.data?.bookedBeds?.length > 0) {
        for (const bookedBed of bookingDetailsResponse.data.bookedBeds) {
          const bedId = bookedBed.bed?.id || bookedBed.bedId;
          if (!bedId) continue;

          // Get this bed's specific checkout date
          const bedCheckOut = new Date(bookedBed.checkOut || booking.checkOut);
          
          // Only check availability if this bed needs extension (checkout is before proposed date)
          if (bedCheckOut >= proposedCheckOut) {
            console.log(`[AVAILABILITY] Bed ${bookedBed.id} already extends to or beyond selected date. No extension needed.`);
            bedAvailability[bookedBed.id] = {
              available: false,
              unavailableDates: [],
              bedName: bookedBed.bed?.name || bookedBed.bedName || `Bed ${bedId}`,
              roomName: bookedBed.bed?.room?.name || bookedBed.roomName || 'Shared Room',
              alreadyExtended: true,
              currentCheckout: bedCheckOut.toISOString().split('T')[0]
            };
            continue;
          }

          // Calculate days needed for THIS bed's extension
          const bedDaysDifference = Math.ceil((proposedCheckOut - bedCheckOut) / (1000 * 60 * 60 * 24));
          
          let isAvailable = true;
          const unavailableDates = [];

          // Check day-by-day availability from this bed's checkout date
          for (let i = 0; i < bedDaysDifference; i++) {
            const dayStart = new Date(bedCheckOut);
            dayStart.setDate(dayStart.getDate() + i);
            
            const dayEnd = new Date(dayStart);
            dayEnd.setDate(dayEnd.getDate() + 1);
            
            const params = {
              checkIn: dayStart.toISOString(),
              checkOut: dayEnd.toISOString(),
              excludeBookingId: booking.id
            };

            try {
              const response = await fetch(`${API_CONFIG.BASE_URL}/api/availability/bed/${bedId}?${new URLSearchParams(params)}`);
              if (response?.ok) {
                const data = await response.json();
                const dayAvailable = data.data?.isAvailable !== false && data.isAvailable !== false;
                
                if (!dayAvailable) {
                  isAvailable = false;
                  unavailableDates.push(dayStart.toISOString().split('T')[0]);
                }
              }
            } catch (error) {
              console.warn(`Failed to check bed ${bedId} for ${dayStart.toISOString().split('T')[0]}:`, error);
              isAvailable = false;
            }
          }

          bedAvailability[bookedBed.id] = {
            available: isAvailable,
            unavailableDates,
            bedName: bookedBed.bed?.name || bookedBed.bedName || `Bed ${bedId}`,
            roomName: bookedBed.bed?.room?.name || bookedBed.roomName || 'Shared Room'
          };
        }
      }

      const availabilityData = { rooms: roomAvailability, beds: bedAvailability };
      setEntityAvailability(availabilityData);
      
      // Auto-select entities that need extension (available ones, exclude already extended)
      if (extendMode === 'individual') {
        const availableRoomIds = Object.keys(roomAvailability).filter(id => 
          roomAvailability[id].available && !roomAvailability[id].alreadyExtended
        );
        const availableBedIds = Object.keys(bedAvailability).filter(id => 
          bedAvailability[id].available && !bedAvailability[id].alreadyExtended
        );
        setSelectedRoomsToExtend(availableRoomIds);
        setSelectedBedsToExtend(availableBedIds);
      }
      
      // Return the availability data so it can be used immediately
      return availabilityData;
      
    } catch (error) {
      console.error('Error checking multi-entity availability:', error);
      setEntityAvailability({ rooms: {}, beds: {} });
      return { rooms: {}, beds: {} };
    } finally {
      setLoadingAvailability(false);
    }
  }, [booking.checkOut, booking.id, bookingDetailsResponse.data, extendMode]);

  // Calculate extension preview for bulk or individual mode
  const calculateExtensionPreview = useCallback(async (newCheckOut, availabilityOverride = null) => {
    if (!newCheckOut) {
      setExtensionPreview(null);
      return;
    }

    const proposedCheckOut = new Date(newCheckOut + 'T11:00:00.000Z');
    
    // Don't validate against booking's overall checkout because:
    // - Individual entities may have different checkout dates
    // - We need to calculate for entities that need extension (their checkout < proposedCheckOut)
    // - Entities already extending beyond proposedCheckOut will be marked with alreadyExtended flag

    try {
      setCalculatingExtendPrice(true);
      setExtendPriceError('');

      let totalAdditionalCost = 0;
      const entityCosts = { rooms: {}, beds: {} };
      
      // Use provided availability data or fall back to state
      const currentAvailability = availabilityOverride || entityAvailability;

      if (extendMode === 'bulk') {
        console.log('[BULK MODE] Starting bulk calculation');
        console.log('[BULK MODE] Entity availability:', currentAvailability);
        console.log('[BULK MODE] Booked rooms:', bookingDetailsResponse.data?.bookedRooms);
        console.log('[BULK MODE] Booked beds:', bookingDetailsResponse.data?.bookedBeds);
        
        // Check if entityAvailability is empty or not initialized
        if (!currentAvailability || (Object.keys(currentAvailability.rooms || {}).length === 0 && Object.keys(currentAvailability.beds || {}).length === 0)) {
          console.warn('[BULK MODE] Entity availability is empty! Need to run availability check first.');
        }
        
        // Calculate for all rooms and beds
        if (bookingDetailsResponse.data?.bookedRooms?.length > 0) {
          for (const bookedRoom of bookingDetailsResponse.data.bookedRooms) {
            console.log(`[BULK MODE] Processing room ${bookedRoom.id}:`, bookedRoom);
            console.log(`[BULK MODE] Room ${bookedRoom.id} availability info:`, currentAvailability.rooms[bookedRoom.id]);
            
            // Skip if already extended or not available
            if (currentAvailability.rooms[bookedRoom.id]?.alreadyExtended) {
              console.log(`[BULK MODE] Skipping room ${bookedRoom.id} - already extends beyond selected date`);
              continue;
            }
            
            if (!currentAvailability.rooms[bookedRoom.id]?.available) {
              console.log(`[BULK MODE] Skipping room ${bookedRoom.id} - not available`);
              continue;
            }

            // Use the entity's own checkout date, not the booking's checkout date
            const entityCheckOut = bookedRoom.checkOut || booking.checkOut;
            console.log(`[BULK MODE] Room ${bookedRoom.id} current checkout:`, entityCheckOut);

            const priceData = {
              checkIn: entityCheckOut,
              checkOut: proposedCheckOut.toISOString(),
              roomId: bookedRoom.room?.id || bookedRoom.roomId,
              bedId: null,
              courseId: null,
              addOns: [],
              isInterState: false
            };

            console.log(`[BULK MODE] Calculating price for room ${bookedRoom.id}:`, priceData);
            const response = await bookingsService.calculatePrice(priceData);
            console.log(`[BULK MODE] Price response for room ${bookedRoom.id}:`, response);
            
            if (response.success && response.data) {
              const cost = response.data.totalPrice || response.data.totalAmount || 0;
              console.log(`[BULK MODE] Room ${bookedRoom.id} cost: ${cost}, running total: ${totalAdditionalCost} + ${cost} = ${totalAdditionalCost + cost}`);
              entityCosts.rooms[bookedRoom.id] = cost;
              totalAdditionalCost += cost;
            }
          }
        }

        if (bookingDetailsResponse.data?.bookedBeds?.length > 0) {
          for (const bookedBed of bookingDetailsResponse.data.bookedBeds) {
            console.log(`[BULK MODE] Processing bed ${bookedBed.id}:`, bookedBed);
            console.log(`[BULK MODE] Bed ${bookedBed.id} availability info:`, currentAvailability.beds[bookedBed.id]);
            
            // Skip if already extended or not available
            if (currentAvailability.beds[bookedBed.id]?.alreadyExtended) {
              console.log(`[BULK MODE] Skipping bed ${bookedBed.id} - already extends beyond selected date`);
              continue;
            }
            
            if (!currentAvailability.beds[bookedBed.id]?.available) {
              console.log(`[BULK MODE] Skipping bed ${bookedBed.id} - not available`);
              continue;
            }

            // Use the entity's own checkout date, not the booking's checkout date
            const entityCheckOut = bookedBed.checkOut || booking.checkOut;
            console.log(`[BULK MODE] Bed ${bookedBed.id} current checkout:`, entityCheckOut);

            const priceData = {
              checkIn: entityCheckOut,
              checkOut: proposedCheckOut.toISOString(),
              roomId: null,
              bedId: bookedBed.bed?.id || bookedBed.bedId,
              courseId: null,
              addOns: [],
              isInterState: false
            };

            console.log(`[BULK MODE] Calculating price for bed ${bookedBed.id}:`, priceData);
            const response = await bookingsService.calculatePrice(priceData);
            console.log(`[BULK MODE] Price response for bed ${bookedBed.id}:`, response);
            if (response.success && response.data) {
              const cost = response.data.totalPrice || response.data.totalAmount || 0;
              console.log(`[BULK MODE] Bed ${bookedBed.id} cost: ${cost}, running total: ${totalAdditionalCost} + ${cost} = ${totalAdditionalCost + cost}`);
              entityCosts.beds[bookedBed.id] = cost;
              totalAdditionalCost += cost;
            }
          }
        }
        
        console.log('[BULK MODE] Final total cost:', totalAdditionalCost);
        console.log('[BULK MODE] Entity costs breakdown:', entityCosts);
      } else {
        // Individual mode - calculate only for selected entities
        console.log('[INDIVIDUAL MODE] Starting individual calculation');
        console.log('[INDIVIDUAL MODE] Selected rooms:', selectedRoomsToExtend);
        console.log('[INDIVIDUAL MODE] Selected beds:', selectedBedsToExtend);
        
        if (selectedRoomsToExtend.length > 0) {
          const selectedRooms = bookingDetailsResponse.data?.bookedRooms?.filter(r => selectedRoomsToExtend.includes(r.id));
          for (const bookedRoom of selectedRooms || []) {
            // Use the entity's own checkout date, not the booking's checkout date
            const entityCheckOut = bookedRoom.checkOut || booking.checkOut;
            console.log(`[INDIVIDUAL MODE] Room ${bookedRoom.id} current checkout:`, entityCheckOut);
            
            const priceData = {
              checkIn: entityCheckOut,
              checkOut: proposedCheckOut.toISOString(),
              roomId: bookedRoom.room?.id || bookedRoom.roomId,
              bedId: null,
              courseId: null,
              addOns: [],
              isInterState: false
            };

            console.log(`[INDIVIDUAL MODE] Calculating price for room ${bookedRoom.id}:`, priceData);
            const response = await bookingsService.calculatePrice(priceData);
            console.log(`[INDIVIDUAL MODE] Price response for room ${bookedRoom.id}:`, response);
            
            if (response.success && response.data) {
              const cost = response.data.totalPrice || response.data.totalAmount || 0;
              console.log(`[INDIVIDUAL MODE] Room ${bookedRoom.id} cost: ${cost}`);
              entityCosts.rooms[bookedRoom.id] = cost;
              totalAdditionalCost += cost;
            }
          }
        }

        if (selectedBedsToExtend.length > 0) {
          const selectedBeds = bookingDetailsResponse.data?.bookedBeds?.filter(b => selectedBedsToExtend.includes(b.id));
          for (const bookedBed of selectedBeds || []) {
            // Use the entity's own checkout date, not the booking's checkout date
            const entityCheckOut = bookedBed.checkOut || booking.checkOut;
            console.log(`[INDIVIDUAL MODE] Bed ${bookedBed.id} current checkout:`, entityCheckOut);
            
            const priceData = {
              checkIn: entityCheckOut,
              checkOut: proposedCheckOut.toISOString(),
              roomId: null,
              bedId: bookedBed.bed?.id || bookedBed.bedId,
              courseId: null,
              addOns: [],
              isInterState: false
            };

            console.log(`[INDIVIDUAL MODE] Calculating price for bed ${bookedBed.id}:`, priceData);
            const response = await bookingsService.calculatePrice(priceData);
            console.log(`[INDIVIDUAL MODE] Price response for bed ${bookedBed.id}:`, response);
            
            if (response.success && response.data) {
              const cost = response.data.totalPrice || response.data.totalAmount || 0;
              console.log(`[INDIVIDUAL MODE] Bed ${bookedBed.id} cost: ${cost}`);
              entityCosts.beds[bookedBed.id] = cost;
              totalAdditionalCost += cost;
            }
          }
        }
        
        console.log('[INDIVIDUAL MODE] Final total cost:', totalAdditionalCost);
      }

      console.log('[CALCULATE PREVIEW] Final calculation complete');
      console.log('[CALCULATE PREVIEW] Mode:', extendMode);
      console.log('[CALCULATE PREVIEW] Total additional cost:', totalAdditionalCost);
      console.log('[CALCULATE PREVIEW] Entity costs:', entityCosts);
      
      setExtensionPreview({
        totalCost: totalAdditionalCost,
        entityCosts,
        newCheckOutDate: newCheckOut
      });
      setExtendPrice(totalAdditionalCost);
      
    } catch (error) {
      console.error('Error calculating extension preview:', error);
      setExtendPriceError('Failed to calculate extension price. Please try again.');
      setExtensionPreview(null);
    } finally {
      setCalculatingExtendPrice(false);
    }
  }, [booking.checkOut, booking.id, bookingDetailsResponse.data, extendMode, selectedRoomsToExtend, selectedBedsToExtend, entityAvailability]);

  // Handle extend date change for multi-entity
  const handleMultiEntityExtendDateChange = async (date) => {
    setNewCheckOutDate(date);
    if (date) {
      // Get availability data and pass it directly to calculateExtensionPreview
      const availabilityData = await checkMultiEntityExtendAvailability(date);
      await calculateExtensionPreview(date, availabilityData);
    } else {
      setExtendPrice(0);
      setExtendPriceError('');
      setExtensionPreview(null);
      setEntityAvailability({ rooms: {}, beds: {} });
    }
  };

  // Handle extend mode change
  const handleExtendModeChange = (mode) => {
    setExtendMode(mode);
    // Reset selections when switching modes
    if (mode === 'bulk') {
      setSelectedRoomsToExtend([]);
      setSelectedBedsToExtend([]);
    } else {
      // Auto-select all available entities in individual mode
      const availableRoomIds = Object.keys(entityAvailability.rooms).filter(id => entityAvailability.rooms[id].available);
      const availableBedIds = Object.keys(entityAvailability.beds).filter(id => entityAvailability.beds[id].available);
      setSelectedRoomsToExtend(availableRoomIds);
      setSelectedBedsToExtend(availableBedIds);
    }
    // Recalculate preview with new mode
    if (newCheckOutDate) {
      calculateExtensionPreview(newCheckOutDate);
    }
  };

  // Handle room selection toggle
  const toggleRoomSelection = (bookedRoomId) => {
    setSelectedRoomsToExtend(prev => {
      if (prev.includes(bookedRoomId)) {
        return prev.filter(id => id !== bookedRoomId);
      } else {
        return [...prev, bookedRoomId];
      }
    });
  };

  // Handle bed selection toggle
  const toggleBedSelection = (bookedBedId) => {
    setSelectedBedsToExtend(prev => {
      if (prev.includes(bookedBedId)) {
        return prev.filter(id => id !== bookedBedId);
      } else {
        return [...prev, bookedBedId];
      }
    });
  };

  // Auto-recalculate extension preview when selections change in individual mode
  useEffect(() => {
    if (extendMode === 'individual' && newCheckOutDate) {
      calculateExtensionPreview(newCheckOutDate);
    }
  }, [selectedRoomsToExtend, selectedBedsToExtend, extendMode, newCheckOutDate, calculateExtensionPreview]);

  // Execute extend booking with new endpoints
  const executeExtendBooking = async () => {
    if (!newCheckOutDate) {
      toast.error('Please select a new checkout date');
      return;
    }

    try {
      setSaving(true);

      if (extendMode === 'bulk') {
        // Use bulk extend endpoint
        const extendData = {
          newCheckOutDate: new Date(newCheckOutDate + 'T11:00:00.000Z').toISOString()
        };

        const response = await bookingsService.extendBulk(booking.id, extendData);
        
        if (response.success) {
          toast.success(`Booking extended successfully! Additional cost: ₹${extendPrice.toFixed(2)}`);
          await loadBookingDetails(booking.id); // Refresh booking data
          setNewCheckOutDate('');
          setExtendPrice(0);
          setExtensionPreview(null);
          setEntityAvailability({ rooms: {}, beds: {} });
        } else {
          throw new Error(response.message || 'Failed to extend booking');
        }
      } else {
        // Individual mode - extend selected entities one by one
        const extendPromises = [];

        // Extend selected rooms
        for (const bookedRoomId of selectedRoomsToExtend) {
          const extendData = {
            entityType: 'room',
            entityId: bookedRoomId,
            newCheckOutDate: new Date(newCheckOutDate + 'T11:00:00.000Z').toISOString()
          };
          extendPromises.push(bookingsService.extendEntity(booking.id, extendData));
        }

        // Extend selected beds
        for (const bookedBedId of selectedBedsToExtend) {
          const extendData = {
            entityType: 'bed',
            entityId: bookedBedId,
            newCheckOutDate: new Date(newCheckOutDate + 'T11:00:00.000Z').toISOString()
          };
          extendPromises.push(bookingsService.extendEntity(booking.id, extendData));
        }

        const results = await Promise.allSettled(extendPromises);
        
        const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
        const totalCount = results.length;

        if (successCount === totalCount) {
          toast.success(`All ${totalCount} entities extended successfully! Additional cost: ₹${extendPrice.toFixed(2)}`);
        } else if (successCount > 0) {
          toast.warning(`${successCount} out of ${totalCount} entities extended successfully`);
        } else {
          throw new Error('Failed to extend any entities');
        }

        await loadBookingDetails(booking.id); // Refresh booking data
        setNewCheckOutDate('');
        setExtendPrice(0);
        setExtensionPreview(null);
        setEntityAvailability({ rooms: {}, beds: {} });
        setSelectedRoomsToExtend([]);
        setSelectedBedsToExtend([]);
      }
    } catch (error) {
      console.error('Error extending booking:', error);
      toast.error(error.message || 'Failed to extend booking');
    } finally {
      setSaving(false);
    }
  };

  // Room exchange functions
  const loadAvailableRooms = useCallback(async () => {
    try {
      setLoadingRooms(true);
      setExchangePriceError('');
      
      // Get available rooms for the same dates
      const params = {
        checkIn: booking.checkIn,
        checkOut: booking.checkOut,
        excludeBookingId: booking.id, // Exclude current booking from conflict check
        limit: 50
      };

      // Fetch both rooms and beds in parallel
      const [roomsResponse, bedsResponse] = await Promise.all([
        availabilityService.getRooms(params),
        availabilityService.getBeds(params)
      ]);

      const roomsData = roomsResponse.availableRooms || roomsResponse.data?.availableRooms || [];
      const bedsData = bedsResponse.availableBeds || bedsResponse.data?.availableBeds || [];
      
      // Filter out current room/bed if it's available
      // Use helper functions to get current room/bed IDs from new booking format
      const filteredRooms = roomsData.filter(room => {
        const currentRoomId = primaryRoom?.id;
        if (currentRoomId && room.id === currentRoomId) {
          return false; // Don't show current room
        }
        return true;
      });

      // Filter out current bed if it's available
      const filteredBeds = bedsData.filter(bed => {
        const currentBedId = primaryBed?.id;
        if (currentBedId && bed.id === currentBedId) {
          return false; // Don't show current bed
        }
        return true;
      });
      
      setAvailableRooms(Array.isArray(filteredRooms) ? filteredRooms : []);
      setAvailableBeds(Array.isArray(filteredBeds) ? filteredBeds : []);
      
      console.log('Loaded rooms:', filteredRooms.length);
      console.log('Loaded beds:', filteredBeds.length);
      
    } catch (error) {
      console.error('Error loading available rooms and beds:', error);
      toast.error('Failed to load available accommodations');
      setAvailableRooms([]);
      setAvailableBeds([]);
      setExchangePriceError('Failed to load available accommodations');
    } finally {
      setLoadingRooms(false);
    }
  }, [booking.checkIn, booking.checkOut, booking.id, booking.roomId, booking.room, booking.bedId, booking.bed]);

  const calculateRoomExchangePrice = async (newRoomId, newBedId = null) => {
    if (!newRoomId) {
      setRoomExchangeDifference(0);
      return;
    }

    try {
      setCalculatingExchangePrice(true);
      setExchangePriceError('');

      // Calculate new room price
      const newPriceData = {
        roomId: newRoomId,
        bedId: newBedId,
        checkIn: booking.checkIn,
        checkOut: booking.checkOut,
        courseId: primaryCourse?.id || null // Use helper to get course ID
      };

      const newPriceResponse = await bookingsService.calculatePrice(newPriceData);
      const newAccommodationPrice = newPriceResponse.data?.accommodationPrice || 0;

      // Calculate current room price
      // Use helper functions to get room/bed/course IDs from new booking format
      const currentPriceData = {
        roomId: primaryRoom?.id || null,
        bedId: primaryBed?.id || null,
        checkIn: booking.checkIn,
        checkOut: booking.checkOut,
        courseId: primaryCourse?.id || null
      };

      const currentPriceResponse = await bookingsService.calculatePrice(currentPriceData);
      const currentAccommodationPrice = currentPriceResponse.data?.accommodationPrice || 0;

      // Calculate difference
      const priceDifference = newAccommodationPrice - currentAccommodationPrice;
      
      setRoomExchangeDifference(priceDifference);

    } catch (error) {
      console.error('Error calculating exchange price:', error);
      setExchangePriceError('Failed to calculate exchange pricing');
      setRoomExchangeDifference(0);
    } finally {
      setCalculatingExchangePrice(false);
    }
  };

  const handleRoomSelect = (room) => {
    if (selectedNewRoom && selectedNewRoom.id === room.id) {
      // Deselecting current room
      setSelectedNewRoom(null);
      setSelectedNewBed(null);
      setRoomExchangeDifference(0);
    } else {
      // Selecting new room
      setSelectedNewRoom(room);
      setSelectedNewBed(null); // Reset bed selection
      
      // For private rooms, calculate price immediately
      if (!room.roomType?.isShared) {
        calculateRoomExchangePrice(room.id, null);
      } else {
        // For shared rooms, wait for bed selection
        setRoomExchangeDifference(0);
      }
    }
  };

  const handleBedSelect = (bed) => {
    if (selectedNewBed && selectedNewBed.id === bed.id) {
      // Deselecting current bed
      setSelectedNewBed(null);
      setRoomExchangeDifference(0);
    } else {
      // Selecting new bed
      setSelectedNewBed(bed);
      // Only calculate price if a room is selected
      if (selectedNewRoom) {
        calculateRoomExchangePrice(selectedNewRoom.id, bed.id);
      }
    }
  };

  const loadBedsForRoom = async (roomId) => {
    try {
      // Filter the available beds to only show beds for the selected room
      const roomBeds = availableBeds.filter(bed => {
        const bedRoomId = bed.room?.id || bed.roomId;
        return bedRoomId === roomId;
      });
      console.log(`Room ${roomId} has ${roomBeds.length} available beds:`, roomBeds.map(bed => `Bed ${bed.bedNumber}`));
      return roomBeds;
    } catch (error) {
      console.error('Error loading beds for room:', error);
      return [];
    }
  };

  // Load available rooms when exchange section is active
  useEffect(() => {
    if (activeSection === 'exchange') {
      loadAvailableRooms();
    }
  }, [activeSection, loadAvailableRooms]);

  // Handle early checkout
  const handleEarlyCheckOut = async () => {
    if (!earlyCheckOutDate) {
      toast.error('Please select an early checkout date');
      return;
    }

    const checkOutDate = new Date(earlyCheckOutDate);
    const currentDate = new Date();
    const originalCheckOut = new Date(booking.checkOut);

    // Validate early checkout date
    if (checkOutDate <= currentDate) {
      toast.error('Early checkout date must be in the future');
      return;
    }

    if (checkOutDate >= originalCheckOut) {
      toast.error('Early checkout date must be before the original checkout date');
      return;
    }

    // Confirm early checkout
    if (!window.confirm(`Are you sure you want to process early checkout for ${format(checkOutDate, 'MMMM dd, yyyy')}? This action cannot be undone.`)) {
      return;
    }

    setProcessingEarlyCheckOut(true);
    try {
      // Convert to ISO string with time
      const checkOutDateTime = new Date(earlyCheckOutDate + 'T10:00:00.000Z').toISOString();
      
      const response = await bookingsService.earlyCheckOut(booking.id, checkOutDateTime);
      
      // Handle payment warnings from backend
      if (response && response.warning) {
        toast.warning(response.warning, { autoClose: 8000 });
        toast.success('Early checkout processed successfully');
      } else {
        toast.success('Early checkout processed successfully');
      }
      
      onUpdate(); // Refresh booking data
      onClose(); // Close modal
    } catch (error) {
      console.error('Early checkout error:', error);
      // Handle payment validation errors from backend
      const errorMessage = error.response?.data?.error || error.error || error.message || 'Failed to process early checkout. Please try again.';
      toast.error(errorMessage, { autoClose: 10000 });
    } finally {
      setProcessingEarlyCheckOut(false);
    }
  };

  // ===== MULTI-ENTITY EARLY CHECK-IN FUNCTIONS =====

  // Check availability for all rooms and beds for early check-in
  const checkMultiEntityEarlyCheckinAvailability = useCallback(async (newCheckIn) => {
    if (!newCheckIn) {
      setEarlyCheckinEntityAvailability({ rooms: {}, beds: {} });
      return;
    }

    const proposedCheckIn = new Date(newCheckIn + 'T15:00:00.000Z');
    
    // Find the latest check-in date among all entities
    const roomCheckins = bookingDetailsResponse.data?.bookedRooms?.map(r => new Date(r.checkIn || booking.checkIn)) || [];
    const bedCheckins = bookingDetailsResponse.data?.bookedBeds?.map(b => new Date(b.checkIn || booking.checkIn)) || [];
    const allCheckins = [...roomCheckins, ...bedCheckins];
    
    if (allCheckins.length === 0) {
      toast.error('No entities found for early check-in');
      return;
    }
    
    // Note: We DON'T validate against latest checkin here because:
    // - Individual entities may have different check-in dates
    // - Some entities might already check-in earlier than selected date (marked as alreadyEarlier)
    // - We need to check each entity individually

    try {
      setLoadingEarlyCheckinAvailability(true);
      
      const roomAvailability = {};
      const bedAvailability = {};

      // Check each booked room
      if (bookingDetailsResponse.data?.bookedRooms?.length > 0) {
        for (const bookedRoom of bookingDetailsResponse.data.bookedRooms) {
          const roomId = bookedRoom.room?.id || bookedRoom.roomId;
          if (!roomId) continue;

          // Get this room's specific check-in date
          const roomCheckIn = new Date(bookedRoom.checkIn || booking.checkIn);
          
          // Only check availability if this room needs early check-in
          if (roomCheckIn <= proposedCheckIn) {
            console.log(`[EARLY CHECKIN] Room ${bookedRoom.id} already checks in at or before selected date.`);
            roomAvailability[bookedRoom.id] = {
              available: false,
              unavailableDates: [],
              roomName: bookedRoom.room?.name || `Room ${roomId}`,
              alreadyEarlier: true,
              currentCheckin: roomCheckIn.toISOString().split('T')[0]
            };
            continue;
          }

          // Calculate days needed for THIS room's early check-in
          const roomDaysDifference = Math.ceil((roomCheckIn - proposedCheckIn) / (1000 * 60 * 60 * 24));
          
          let isAvailable = true;
          const unavailableDates = [];

          // Check day-by-day availability from proposed date to room's check-in
          for (let i = 0; i < roomDaysDifference; i++) {
            const dayStart = new Date(proposedCheckIn);
            dayStart.setDate(dayStart.getDate() + i);
            
            const dayEnd = new Date(dayStart);
            dayEnd.setDate(dayEnd.getDate() + 1);
            
            const params = {
              checkIn: dayStart.toISOString(),
              checkOut: dayEnd.toISOString(),
              excludeBookingId: booking.id
            };

            try {
              const response = await fetch(`${API_CONFIG.BASE_URL}/api/availability/room/${roomId}?${new URLSearchParams(params)}`);
              if (response?.ok) {
                const data = await response.json();
                const dayAvailable = data.data?.isAvailable !== false && data.isAvailable !== false;
                
                if (!dayAvailable) {
                  isAvailable = false;
                  unavailableDates.push(dayStart.toISOString().split('T')[0]);
                }
              }
            } catch (error) {
              console.warn(`Failed to check room ${roomId}:`, error);
              isAvailable = false;
            }
          }

          roomAvailability[bookedRoom.id] = {
            available: isAvailable,
            unavailableDates,
            roomName: bookedRoom.room?.name || `Room ${roomId}`
          };
        }
      }

      // Check each booked bed (same logic as rooms)
      if (bookingDetailsResponse.data?.bookedBeds?.length > 0) {
        for (const bookedBed of bookingDetailsResponse.data.bookedBeds) {
          const bedId = bookedBed.bed?.id || bookedBed.bedId;
          if (!bedId) continue;

          const bedCheckIn = new Date(bookedBed.checkIn || booking.checkIn);
          
          if (bedCheckIn <= proposedCheckIn) {
            console.log(`[EARLY CHECKIN] Bed ${bookedBed.id} already checks in at or before selected date.`);
            bedAvailability[bookedBed.id] = {
              available: false,
              unavailableDates: [],
              bedName: bookedBed.bed?.name || `Bed ${bedId}`,
              roomName: bookedBed.bed?.room?.name || 'Shared Room',
              alreadyEarlier: true,
              currentCheckin: bedCheckIn.toISOString().split('T')[0]
            };
            continue;
          }

          const bedDaysDifference = Math.ceil((bedCheckIn - proposedCheckIn) / (1000 * 60 * 60 * 24));
          
          let isAvailable = true;
          const unavailableDates = [];

          for (let i = 0; i < bedDaysDifference; i++) {
            const dayStart = new Date(proposedCheckIn);
            dayStart.setDate(dayStart.getDate() + i);
            
            const dayEnd = new Date(dayStart);
            dayEnd.setDate(dayEnd.getDate() + 1);
            
            const params = {
              checkIn: dayStart.toISOString(),
              checkOut: dayEnd.toISOString(),
              excludeBookingId: booking.id
            };

            try {
              const response = await fetch(`${API_CONFIG.BASE_URL}/api/availability/bed/${bedId}?${new URLSearchParams(params)}`);
              if (response?.ok) {
                const data = await response.json();
                const dayAvailable = data.data?.isAvailable !== false && data.isAvailable !== false;
                
                if (!dayAvailable) {
                  isAvailable = false;
                  unavailableDates.push(dayStart.toISOString().split('T')[0]);
                }
              }
            } catch (error) {
              console.warn(`Failed to check bed ${bedId}:`, error);
              isAvailable = false;
            }
          }

          bedAvailability[bookedBed.id] = {
            available: isAvailable,
            unavailableDates,
            bedName: bookedBed.bed?.name || `Bed ${bedId}`,
            roomName: bookedBed.bed?.room?.name || 'Shared Room'
          };
        }
      }

      const availabilityData = { rooms: roomAvailability, beds: bedAvailability };
      setEarlyCheckinEntityAvailability(availabilityData);
      
      // Auto-select entities in individual mode
      if (earlyCheckinMode === 'individual') {
        const availableRoomIds = Object.keys(roomAvailability)
          .filter(id => roomAvailability[id].available && !roomAvailability[id].alreadyEarlier);
        const availableBedIds = Object.keys(bedAvailability)
          .filter(id => bedAvailability[id].available && !bedAvailability[id].alreadyEarlier);
        
        setSelectedRoomsForEarlyCheckin(availableRoomIds);
        setSelectedBedsForEarlyCheckin(availableBedIds);
      }
      
      return availabilityData;
      
    } catch (error) {
      console.error('Error checking multi-entity early check-in availability:', error);
      setEarlyCheckinEntityAvailability({ rooms: {}, beds: {} });
      return { rooms: {}, beds: {} };
    } finally {
      setLoadingEarlyCheckinAvailability(false);
    }
  }, [booking.checkIn, booking.id, bookingDetailsResponse.data, earlyCheckinMode]);

  // Calculate early check-in preview for bulk or individual mode
  const calculateEarlyCheckinPreview = useCallback(async (newCheckIn, availabilityOverride = null) => {
    if (!newCheckIn) {
      setEarlyCheckinPreview(null);
      return;
    }

    const proposedCheckIn = new Date(newCheckIn + 'T15:00:00.000Z');
    
    // Don't validate against booking's overall check-in because:
    // - Individual entities may have different check-in dates
    // - We need to calculate for entities that need early check-in

    try {
      setCalculatingEarlyCheckinPrice(true);
      setEarlyCheckinError('');

      let totalAdditionalCost = 0;
      const entityCosts = { rooms: {}, beds: {} };
      
      const currentAvailability = availabilityOverride || earlyCheckinEntityAvailability;

      if (earlyCheckinMode === 'bulk') {
        console.log('[EARLY CHECKIN BULK] Calculating for all entities');
        
        // Calculate for all rooms
        if (bookingDetailsResponse.data?.bookedRooms?.length > 0) {
          for (const bookedRoom of bookingDetailsResponse.data.bookedRooms) {
            const roomCheckIn = new Date(bookedRoom.checkIn || booking.checkIn);
            
            // Skip if already earlier
            if (roomCheckIn <= proposedCheckIn) {
              console.log(`[BULK] Skipping room ${bookedRoom.id} - already earlier`);
              continue;
            }

            const priceData = {
              checkIn: proposedCheckIn.toISOString(),
              checkOut: roomCheckIn.toISOString(),
              roomId: bookedRoom.room?.id || bookedRoom.roomId,
              courseId: null,
              addOns: [],
              isInterState: false
            };

            try {
              const response = await bookingsService.calculatePrice(priceData);
              const roomCost = response.data?.totalPrice || 0;
              entityCosts.rooms[bookedRoom.id] = roomCost;
              totalAdditionalCost += roomCost;
            } catch (error) {
              console.error(`Error calculating price for room ${bookedRoom.id}:`, error);
            }
          }
        }

        // Calculate for all beds
        if (bookingDetailsResponse.data?.bookedBeds?.length > 0) {
          for (const bookedBed of bookingDetailsResponse.data.bookedBeds) {
            const bedCheckIn = new Date(bookedBed.checkIn || booking.checkIn);
            
            if (bedCheckIn <= proposedCheckIn) {
              continue;
            }

            const priceData = {
              checkIn: proposedCheckIn.toISOString(),
              checkOut: bedCheckIn.toISOString(),
              roomId: null,
              bedId: bookedBed.bed?.id || bookedBed.bedId,
              courseId: null,
              addOns: [],
              isInterState: false
            };

            try {
              const response = await bookingsService.calculatePrice(priceData);
              const bedCost = response.data?.totalPrice || 0;
              entityCosts.beds[bookedBed.id] = bedCost;
              totalAdditionalCost += bedCost;
            } catch (error) {
              console.error(`Error calculating price for bed ${bookedBed.id}:`, error);
            }
          }
        }

      } else {
        console.log('[EARLY CHECKIN INDIVIDUAL] Calculating for selected entities');
        
        // Calculate only for selected rooms
        if (bookingDetailsResponse.data?.bookedRooms?.length > 0) {
          for (const bookedRoom of bookingDetailsResponse.data.bookedRooms) {
            if (!selectedRoomsForEarlyCheckin.includes(bookedRoom.id)) {
              continue;
            }

            const roomCheckIn = new Date(bookedRoom.checkIn || booking.checkIn);
            
            if (roomCheckIn <= proposedCheckIn) {
              continue;
            }

            const priceData = {
              checkIn: proposedCheckIn.toISOString(),
              checkOut: roomCheckIn.toISOString(),
              roomId: bookedRoom.room?.id || bookedRoom.roomId,
              courseId: null,
              addOns: [],
              isInterState: false
            };

            try {
              const response = await bookingsService.calculatePrice(priceData);
              const roomCost = response.data?.totalPrice || 0;
              entityCosts.rooms[bookedRoom.id] = roomCost;
              totalAdditionalCost += roomCost;
            } catch (error) {
              console.error(`Error calculating price for room ${bookedRoom.id}:`, error);
            }
          }
        }

        // Calculate for selected beds
        if (bookingDetailsResponse.data?.bookedBeds?.length > 0) {
          for (const bookedBed of bookingDetailsResponse.data.bookedBeds) {
            if (!selectedBedsForEarlyCheckin.includes(bookedBed.id)) {
              continue;
            }

            const bedCheckIn = new Date(bookedBed.checkIn || booking.checkIn);
            
            if (bedCheckIn <= proposedCheckIn) {
              continue;
            }

            const priceData = {
              checkIn: proposedCheckIn.toISOString(),
              checkOut: bedCheckIn.toISOString(),
              roomId: null,
              bedId: bookedBed.bed?.id || bookedBed.bedId,
              courseId: null,
              addOns: [],
              isInterState: false
            };

            try {
              const response = await bookingsService.calculatePrice(priceData);
              const bedCost = response.data?.totalPrice || 0;
              entityCosts.beds[bookedBed.id] = bedCost;
              totalAdditionalCost += bedCost;
            } catch (error) {
              console.error(`Error calculating price for bed ${bookedBed.id}:`, error);
            }
          }
        }
      }

      console.log('[EARLY CHECKIN PREVIEW] Total cost:', totalAdditionalCost);
      
      setEarlyCheckinPreview({
        totalCost: totalAdditionalCost,
        entityCosts,
        newCheckInDate: newCheckIn
      });
      setEarlyCheckinPrice(totalAdditionalCost);
      
    } catch (error) {
      console.error('Error calculating early check-in preview:', error);
      setEarlyCheckinError('Failed to calculate early check-in price');
      setEarlyCheckinPreview(null);
    } finally {
      setCalculatingEarlyCheckinPrice(false);
    }
  }, [booking.checkIn, booking.id, bookingDetailsResponse.data, earlyCheckinMode, 
      selectedRoomsForEarlyCheckin, selectedBedsForEarlyCheckin, earlyCheckinEntityAvailability]);

  // Handle early check-in date change for multi-entity
  const handleMultiEntityEarlyCheckinDateChange = async (date) => {
    setNewCheckInDate(date);
    if (date) {
      const availabilityData = await checkMultiEntityEarlyCheckinAvailability(date);
      await calculateEarlyCheckinPreview(date, availabilityData);
    } else {
      setEarlyCheckinPrice(0);
      setEarlyCheckinError('');
      setEarlyCheckinPreview(null);
      setEarlyCheckinEntityAvailability({ rooms: {}, beds: {} });
    }
  };

  // Handle early check-in mode change
  const handleEarlyCheckinModeChange = (mode) => {
    setEarlyCheckinMode(mode);
    
    if (mode === 'bulk') {
      setSelectedRoomsForEarlyCheckin([]);
      setSelectedBedsForEarlyCheckin([]);
    } else {
      const availableRoomIds = Object.keys(earlyCheckinEntityAvailability.rooms)
        .filter(id => earlyCheckinEntityAvailability.rooms[id].available && 
                     !earlyCheckinEntityAvailability.rooms[id].alreadyEarlier);
      const availableBedIds = Object.keys(earlyCheckinEntityAvailability.beds)
        .filter(id => earlyCheckinEntityAvailability.beds[id].available && 
                     !earlyCheckinEntityAvailability.beds[id].alreadyEarlier);
      
      setSelectedRoomsForEarlyCheckin(availableRoomIds);
      setSelectedBedsForEarlyCheckin(availableBedIds);
    }
    
    if (newCheckInDate) {
      calculateEarlyCheckinPreview(newCheckInDate);
    }
  };

  // Toggle room selection for early check-in
  const toggleRoomEarlyCheckinSelection = (bookedRoomId) => {
    setSelectedRoomsForEarlyCheckin(prev => {
      if (prev.includes(bookedRoomId)) {
        return prev.filter(id => id !== bookedRoomId);
      } else {
        return [...prev, bookedRoomId];
      }
    });
  };

  // Toggle bed selection for early check-in
  const toggleBedEarlyCheckinSelection = (bookedBedId) => {
    setSelectedBedsForEarlyCheckin(prev => {
      if (prev.includes(bookedBedId)) {
        return prev.filter(id => id !== bookedBedId);
      } else {
        return [...prev, bookedBedId];
      }
    });
  };

  // Auto-recalculate early check-in preview when selections change in individual mode
  useEffect(() => {
    if (earlyCheckinMode === 'individual' && newCheckInDate) {
      calculateEarlyCheckinPreview(newCheckInDate);
    }
  }, [selectedRoomsForEarlyCheckin, selectedBedsForEarlyCheckin, earlyCheckinMode, newCheckInDate, calculateEarlyCheckinPreview]);

  // Execute early check-in with new endpoints
  const executeEarlyCheckin = async () => {
    if (!newCheckInDate) {
      toast.error('Please select a new check-in date');
      return;
    }

    if (!earlyCheckinPreview) {
      toast.error('Please wait for price calculation to complete');
      return;
    }

    try {
      setProcessingEarlyCheckin(true);

      let response;
      
      if (earlyCheckinMode === 'bulk') {
        console.log('[EARLY CHECKIN] Executing bulk early check-in');
        
        response = await bookingsService.earlyCheckinBulk(booking.id, {
          newCheckIn: new Date(newCheckInDate + 'T15:00:00.000Z').toISOString()
        });
      } else {
        console.log('[EARLY CHECKIN] Executing individual early check-in');
        
        if (selectedRoomsForEarlyCheckin.length === 0 && selectedBedsForEarlyCheckin.length === 0) {
          toast.error('Please select at least one entity for early check-in');
          return;
        }

        const newCheckInISO = new Date(newCheckInDate + 'T15:00:00.000Z').toISOString();
        const results = [];
        let successCount = 0;
        let errorCount = 0;

        // Process each selected room
        for (const roomId of selectedRoomsForEarlyCheckin) {
          try {
            console.log(`[EARLY CHECKIN] Processing room ${roomId}`);
            const result = await bookingsService.earlyCheckinEntity(booking.id, {
              bookedRoomId: roomId,
              newCheckIn: newCheckInISO
            });
            results.push({ type: 'room', id: roomId, success: true, result });
            successCount++;
          } catch (error) {
            console.error(`[EARLY CHECKIN] Failed to process room ${roomId}:`, error);
            results.push({ type: 'room', id: roomId, success: false, error: error.message });
            errorCount++;
          }
        }

        // Process each selected bed
        for (const bedId of selectedBedsForEarlyCheckin) {
          try {
            console.log(`[EARLY CHECKIN] Processing bed ${bedId}`);
            const result = await bookingsService.earlyCheckinEntity(booking.id, {
              bookedBedId: bedId,
              newCheckIn: newCheckInISO
            });
            results.push({ type: 'bed', id: bedId, success: true, result });
            successCount++;
          } catch (error) {
            console.error(`[EARLY CHECKIN] Failed to process bed ${bedId}:`, error);
            results.push({ type: 'bed', id: bedId, success: false, error: error.message });
            errorCount++;
          }
        }

        console.log('[EARLY CHECKIN] Individual processing complete:', { successCount, errorCount, results });

        if (successCount === 0) {
          throw new Error('Failed to process any entities for early check-in');
        }

        if (errorCount > 0) {
          toast.warning(`Early check-in completed with ${successCount} success(es) and ${errorCount} failure(s)`);
        }

        // Use the last successful response for the success message
        response = results.find(r => r.success)?.result || { success: true };
      }

      if (!response.success) {
        throw new Error(response.message || 'Failed to process early check-in');
      }

      toast.success(response.message || 'Early check-in processed successfully!');
      
      // Reset states
      setNewCheckInDate('');
      setEarlyCheckinPrice(0);
      setEarlyCheckinPreview(null);
      setEarlyCheckinEntityAvailability({ rooms: {}, beds: {} });
      setSelectedRoomsForEarlyCheckin([]);
      setSelectedBedsForEarlyCheckin([]);
      
      // Refresh booking data
      if (onUpdate) {
        onUpdate();
      }
      
    } catch (error) {
      console.error('Error processing early check-in:', error);
      toast.error(error.message || 'Failed to process early check-in');
    } finally {
      setProcessingEarlyCheckin(false);
    }
  };

  // Handle room exchange to private room
  const handlePrivateRoomExchange = async () => {
    if (!selectedNewRoom) {
      toast.error('Please select a room for exchange');
      return;
    }

    if (!window.confirm(`Are you sure you want to exchange to Room ${selectedNewRoom.roomNumber}? This will update the booking immediately.`)) {
      return;
    }

    setCalculatingExchangePrice(true);
    try {
      // For private rooms, only update roomId (omit bedId entirely)
      const updateData = {
        roomId: selectedNewRoom.id
      };
      console.log('Private room exchange data:', updateData);
      
      await bookingsService.update(booking.id, updateData);
      toast.success('Room exchange completed successfully');
      onUpdate(); // Refresh booking data
      onClose(); // Close modal
    } catch (error) {
      console.error('Room exchange error:', error);
      toast.error('Failed to exchange room. Please try again.');
    } finally {
      setCalculatingExchangePrice(false);
    }
  };

  // Handle room exchange to shared bed
  const handleSharedBedExchange = async () => {
    if (!selectedNewBed) {
      toast.error('Please select a bed for exchange');
      return;
    }

    if (!window.confirm(`Are you sure you want to exchange to Room ${selectedNewBed.room?.roomNumber} - Bed ${selectedNewBed.bedNumber}? This will update the booking immediately.`)) {
      return;
    }

    setCalculatingExchangePrice(true);
    try {
      // For shared beds, we need to update both room and bed
      const updateData = {
        roomId: selectedNewBed.room.id,
        bedId: selectedNewBed.id
      };
      console.log('Shared bed exchange data:', updateData);
      
      await bookingsService.update(booking.id, updateData);
      toast.success('Bed exchange completed successfully');
      onUpdate(); // Refresh booking data
      onClose(); // Close modal
    } catch (error) {
      console.error('Bed exchange error:', error);
      toast.error('Failed to exchange bed. Please try again.');
    } finally {
      setCalculatingExchangePrice(false);
    }
  };

  const sections = [
  { id: 'details', label: 'Details' },
    { id: 'overview', label: 'Overview' },
    { id: 'extend', label: 'Extend Booking' },
    // Early check-in only for confirmed or checked-in guests (super admin and property admin)
    ...((isSuperAdmin || isPropertyAdmin) && ['CONFIRMED', 'CHECKEDIN'].includes(booking.status) ? [{ id: 'early-checkin', label: 'Early Check-in' }] : []),
    // TEMPORARILY REMOVED - Early Checkout tab
    // ...((isSuperAdmin || isPropertyAdmin) && booking.status === 'CHECKEDIN' ? [{ id: 'early-checkout', label: 'Early Checkout' }] : []),
    // Room exchange only for confirmed or checked-in guests
    // TEMPORARILY COMMENTED OUT - Room Exchange tab
    // ...(['CONFIRMED', 'CHECKEDIN'].includes(booking.status) ? [{ id: 'exchange', label: 'Room Exchange' }] : []),
    // TEMPORARILY COMMENTED OUT - Course Allocation tab
    // { id: 'courses', label: 'Course Allocation' },
    { id: 'addons', label: 'Add-on Services' },
    { id: 'other-edits', label: 'Other Edits' },
    { id: 'payments', label: 'Payments' },
    // Custom Breakdowns tab only for SUPERADMIN
    ...(isSuperAdmin ? [{ id: 'custom-breakdowns', label: 'Custom Breakdowns' }] : []),
    { id: 'audit-trail', label: 'Audit Trail' },
    { id: 'id-proof', label: 'ID Proof' },
    // TEMPORARILY COMMENTED OUT - Summary tab
    // { id: 'summary', label: 'Summary' },
    // Delete section only for superadmins
    ...(isSuperAdmin ? [{ id: 'delete', label: 'Delete Booking' }] : [])
  ];

  // Render nothing if no booking data
  if (!booking) {
    return null;
  }

  // Helper to render section content (shared between mobile and desktop)
  const renderSectionContent = (sectionId) => {
    // All section content rendering will be done below
    return null; // Will be replaced by actual section content in the switch
  };

  // Mobile tab toggle handler
  const handleMobileTabToggle = (sectionId) => {
    setMobileOpenSection(mobileOpenSection === sectionId ? null : sectionId);
    setActiveSection(sectionId); // Keep both in sync
  };

  return (
    <>
      {/* CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media (max-width: 768px) {
            .edit-booking-modal-content {
              width: 100% !important;
              height: 100% !important;
              max-width: 100% !important;
              max-height: 100% !important;
              border-radius: 0 !important;
            }
            
            .edit-booking-nav-tabs {
              display: none !important;
            }
            
            .edit-booking-mobile-accordion {
              display: block !important;
            }
          }
        `}
      </style>
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isMobile ? 'white' : 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: isMobile ? 'stretch' : 'center',
          zIndex: 9999,
          padding: isMobile ? '0' : '10px',
          backdropFilter: isMobile ? 'none' : 'blur(2px)'
        }}
        onClick={isMobile ? undefined : handleBackdropClick}
      >
      <div 
        className="edit-booking-modal-content"
        style={{
          backgroundColor: '#E9DFCE',
          borderRadius: isMobile ? '0' : '12px',
          width: isMobile ? '100%' : '95vw',
          maxWidth: isMobile ? '100%' : '1200px',
          height: isMobile ? '100%' : '95vh',
          maxHeight: isMobile ? '100%' : '800px',
          display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 75px rgba(0, 0, 0, 0.4)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        position: 'relative',
        zIndex: 10000,
        overflow: 'hidden'
      }}
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #E3DFD3',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'white',
          borderRadius: '12px 12px 0 0',
          position: 'relative',
          zIndex: 10001,
          flexShrink: 0
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700', color: '#24332A' }}>
              Edit Booking
            </h2>
            <p style={{ margin: 0, color: '#5F6B61', fontSize: '13px' }}>
              Booking ID: {booking.id.slice(-8)} • {booking.user?.name}
              {booking.course ? ` • Currently: Staycation + ${booking.course.name}` : ' • Currently: Staycation Only'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#5F6B61',
              padding: '6px',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
              zIndex: 10002
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#EEEBE1';
              e.target.style.color = '#36433B';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#5F6B61';
            }}
          >
            ✕
          </button>
        </div>

        {/* Navigation Tabs - Desktop only; on mobile this is replaced by accordion inside content */}
        {!isMobile && (
        <div style={{
          padding: '0 24px',
          borderBottom: '1px solid #E3DFD3',
          display: 'flex',
          gap: '0',
          backgroundColor: 'white',
          position: 'relative',
          zIndex: 10001,
          flexShrink: 0,
          overflowX: 'auto'
        }}>
          {sections.map(section => (
            <button
              key={section.id}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveSection(section.id);
              }}
              style={{
                padding: '12px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: activeSection === section.id ? '2px solid #1A3E2A' : '2px solid transparent',
                color: activeSection === section.id ? '#1A3E2A' : '#5F6B61',
                fontWeight: activeSection === section.id ? '600' : '500',
                cursor: 'pointer',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                minWidth: 'fit-content'
              }}
              onMouseEnter={(e) => {
                if (activeSection !== section.id) {
                  e.target.style.color = '#36433B';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== section.id) {
                  e.target.style.color = '#5F6B61';
                }
              }}
            >
              {section.label}
            </button>
          ))}
        </div>
        )} {/* end !isMobile desktop tabs */}

        {/* Mobile: Accordion/Dropdown Tab Selector */}
        {isMobile && (
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid #E3DFD3',
            backgroundColor: '#F5F3EB',
            flexShrink: 0,
            overflowX: 'auto'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: '8px',
              overflowX: 'auto',
              paddingBottom: '4px'
            }}>
              {sections.map(section => (
                <button
                  key={section.id}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveSection(section.id);
                    setMobileOpenSection(section.id);
                  }}
                  style={{
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '20px',
                    backgroundColor: activeSection === section.id ? '#1A3E2A' : 'white',
                    color: activeSection === section.id ? 'white' : '#5F6B61',
                    fontWeight: activeSection === section.id ? '600' : '500',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                    border: activeSection === section.id ? 'none' : '1px solid #E3DFD3',
                    flexShrink: 0,
                    boxShadow: activeSection === section.id ? '0 2px 4px rgba(26, 62, 42, 0.4)' : 'none'
                  }}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: isMobile ? '12px' : '20px 24px',
          backgroundColor: 'white',
          position: 'relative',
          zIndex: 10001,
          minHeight: 0
        }}>
          {/* Details Section */}
          {activeSection === 'details' && (
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                marginBottom: '20px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '0'
              }}>
                <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '20px', fontWeight: '600', color: '#24332A' }}>
                  Complete Booking Details
                </h3>
                
                {/* Action Buttons Container */}
                {user.role !== 'VIEWER' && (
                  <div style={{ 
                    display: 'flex', 
                    gap: isMobile ? '8px' : '12px', 
                    flexWrap: 'wrap',
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    {/* Recalculate Price Button - ADMIN+ Access */}
                    <button
                      onClick={handleRecalculatePrice}
                      disabled={recalculateLoading}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: recalculateLoading ? '#A5A89B' : '#1A3E2A',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: recalculateLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        opacity: recalculateLoading ? 0.7 : 1,
                        ...mobileActionButtonStyle
                      }}
                      onMouseEnter={(e) => {
                        if (!recalculateLoading) {
                          e.currentTarget.style.backgroundColor = '#163626';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!recalculateLoading) {
                          e.currentTarget.style.backgroundColor = '#1A3E2A';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      <span style={{ fontSize: '16px' }}><WalletIcon size="1em" aria-hidden="true" /></span>
                      {recalculateLoading ? 'Recalculating...' : 'Recalculate Price'}
                    </button>

                    {/* Recalculate Dates Button - ADMIN+ Access */}
                    <button
                      onClick={handleRecalculateDates}
                      disabled={recalculateDatesLoading}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: recalculateDatesLoading ? '#A5A89B' : '#A9613C',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: recalculateDatesLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        opacity: recalculateDatesLoading ? 0.7 : 1,
                        ...mobileActionButtonStyle
                      }}
                      onMouseEnter={(e) => {
                        if (!recalculateDatesLoading) {
                          e.currentTarget.style.backgroundColor = '#975534';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!recalculateDatesLoading) {
                          e.currentTarget.style.backgroundColor = '#A9613C';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      <span style={{ fontSize: '16px' }}><CalendarIcon size="1em" aria-hidden="true" /></span>
                      {recalculateDatesLoading ? 'Recalculating...' : 'Recalculate Dates'}
                    </button>
                  </div>
                )}
              </div>

              {/* Price Recalculation Result Display */}
              {recalculateResult && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#F0F3EE',
                  border: '1px solid #1A3E2A',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '18px' }}><BarChart3Icon size="1em" aria-hidden="true" /></span>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#0F2619' }}>
                      Price Recalculation Results
                    </h4>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div style={{
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #E1E9E2'
                    }}>
                      <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '4px' }}>Previous Total</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#24332A' }}>
                        ${recalculateResult.oldTotalPrice.toFixed(2)}
                      </div>
                    </div>
                    
                    <div style={{
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #E1E9E2'
                    }}>
                      <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '4px' }}>New Total</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#163626' }}>
                        ${recalculateResult.newTotalPrice.toFixed(2)}
                      </div>
                    </div>
                    
                    <div style={{
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #E1E9E2'
                    }}>
                      <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '4px' }}>Difference</div>
                      <div style={{ 
                        fontSize: '18px', 
                        fontWeight: '600', 
                        color: recalculateResult.difference > 0 ? '#8E3628' : recalculateResult.difference < 0 ? '#163626' : '#5F6B61'
                      }}>
                        {recalculateResult.difference > 0 ? '+' : ''}{recalculateResult.difference < 0 ? '-' : ''}${Math.abs(recalculateResult.difference).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Breakdown by Entity Type */}
                  {recalculateResult.breakdown && (
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F2619', marginBottom: '8px' }}>
                        Breakdown by Type:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {Object.entries(recalculateResult.breakdown).map(([type, amount]) => (
                          <div key={type} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: '1px solid #E1E9E2',
                            fontSize: '13px'
                          }}>
                            <span style={{ color: '#4A564D', fontWeight: '500' }}>
                              {type === 'BOOKED_ROOM' ? 'Rooms' : 
                               type === 'BOOKED_BED' ? 'Beds' : 
                               type === 'ADDON_SERVICE' ? 'Addons' : 
                               type}
                            </span>
                            <span style={{ color: '#24332A', fontWeight: '600' }}>
                              ${amount.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dates Recalculation Result Display */}
              {recalculateDatesResult && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#F8F0EA',
                  border: '1px solid #A9613C',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '18px' }}><CalendarIcon size="1em" aria-hidden="true" /></span>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#663923' }}>
                      Date Recalculation Results
                    </h4>
                  </div>
                  
                  {recalculateDatesResult.updated ? (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #E4C2AC'
                        }}>
                          <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '4px' }}>Previous Check-in</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
                            {new Date(recalculateDatesResult.oldDates.checkIn).toLocaleString('en-US', {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })}
                          </div>
                        </div>
                        
                        <div style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #E4C2AC'
                        }}>
                          <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '4px' }}>New Check-in</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#163626' }}>
                            {new Date(recalculateDatesResult.newDates.checkIn).toLocaleString('en-US', {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })}
                          </div>
                        </div>

                        <div style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #E4C2AC'
                        }}>
                          <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '4px' }}>Previous Check-out</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
                            {new Date(recalculateDatesResult.oldDates.checkOut).toLocaleString('en-US', {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })}
                          </div>
                        </div>
                        
                        <div style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #E4C2AC'
                        }}>
                          <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '4px' }}>New Check-out</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#163626' }}>
                            {new Date(recalculateDatesResult.newDates.checkOut).toLocaleString('en-US', {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })}
                          </div>
                        </div>
                      </div>

                      {recalculateDatesResult.booking && (
                        <div style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #E4C2AC',
                          fontSize: '13px'
                        }}>
                          <div style={{ color: '#4A564D', marginBottom: '4px' }}>
                            <strong>Booking:</strong> {recalculateDatesResult.booking.bookingNumber}
                          </div>
                          <div style={{ color: '#4A564D' }}>
                            Entities: {recalculateDatesResult.booking.bookedRoomsCount} room(s), {recalculateDatesResult.booking.bookedBedsCount} bed(s)
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #E4C2AC'
                    }}>
                      <div style={{ fontSize: '14px', color: '#163626', fontWeight: '500', marginBottom: '8px' }}>
                        ✓ Dates are already correct
                      </div>
                      {recalculateDatesResult.currentDates && (
                        <div style={{ fontSize: '13px', color: '#4A564D' }}>
                          Check-in: {new Date(recalculateDatesResult.currentDates.checkIn).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                          <br />
                          Check-out: {new Date(recalculateDatesResult.currentDates.checkOut).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!bookingDetailsResponse.data ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  backgroundColor: '#F5F3EB', 
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <div style={{ fontSize: '16px', color: '#5F6B61', marginBottom: '8px' }}>
                    Loading booking details...
                  </div>
                  <div style={{ fontSize: '14px', color: '#A5A89B' }}>
                    Please wait while we fetch the complete booking information.
                  </div>
                </div>
              ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                {/* Guest Information Section */}
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #E3DFD3',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <div
                    onClick={() => toggleSection('guestInfo')}
                    style={{
                      padding: '16px',
                      backgroundColor: '#F8F0EA',
                      borderBottom: expandedSections.guestInfo ? '1px solid #E3DFD3' : 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '20px' }}><UserIcon size="1em" aria-hidden="true" /></span>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: '#4F2C1B' }}>Guest Information</span>
                    </div>
                    <span style={{ fontSize: '18px', color: '#4F2C1B' }}>
                      {expandedSections.guestInfo ? '▼' : '▶'}
                    </span>
                  </div>
                  {expandedSections.guestInfo && (
                    <div style={{ padding: '16px' }}>
                      {(() => {
                        // Try to find guest data from multiple possible locations
                        const guestData = bookingDetailsResponse.data?.guest || 
                                         bookingDetailsResponse.guest ||
                                         booking.user ||
                                         booking.guest ||
                                         null;
                        
                        console.log('Guest data check:', {
                          fromDetailsResponse: bookingDetailsResponse.data?.guest,
                          fromBookingProp: booking.user || booking.guest,
                          resolved: guestData
                        });

                        return guestData ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                        <div>
                          <span style={{ color: '#5F6B61', fontWeight: '500' }}>Name:</span>
                          <div style={{ color: '#24332A', fontWeight: '600', marginTop: '4px' }}>
                            {guestData.name || booking.guestName || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#5F6B61', fontWeight: '500' }}>Email:</span>
                          <div style={{ color: '#24332A', fontWeight: '600', marginTop: '4px' }}>
                            {guestData.email || booking.guestEmail || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#5F6B61', fontWeight: '500' }}>Phone:</span>
                          <div style={{ color: '#24332A', fontWeight: '600', marginTop: '4px' }}>
                            {guestData.phone || booking.guestPhone || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#5F6B61', fontWeight: '500' }}>Gender:</span>
                          <div style={{ color: '#24332A', fontWeight: '600', marginTop: '4px' }}>
                            {guestData.gender || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#5F6B61', fontWeight: '500' }}>Role:</span>
                          <div style={{ marginTop: '4px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: '#E1E9E2',
                              color: '#0F2619'
                            }}>
                              {guestData.role || 'GUEST'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#5F6B61', fontWeight: '500' }}>Verified:</span>
                          <div style={{ marginTop: '4px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: guestData.isVerified ? '#E1E9E2' : '#F3E6CB',
                              color: guestData.isVerified ? '#0F2619' : '#684A1F'
                            }}>
                              {guestData.isVerified ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <span style={{ color: '#5F6B61', fontWeight: '500' }}>Guest ID:</span>
                          <div style={{ color: '#5F6B61', marginTop: '4px', fontSize: '12px', fontFamily: 'monospace' }}>
                            {guestData.id || 'N/A'}
                          </div>
                        </div>
                        {guestData.createdAt && (
                          <div>
                            <span style={{ color: '#5F6B61', fontWeight: '500' }}>Member Since:</span>
                            <div style={{ color: '#5F6B61', marginTop: '4px', fontSize: '12px' }}>
                              {new Date(guestData.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        )}
                      </div>
                      ) : (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '20px', 
                          backgroundColor: '#F9EEEB', 
                          borderRadius: '6px',
                          border: '1px solid #E5B3A9'
                        }}>
                          <div style={{ fontSize: '14px', color: '#5F241B', marginBottom: '8px' }}>
                            No guest information available for this booking.
                          </div>
                          <div style={{ fontSize: '12px', color: '#8E3628' }}>
                            Booking ID: {booking.id}
                          </div>
                        </div>
                      );
                      })()}
                    </div>
                  )}
                </div>

                {/* Booked Rooms Section */}
                {bookingDetailsResponse.data?.bookedRooms && bookingDetailsResponse.data.bookedRooms.length > 0 && (
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #E3DFD3',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <div
                      onClick={() => toggleSection('bookedRooms')}
                      style={{
                        padding: '16px',
                        backgroundColor: '#F3E6CB',
                        borderBottom: expandedSections.bookedRooms ? '1px solid #E3DFD3' : 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}><HotelIcon size="1em" aria-hidden="true" /></span>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#4F3818' }}>
                          Booked Rooms ({bookingDetailsResponse.data.bookedRooms.length})
                        </span>
                      </div>
                      <span style={{ fontSize: '18px', color: '#4F3818' }}>
                        {expandedSections.bookedRooms ? '▼' : '▶'}
                      </span>
                    </div>
                    {expandedSections.bookedRooms && (
                      <div style={{ padding: '16px' }}>
                        {bookingDetailsResponse.data.bookedRooms.map((bookedRoom, idx) => (
                          <div key={idx} style={{ 
                            backgroundColor: '#F5F3EB', 
                            padding: '16px', 
                            borderRadius: '8px',
                            border: '1px solid #E3DFD3',
                            marginBottom: idx < bookingDetailsResponse.data.bookedRooms.length - 1 ? '12px' : '0'
                          }}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: '#24332A' }}>
                              Room: {bookedRoom.roomNumber}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px', marginBottom: '12px' }}>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Room Type:</span>
                                <div style={{ color: '#24332A', fontWeight: '600', marginTop: '2px' }}>
                                  {bookedRoom.roomTypeName}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Status:</span>
                                <div style={{ marginTop: '2px' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    backgroundColor: bookedRoom.status === 'PENDING' ? '#F3E6CB' : '#E1E9E2',
                                    color: bookedRoom.status === 'PENDING' ? '#684A1F' : '#0F2619'
                                  }}>
                                    {bookedRoom.status}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Nights:</span>
                                <div style={{ color: '#163626', fontWeight: '600', marginTop: '2px' }}>
                                  {bookedRoom.nights}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Base Price/Night:</span>
                                <div style={{ color: '#163626', fontWeight: '600', marginTop: '2px' }}>
                                  ₹{bookedRoom.basePrice?.toFixed(2) || '0.00'}
                                </div>
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: '#5F6B61' }}>Description:</span>
                                <div style={{ color: '#24332A', marginTop: '2px', fontSize: '12px' }}>
                                  {bookedRoom.roomTypeDesc || 'No description'}
                                </div>
                              </div>
                            </div>
                            
                            {bookedRoom.priceDetails && (
                              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #E3DFD3' }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
                                  Price Breakdown:
                                </div>
                                {bookedRoom.priceDetails.breakdownItems?.map((item, itemIdx) => (
                                  <div key={itemIdx} style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    fontSize: '12px',
                                    padding: '4px 0',
                                    borderBottom: itemIdx < bookedRoom.priceDetails.breakdownItems.length - 1 ? '1px solid #EEEBE1' : 'none'
                                  }}>
                                    <span style={{ color: '#5F6B61' }}>{item.name} ({item.description}):</span>
                                    <span style={{ 
                                      color: item.breakdownType === 'TAX' ? '#9E712E' : '#163626',
                                      fontWeight: '600'
                                    }}>
                                      ₹{item.amount?.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  marginTop: '8px',
                                  paddingTop: '8px',
                                  borderTop: '2px solid #E3DFD3',
                                  color: '#163626'
                                }}>
                                  <span>Final Amount:</span>
                                  <span>₹{bookedRoom.priceDetails.finalAmount?.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Booked Beds Section */}
                {bookingDetailsResponse.data?.bookedBeds && bookingDetailsResponse.data.bookedBeds.length > 0 && (
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #E3DFD3',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <div
                      onClick={() => toggleSection('bookedBeds')}
                      style={{
                        padding: '16px',
                        backgroundColor: '#E1E9E2',
                        borderBottom: expandedSections.bookedBeds ? '1px solid #E3DFD3' : 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}><BedDoubleIcon size="1em" aria-hidden="true" /></span>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#0F2619' }}>
                          Booked Beds ({bookingDetailsResponse.data.bookedBeds.length})
                        </span>
                      </div>
                      <span style={{ fontSize: '18px', color: '#0F2619' }}>
                        {expandedSections.bookedBeds ? '▼' : '▶'}
                      </span>
                    </div>
                    {expandedSections.bookedBeds && (
                      <div style={{ padding: '16px' }}>
                        {bookingDetailsResponse.data.bookedBeds.map((bookedBed, idx) => (
                          <div key={idx} style={{ 
                            backgroundColor: '#F5F3EB', 
                            padding: '16px', 
                            borderRadius: '8px',
                            border: '1px solid #E3DFD3',
                            marginBottom: idx < bookingDetailsResponse.data.bookedBeds.length - 1 ? '12px' : '0'
                          }}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: '#24332A' }}>
                              Bed {bookedBed.bedNumber} in Room {bookedBed.roomNumber}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px', marginBottom: '12px' }}>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Room Type:</span>
                                <div style={{ color: '#24332A', fontWeight: '600', marginTop: '2px' }}>
                                  {bookedBed.roomTypeName}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Status:</span>
                                <div style={{ marginTop: '2px' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    backgroundColor: bookedBed.status === 'PENDING' ? '#F3E6CB' : '#E1E9E2',
                                    color: bookedBed.status === 'PENDING' ? '#684A1F' : '#0F2619'
                                  }}>
                                    {bookedBed.status}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Nights:</span>
                                <div style={{ color: '#163626', fontWeight: '600', marginTop: '2px' }}>
                                  {bookedBed.nights}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Base Price/Night:</span>
                                <div style={{ color: '#163626', fontWeight: '600', marginTop: '2px' }}>
                                  ₹{bookedBed.basePrice?.toFixed(2) || '0.00'}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Shared:</span>
                                <div style={{ marginTop: '2px' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    backgroundColor: bookedBed.isShared ? '#F3E6CB' : '#E1E9E2',
                                    color: bookedBed.isShared ? '#684A1F' : '#0F2619'
                                  }}>
                                    {bookedBed.isShared ? 'Yes' : 'No'}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Female Only:</span>
                                <div style={{ marginTop: '2px' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    backgroundColor: bookedBed.isFemaleOnly ? '#F1E0D4' : '#EEEBE1',
                                    color: bookedBed.isFemaleOnly ? '#4F2C1B' : '#36433B'
                                  }}>
                                    {bookedBed.isFemaleOnly ? 'Yes' : 'No'}
                                  </span>
                                </div>
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: '#5F6B61' }}>Description:</span>
                                <div style={{ color: '#24332A', marginTop: '2px', fontSize: '12px' }}>
                                  {bookedBed.roomTypeDesc || 'No description'}
                                </div>
                              </div>
                            </div>
                            
                            {bookedBed.priceDetails && (
                              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #E3DFD3' }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
                                  Price Breakdown:
                                </div>
                                {bookedBed.priceDetails.breakdownItems?.map((item, itemIdx) => (
                                  <div key={itemIdx} style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    fontSize: '12px',
                                    padding: '4px 0',
                                    borderBottom: itemIdx < bookedBed.priceDetails.breakdownItems.length - 1 ? '1px solid #EEEBE1' : 'none'
                                  }}>
                                    <span style={{ color: '#5F6B61' }}>{item.name} ({item.description}):</span>
                                    <span style={{ 
                                      color: item.breakdownType === 'TAX' ? '#9E712E' : '#163626',
                                      fontWeight: '600'
                                    }}>
                                      ₹{item.amount?.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  marginTop: '8px',
                                  paddingTop: '8px',
                                  borderTop: '2px solid #E3DFD3',
                                  color: '#163626'
                                }}>
                                  <span>Final Amount:</span>
                                  <span>₹{bookedBed.priceDetails.finalAmount?.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Booked Courses Section */}
                {bookingDetailsResponse.data?.bookedCourses && bookingDetailsResponse.data.bookedCourses.length > 0 && (
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #E3DFD3',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <div
                      onClick={() => toggleSection('bookedCourses')}
                      style={{
                        padding: '16px',
                        backgroundColor: '#F1E0D4',
                        borderBottom: expandedSections.bookedCourses ? '1px solid #E3DFD3' : 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}><Flower2Icon size="1em" aria-hidden="true" /></span>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#4F2C1B' }}>
                          Booked Courses ({bookingDetailsResponse.data.bookedCourses.length})
                        </span>
                      </div>
                      <span style={{ fontSize: '18px', color: '#4F2C1B' }}>
                        {expandedSections.bookedCourses ? '▼' : '▶'}
                      </span>
                    </div>
                    {expandedSections.bookedCourses && (
                      <div style={{ padding: '16px' }}>
                        {bookingDetailsResponse.data.bookedCourses.map((bookedCourse, idx) => (
                          <div key={idx} style={{ 
                            backgroundColor: '#F5F3EB', 
                            padding: '16px', 
                            borderRadius: '8px',
                            border: '1px solid #E3DFD3',
                            marginBottom: idx < bookingDetailsResponse.data.bookedCourses.length - 1 ? '12px' : '0'
                          }}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: '#24332A' }}>
                              {bookedCourse.courseName}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px', marginBottom: '12px' }}>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Status:</span>
                                <div style={{ marginTop: '2px' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    backgroundColor: bookedCourse.status === 'PENDING' ? '#F3E6CB' : '#E1E9E2',
                                    color: bookedCourse.status === 'PENDING' ? '#684A1F' : '#0F2619'
                                  }}>
                                    {bookedCourse.status}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Quantity:</span>
                                <div style={{ color: '#163626', fontWeight: '600', marginTop: '2px' }}>
                                  {bookedCourse.quantity}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Start Date:</span>
                                <div style={{ color: '#24332A', fontWeight: '600', marginTop: '2px' }}>
                                  {new Date(bookedCourse.startDate).toLocaleDateString('en-US', { 
                                    year: 'numeric', month: 'short', day: 'numeric' 
                                  })}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>End Date:</span>
                                <div style={{ color: '#24332A', fontWeight: '600', marginTop: '2px' }}>
                                  {new Date(bookedCourse.endDate).toLocaleDateString('en-US', { 
                                    year: 'numeric', month: 'short', day: 'numeric' 
                                  })}
                                </div>
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: '#5F6B61' }}>Description:</span>
                                <div style={{ color: '#24332A', marginTop: '2px', fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                                  {bookedCourse.courseDesc || 'No description'}
                                </div>
                              </div>
                            </div>
                            
                            {bookedCourse.priceDetails && (
                              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #E3DFD3' }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
                                  Price Breakdown:
                                </div>
                                {bookedCourse.priceDetails.breakdownItems?.map((item, itemIdx) => (
                                  <div key={itemIdx} style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    fontSize: '12px',
                                    padding: '4px 0',
                                    borderBottom: itemIdx < bookedCourse.priceDetails.breakdownItems.length - 1 ? '1px solid #EEEBE1' : 'none'
                                  }}>
                                    <span style={{ color: '#5F6B61' }}>{item.name} ({item.description}):</span>
                                    <span style={{ 
                                      color: item.breakdownType === 'TAX' ? '#9E712E' : '#163626',
                                      fontWeight: '600'
                                    }}>
                                      ₹{item.amount?.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  marginTop: '8px',
                                  paddingTop: '8px',
                                  borderTop: '2px solid #E3DFD3',
                                  color: '#163626'
                                }}>
                                  <span>Final Amount:</span>
                                  <span>₹{bookedCourse.priceDetails.finalAmount?.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Add-on Services Section */}
                {bookingDetailsResponse.data?.addOns && bookingDetailsResponse.data.addOns.length > 0 && (
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #E3DFD3',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <div
                      onClick={() => toggleSection('addOns')}
                      style={{
                        padding: '16px',
                        backgroundColor: '#F1E0D4',
                        borderBottom: expandedSections.addOns ? '1px solid #E3DFD3' : 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}><ShoppingBagIcon size="1em" aria-hidden="true" /></span>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#663923' }}>
                          Add-on Services ({bookingDetailsResponse.data.addOns.length})
                        </span>
                      </div>
                      <span style={{ fontSize: '18px', color: '#663923' }}>
                        {expandedSections.addOns ? '▼' : '▶'}
                      </span>
                    </div>
                    {expandedSections.addOns && (
                      <div style={{ padding: '16px' }}>
                        {bookingDetailsResponse.data.addOns.map((addon, idx) => (
                          <div key={idx} style={{ 
                            backgroundColor: '#F5F3EB', 
                            padding: '16px', 
                            borderRadius: '8px',
                            border: '1px solid #E3DFD3',
                            marginBottom: idx < bookingDetailsResponse.data.addOns.length - 1 ? '12px' : '0'
                          }}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: '#24332A' }}>
                              {addon.service?.name || addon.item?.name || 'Add-on Service'}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px', marginBottom: '12px' }}>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Service Type:</span>
                                <div style={{ color: '#24332A', fontWeight: '600', marginTop: '2px' }}>
                                  {addon.service ? 'Service' : 'Item'}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Quantity:</span>
                                <div style={{ color: '#163626', fontWeight: '600', marginTop: '2px' }}>
                                  {addon.quantity || 1}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Unit Price:</span>
                                <div style={{ color: '#163626', fontWeight: '600', marginTop: '2px' }}>
                                  ₹{(addon.price || addon.item?.unitPrice || 0).toFixed(2)}
                                </div>
                              </div>
                              <div>
                                <span style={{ color: '#5F6B61' }}>Subtotal:</span>
                                <div style={{ color: '#163626', fontWeight: '600', marginTop: '2px' }}>
                                  ₹{((addon.price || addon.item?.unitPrice || 0) * (addon.quantity || 1)).toFixed(2)}
                                </div>
                              </div>
                              {addon.service?.description && (
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <span style={{ color: '#5F6B61' }}>Description:</span>
                                  <div style={{ color: '#24332A', marginTop: '2px', fontSize: '12px' }}>
                                    {addon.service.description}
                                  </div>
                                </div>
                              )}
                              {addon.item && (
                                <>
                                  <div>
                                    <span style={{ color: '#5F6B61' }}>Item Name:</span>
                                    <div style={{ color: '#24332A', fontWeight: '600', marginTop: '2px' }}>
                                      {addon.item.name}
                                    </div>
                                  </div>
                                  {addon.item.sku && (
                                    <div>
                                      <span style={{ color: '#5F6B61' }}>SKU:</span>
                                      <div style={{ color: '#5F6B61', marginTop: '2px', fontSize: '12px' }}>
                                        {addon.item.sku}
                                      </div>
                                    </div>
                                  )}
                                  {addon.item.description && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                      <span style={{ color: '#5F6B61' }}>Item Description:</span>
                                      <div style={{ color: '#24332A', marginTop: '2px', fontSize: '12px' }}>
                                        {addon.item.description}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: '#5F6B61' }}>Added On:</span>
                                <div style={{ color: '#5F6B61', marginTop: '2px', fontSize: '12px' }}>
                                  {new Date(addon.createdAt).toLocaleString()}
                                </div>
                              </div>
                            </div>
                            
                            {addon.priceDetails && (
                              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #E3DFD3' }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
                                  Price Breakdown:
                                </div>
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  fontSize: '12px',
                                  padding: '4px 0',
                                  borderBottom: '1px solid #EEEBE1'
                                }}>
                                  <span style={{ color: '#5F6B61' }}>Base Price per Unit:</span>
                                  <span style={{ color: '#163626', fontWeight: '600' }}>
                                    ₹{addon.priceDetails.basePricePerUnit?.toFixed(2)}
                                  </span>
                                </div>
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  fontSize: '12px',
                                  padding: '4px 0',
                                  borderBottom: '1px solid #EEEBE1'
                                }}>
                                  <span style={{ color: '#5F6B61' }}>Quantity:</span>
                                  <span style={{ color: '#163626', fontWeight: '600' }}>
                                    {addon.priceDetails.quantity || 1}
                                  </span>
                                </div>
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  fontSize: '12px',
                                  padding: '4px 0',
                                  borderBottom: '1px solid #EEEBE1'
                                }}>
                                  <span style={{ color: '#5F6B61' }}>Subtotal:</span>
                                  <span style={{ color: '#163626', fontWeight: '600' }}>
                                    ₹{addon.priceDetails.subtotal?.toFixed(2)}
                                  </span>
                                </div>
                                {addon.priceDetails.breakdownItems?.map((item, itemIdx) => (
                                  <div key={itemIdx} style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    fontSize: '12px',
                                    padding: '4px 0',
                                    borderBottom: itemIdx < addon.priceDetails.breakdownItems.length - 1 ? '1px solid #EEEBE1' : 'none'
                                  }}>
                                    <span style={{ color: '#5F6B61' }}>{item.name} ({item.description}):</span>
                                    <span style={{ 
                                      color: item.breakdownType === 'TAX' ? '#9E712E' : '#163626',
                                      fontWeight: '600'
                                    }}>
                                      ₹{item.amount?.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  marginTop: '8px',
                                  paddingTop: '8px',
                                  borderTop: '2px solid #E3DFD3',
                                  color: '#163626'
                                }}>
                                  <span>Final Amount:</span>
                                  <span>₹{addon.priceDetails.finalAmount?.toFixed(2)}</span>
                                </div>
                                {addon.priceDetails.totalTaxes > 0 && (
                                  <div style={{ 
                                    fontSize: '11px',
                                    color: '#5F6B61',
                                    marginTop: '8px',
                                    paddingTop: '8px',
                                    borderTop: '1px solid #EEEBE1'
                                  }}>
                                    Total Taxes: ₹{addon.priceDetails.totalTaxes?.toFixed(2)} | 
                                    Total Fees: ₹{addon.priceDetails.totalFees?.toFixed(2)}
                                    {addon.priceDetails.totalDiscounts > 0 && ` | Discounts: ₹${addon.priceDetails.totalDiscounts?.toFixed(2)}`}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* Overview Section */}
          {activeSection === 'overview' && (
            <div>
              <h3 style={{ margin: '0 0 24px 0', fontSize: isMobile ? '16px' : '20px', fontWeight: '600', color: '#24332A' }}>
                Booking Overview
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: isMobile ? '12px' : '24px'
              }}>
                {/* Guest Information */}
                <div style={{
                  backgroundColor: '#F5F3EB',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                      Guest Information
                    </h4>
                    <div style={{ ...mobileButtonRowStyle, width: isMobile ? '100%' : 'auto' }}>
                      <button
                        onClick={() => setIsEditingGuestCount(!isEditingGuestCount)}
                        style={{
                          backgroundColor: isEditingGuestCount ? '#8E3628' : '#1A3E2A',
                          color: 'white',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          ...mobileActionButtonStyle
                        }}
                      >
                        {isEditingGuestCount ? '✕ Cancel' : 'Edit Count'}
                      </button>
                      {(isSuperAdmin || isPropertyAdmin) && (
                        <button
                          onClick={() => setIsEditingUser(!isEditingUser)}
                          style={{
                            backgroundColor: isEditingUser ? '#8E3628' : '#A9613C',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            ...mobileActionButtonStyle
                          }}
                        >
                          {isEditingUser ? '✕ Cancel' : 'Edit User'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    {/* User Details Section */}
                    {isEditingUser && (isSuperAdmin || isPropertyAdmin) ? (
                      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F3E6CB', borderRadius: '6px', border: '1px solid #B68235' }}>
                        <div style={{ marginBottom: '12px', fontSize: '13px', fontWeight: '600', color: '#684A1F' }}>
                          {isSuperAdmin ? 'Super Admin' : 'Property Admin'} Edit Mode
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                              Name *
                            </label>
                            <input
                              type="text"
                              value={editableUserData.name}
                              onChange={(e) => setEditableUserData(prev => ({ ...prev, name: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                              placeholder="Full name"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                              Email *
                            </label>
                            <input
                              type="email"
                              value={editableUserData.email}
                              onChange={(e) => setEditableUserData(prev => ({ ...prev, email: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                              placeholder="email@example.com"
                            />
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                              Phone
                            </label>
                            <input
                              type="tel"
                              value={editableUserData.phone}
                              onChange={(e) => setEditableUserData(prev => ({ ...prev, phone: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                              placeholder="+91-9876543210"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                              Gender
                            </label>
                            <select
                              value={editableUserData.gender}
                              onChange={(e) => setEditableUserData(prev => ({ ...prev, gender: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                            >
                              <option value="">Select Gender</option>
                              <option value="MALE">Male</option>
                              <option value="FEMALE">Female</option>
                              <option value="OTHER">Other</option>
                            </select>
                          </div>
                        </div>
                        <div style={{ marginTop: '12px' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                            Address
                          </label>
                          <input
                            type="text"
                            value={editableUserData.address}
                            onChange={(e) => setEditableUserData(prev => ({ ...prev, address: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #D2CDBF',
                              borderRadius: '4px',
                              fontSize: '13px'
                            }}
                            placeholder="Street address"
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                              City
                            </label>
                            <input
                              type="text"
                              value={editableUserData.city}
                              onChange={(e) => setEditableUserData(prev => ({ ...prev, city: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                              placeholder="City"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                              State
                            </label>
                            <input
                              type="text"
                              value={editableUserData.state}
                              onChange={(e) => setEditableUserData(prev => ({ ...prev, state: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                              placeholder="State"
                            />
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                              Country
                            </label>
                            <input
                              type="text"
                              value={editableUserData.country}
                              onChange={(e) => setEditableUserData(prev => ({ ...prev, country: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                              placeholder="Country"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                              Pincode
                            </label>
                            <input
                              type="text"
                              value={editableUserData.pincode}
                              onChange={(e) => setEditableUserData(prev => ({ ...prev, pincode: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: '1px solid #D2CDBF',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}
                              placeholder="Pincode"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div><strong>Name:</strong> {booking.user?.name || booking.guestName}</div>
                        <div><strong>Email:</strong> {booking.user?.email || booking.guestEmail}</div>
                        {(booking.user?.phone || editableUserData.phone) && (
                          <div><strong>Phone:</strong> {booking.user?.phone || editableUserData.phone}</div>
                        )}
                        {(booking.user?.gender || editableUserData.gender) && (
                          <div><strong>Gender:</strong> {booking.user?.gender || editableUserData.gender}</div>
                        )}
                        {booking.user?.role && <div><strong>Role:</strong> {booking.user.role}</div>}
                        {booking.user?.address && (
                          <div><strong>Address:</strong> {booking.user.address}</div>
                        )}
                        {(booking.user?.city || booking.user?.state || booking.user?.country) && (
                          <div><strong>Location:</strong> {[booking.user.city, booking.user.state, booking.user.country].filter(Boolean).join(', ')}{booking.user?.pincode ? ` - ${booking.user.pincode}` : ''}</div>
                        )}
                      </>
                    )}
                    
                    {/* Guest Count Section */}
                    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #E3DFD3' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <strong>Guest Count:</strong>
                        {isEditingGuestCount ? (
                          <CounterInput
                            label=""
                            value={editableGuestCount}
                            onChange={setEditableGuestCount}
                            min={1}
                            max={10}
                            style={{ width: '150px' }}
                          />
                        ) : (
                          <span style={{ 
                            backgroundColor: '#E1E9E2', 
                            color: '#163626', 
                            padding: '4px 8px', 
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            {editableGuestCount || booking.guestCount || 1} {(editableGuestCount || booking.guestCount || 1) > 1 ? 'guests' : 'guest'}
                          </span>
                        )}
                      </div>
                      {editableGuestCount !== booking.guestCount && (
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#9E712E', 
                          marginTop: '4px',
                          fontStyle: 'italic'
                        }}>
                          Changed from {booking.guestCount || 1} to {editableGuestCount}
                        </div>
                      )}
                    </div>

                    {/* Super Admin Save Button */}
                    {(isSuperAdmin || isPropertyAdmin) && (isEditingUser || isEditingGuestCount) && (
                      <div style={{ marginTop: '16px', textAlign: 'center' }}>
                        <button
                          onClick={handleSuperAdminUpdate}
                          disabled={updatingBooking}
                          style={{
                            backgroundColor: updatingBooking ? '#A5A89B' : '#163626',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: updatingBooking ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {updatingBooking ? (
                            <>
                              <span style={{ 
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                border: '2px solid #ffffff40',
                                borderTop: '2px solid #ffffff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}></span>
                              Updating...
                            </>
                          ) : (
                            <>
                              Save User & Guest Count Changes
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Accommodation Details */}
                <div style={{
                  backgroundColor: '#F5F3EB',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                    Accommodation
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    <div><strong>Accommodation:</strong> {getAccommodationDisplay(booking)}</div>
                    {(() => {
                      const rooms = getAllRooms(booking);
                      const beds = getAllBeds(booking);
                      const room = primaryRoom;
                      const bed = primaryBed;
                      
                      // Show type and property from primary accommodation
                      const typeName = room?.roomType?.name || bed?.roomType?.name || 'N/A';
                      const propertyName = room?.property?.name || bed?.room?.property?.name || 'N/A';
                      
                      return (
                        <>
                          <div><strong>Type:</strong> {typeName}</div>
                          <div><strong>Property:</strong> {propertyName}</div>
                          
                          {/* Show details for multiple rooms/beds */}
                          {rooms.length > 1 && (
                            <div style={{ marginTop: '12px' }}>
                              <strong>All Rooms:</strong>
                              {rooms.map((r, idx) => (
                                <div key={r.bookedRoomId || idx} style={{ marginLeft: '12px', fontSize: '13px', color: '#5F6B61' }}>
                                  • Room {r.roomNumber}: {r.nights} nights @ ₹{r.finalAmount}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {beds.length > 1 && (
                            <div style={{ marginTop: '12px' }}>
                              <strong>All Beds:</strong>
                              {beds.map((b, idx) => (
                                <div key={b.bookedBedId || idx} style={{ marginLeft: '12px', fontSize: '13px', color: '#5F6B61' }}>
                                  • Bed {b.bedNumber} in Room {b.roomNumber}: {b.nights} nights @ ₹{b.finalAmount}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Booking Details */}
                <div style={{
                  backgroundColor: '#F5F3EB',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                    Booking Details
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    <div><strong>Check-in:</strong> {formatDate(booking.checkIn)}</div>
                    <div><strong>Check-out:</strong> {formatDate(booking.checkOut)}</div>
                    <div><strong>Duration:</strong> {calculateNights()} nights</div>
                    <div><strong>Status:</strong> 
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: booking.status === 'CONFIRMED' ? '#E1E9E2' : '#F3E6CB',
                        color: booking.status === 'CONFIRMED' ? '#0F2619' : '#684A1F'
                      }}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Web Check-in Link */}
                <div style={{
                  backgroundColor: '#F0F3EE',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #1A3E2A'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#0F2619' }}>
                    Web Check-in Link
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    <p style={{ margin: '0 0 12px 0', color: '#0B1D13', fontSize: '13px' }}>
                      Share this link with the guest for online check-in
                    </p>
                    <div style={{
                      backgroundColor: 'white',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #C4D4C8',
                      marginBottom: '12px',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      color: '#0F2619',
                      wordBreak: 'break-all',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const link = `${API_CONFIG.WEB_CHECKIN_URL}/sign/${booking.id}`;
                      navigator.clipboard.writeText(link);
                      setCopiedLink(true);
                      setTimeout(() => setCopiedLink(false), 2000);
                      toast.success('Link copied to clipboard!');
                    }}
                    title="Click to copy"
                    >
                      {API_CONFIG.WEB_CHECKIN_URL}/sign/{booking.id}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const link = `${API_CONFIG.WEB_CHECKIN_URL}/sign/${booking.id}`;
                        navigator.clipboard.writeText(link);
                        setCopiedLink(true);
                        setTimeout(() => setCopiedLink(false), 2000);
                        toast.success('Link copied to clipboard!');
                      }}
                      style={{
                        backgroundColor: copiedLink ? '#1A3E2A' : '#1A3E2A',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease',
                        width: '100%',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        if (!copiedLink) {
                          e.target.style.backgroundColor = '#163626';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!copiedLink) {
                          e.target.style.backgroundColor = '#1A3E2A';
                        }
                      }}
                    >
                      {copiedLink ? (
                        <>
                          ✓ Link Copied!
                        </>
                      ) : (
                        <>
                          Copy Link
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Course Information */}
                {(() => {
                  const courses = getAllCourses(booking);
                  if (courses.length === 0) return null;
                  
                  return (
                    <div style={{
                      backgroundColor: '#F5F3EB',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #E3DFD3'
                    }}>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        Course Details
                      </h4>
                      <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                        {courses.length === 1 ? (
                          // Single course display
                          <>
                            <div><strong>Course:</strong> {courses[0].name}</div>
                            {courses[0].description && (
                              <div><strong>Description:</strong> {courses[0].description}</div>
                            )}
                            {courses[0].fee > 0 && (
                              <div><strong>Course Fee:</strong> ₹{courses[0].fee} × {courses[0].quantity} = ₹{courses[0].finalAmount}</div>
                            )}
                          </>
                        ) : (
                          // Multiple courses display
                          <>
                            <div><strong>Total Courses:</strong> {courses.length}</div>
                            <div style={{ marginTop: '12px' }}>
                              {courses.map((course, idx) => (
                                <div key={course.bookedCourseId || idx} style={{ 
                                  marginBottom: '12px', 
                                  paddingBottom: '12px',
                                  borderBottom: idx < courses.length - 1 ? '1px solid #E3DFD3' : 'none'
                                }}>
                                  <div style={{ fontWeight: '600' }}>{course.name}</div>
                                  {course.description && (
                                    <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '4px' }}>{course.description}</div>
                                  )}
                                  <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '4px' }}>
                                    ₹{course.fee} × {course.quantity} = ₹{course.finalAmount}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Extend Booking Section */}
          {activeSection === 'extend' && (
            <div>
              <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                Extend Booking
              </h3>
              
              <div style={{
                backgroundColor: '#F0F3EE',
                border: '1px solid #1A3E2A',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px' }}><CalendarIcon size="1em" aria-hidden="true" /></span>
                  <strong style={{ color: '#0F2619' }}>Extend Checkout Date - Multi-Entity Support</strong>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: '#0B1D13', lineHeight: '1.5' }}>
                  Extend the checkout date for this booking. You can extend all rooms/beds at once (Bulk) or select specific ones (Individual). 
                  Additional charges will be calculated based on the extended duration.
                </p>
              </div>

              <div style={{
                backgroundColor: '#FAF4E8',
                border: '1px solid #B68235',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '18px' }}><AlertTriangleIcon size="1em" aria-hidden="true" /></span>
                  <strong style={{ color: '#684A1F' }}>Important Notice</strong>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: '#4F3818', lineHeight: '1.5' }}>
                  Extension is subject to room/bed availability. If there are conflicting bookings for the selected dates, 
                  those entities will not be available for extension. You can proceed with available entities only.
                </p>
              </div>

              {/* Mode Selector */}
              <div style={{
                ...mobileButtonRowStyle,
                marginBottom: '24px',
                padding: '8px',
                backgroundColor: '#F5F3EB',
                borderRadius: '8px',
                border: '1px solid #E3DFD3'
              }}>
                <button
                  onClick={() => handleExtendModeChange('bulk')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '6px',
                    border: extendMode === 'bulk' ? '2px solid #1A3E2A' : '1px solid #D2CDBF',
                    backgroundColor: extendMode === 'bulk' ? '#E1E9E2' : 'white',
                    color: extendMode === 'bulk' ? '#0F2619' : '#36433B',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ...mobileActionButtonStyle
                  }}
                >
                  Extend All (Bulk)
                </button>
                <button
                  onClick={() => handleExtendModeChange('individual')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '6px',
                    border: extendMode === 'individual' ? '2px solid #1A3E2A' : '1px solid #D2CDBF',
                    backgroundColor: extendMode === 'individual' ? '#E1E9E2' : 'white',
                    color: extendMode === 'individual' ? '#0F2619' : '#36433B',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ...mobileActionButtonStyle
                  }}
                >
                  Select Individual
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '24px',
                marginBottom: '24px'
              }}>
                {/* Current Booking Dates */}
                <div style={{
                  backgroundColor: '#F5F3EB',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                    Current Booking Dates
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Check-in:</strong> 
                      <div style={{ color: '#163626', fontWeight: '600' }}>{formatDate(booking.checkIn)}</div>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Current Check-out:</strong>
                      <div style={{ color: '#8E3628', fontWeight: '600' }}>{formatDate(booking.checkOut)}</div>
                    </div>
                    <div>
                      <strong>Current Duration:</strong>
                      <div style={{ color: '#5F6B61' }}>{calculateNights()} nights</div>
                    </div>
                  </div>
                </div>

                {/* New Checkout Date Selector */}
                <div style={{
                  backgroundColor: '#F5F3EB',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                    New Checkout Date
                  </h4>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#36433B'
                    }}>
                      Select New Checkout Date:
                    </label>
                    
                    <input
                      type="date"
                      value={newCheckOutDate}
                      onChange={(e) => handleMultiEntityExtendDateChange(e.target.value)}
                      min={(() => {
                        // Find the earliest checkout date among all entities
                        const roomCheckouts = bookingDetailsResponse.data?.bookedRooms?.map(r => new Date(r.checkOut || booking.checkOut)) || [];
                        const bedCheckouts = bookingDetailsResponse.data?.bookedBeds?.map(b => new Date(b.checkOut || booking.checkOut)) || [];
                        const allCheckouts = [...roomCheckouts, ...bedCheckouts];
                        
                        if (allCheckouts.length === 0) {
                          return new Date(new Date(booking.checkOut).getTime() + 24*60*60*1000).toISOString().split('T')[0];
                        }
                        
                        // Get the earliest checkout date and add 1 day
                        const earliestCheckout = new Date(Math.min(...allCheckouts));
                        return new Date(earliestCheckout.getTime() + 24*60*60*1000).toISOString().split('T')[0];
                      })()}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px solid #D2CDBF',
                        fontSize: '14px'
                      }}
                    />
                    
                    {loadingAvailability && (
                      <div style={{ 
                        marginTop: '12px',
                        textAlign: 'center', 
                        padding: '12px',
                        color: '#5F6B61',
                        backgroundColor: '#F5F3EB',
                        borderRadius: '6px',
                        border: '1px solid #E3DFD3',
                        fontSize: '14px'
                      }}>
                        <div style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #E3DFD3', borderTop: '2px solid #1A3E2A', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: '8px' }}></div>
                        Checking availability...
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Entity Selection (Individual Mode) */}
              {extendMode === 'individual' && newCheckOutDate && !loadingAvailability && (
                <div style={{
                  backgroundColor: '#F0F3EE',
                  border: '1px solid #1A3E2A',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#0F2619' }}>
                    Select Rooms/Beds to Extend
                  </h4>
                  
                  {/* Booked Rooms Selection */}
                  {bookingDetailsResponse.data?.bookedRooms?.length > 0 && (
                    <div style={{ marginBottom: bookingDetailsResponse.data?.bookedBeds?.length > 0 ? '20px' : '0' }}>
                      <strong style={{ display: 'block', marginBottom: '12px', color: '#36433B', fontSize: '15px' }}>
                        Booked Rooms ({bookingDetailsResponse.data.bookedRooms.length}):
                      </strong>
                      {bookingDetailsResponse.data.bookedRooms.map((bookedRoom) => {
                        const availabilityInfo = entityAvailability.rooms[bookedRoom.id];
                        const alreadyExtended = availabilityInfo?.alreadyExtended === true;
                        const isAvailable = availabilityInfo?.available !== false && !alreadyExtended;
                        const isSelected = selectedRoomsToExtend.includes(bookedRoom.id);
                        const roomName = bookedRoom.room?.name || bookedRoom.roomName || `Room ${bookedRoom.room?.id || bookedRoom.roomId}`;
                        const unavailableDates = availabilityInfo?.unavailableDates || [];
                        const currentCheckout = bookedRoom.checkOut || booking.checkOut;
                        
                        return (
                          <div
                            key={bookedRoom.id}
                            onClick={() => isAvailable && !alreadyExtended && toggleRoomSelection(bookedRoom.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '14px',
                              marginBottom: '10px',
                              borderRadius: '6px',
                              border: '2px solid ' + (alreadyExtended ? '#E1E9E2' : (isAvailable ? (isSelected ? '#1A3E2A' : '#D2CDBF') : '#D48A7C')),
                              backgroundColor: alreadyExtended ? '#F0F3EE' : (isAvailable ? (isSelected ? '#F0F3EE' : 'white') : '#F9EEEB'),
                              cursor: (isAvailable && !alreadyExtended) ? 'pointer' : 'not-allowed',
                              opacity: alreadyExtended ? 0.7 : (isAvailable ? 1 : 0.7),
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!isAvailable || alreadyExtended}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (isAvailable && !alreadyExtended) toggleRoomSelection(bookedRoom.id);
                              }}
                              style={{ 
                                cursor: (isAvailable && !alreadyExtended) ? 'pointer' : 'not-allowed',
                                width: '18px',
                                height: '18px'
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                                {roomName}
                              </div>
                              <div style={{ fontSize: '13px', color: '#5F6B61' }}>
                                Current Checkout: {new Date(currentCheckout).toLocaleDateString()}
                              </div>
                              <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '2px' }}>
                                Current Cost: ₹{(bookedRoom.finalAmount || bookedRoom.amount || 0).toFixed(2)}
                                {extensionPreview?.entityCosts?.rooms?.[bookedRoom.id] && (
                                  <span style={{ color: '#163626', fontWeight: '600', marginLeft: '8px' }}>
                                    • Extension: +₹{extensionPreview.entityCosts.rooms[bookedRoom.id].toFixed(2)}
                                  </span>
                                )}
                              </div>
                              {alreadyExtended && (
                                <div style={{ fontSize: '12px', color: '#163626', marginTop: '4px', fontWeight: '500' }}>
                                  ✓ Already extends to or beyond selected date
                                </div>
                              )}
                              {unavailableDates.length > 0 && !alreadyExtended && (
                                <div style={{ fontSize: '12px', color: '#8E3628', marginTop: '4px' }}>
                                  Unavailable dates: {unavailableDates.slice(0, 3).map(d => new Date(d).toLocaleDateString()).join(', ')}
                                  {unavailableDates.length > 3 && ` +${unavailableDates.length - 3} more`}
                                </div>
                              )}
                            </div>
                            {alreadyExtended ? (
                              <span style={{ color: '#163626', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                ✓ Extended
                              </span>
                            ) : isAvailable ? (
                              <span style={{ color: '#1A3E2A', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                ✓ Available
                              </span>
                            ) : (
                              <span style={{ color: '#8E3628', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                ✗ Unavailable
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Booked Beds Selection */}
                  {bookingDetailsResponse.data?.bookedBeds?.length > 0 && (
                    <div>
                      <strong style={{ display: 'block', marginBottom: '12px', color: '#36433B', fontSize: '15px' }}>
                        Booked Beds ({bookingDetailsResponse.data.bookedBeds.length}):
                      </strong>
                      {bookingDetailsResponse.data.bookedBeds.map((bookedBed) => {
                        const availabilityInfo = entityAvailability.beds[bookedBed.id];
                        const alreadyExtended = availabilityInfo?.alreadyExtended === true;
                        const isAvailable = availabilityInfo?.available !== false && !alreadyExtended;
                        const isSelected = selectedBedsToExtend.includes(bookedBed.id);
                        const bedName = bookedBed.bed?.name || bookedBed.bedName || `Bed ${bookedBed.bed?.id || bookedBed.bedId}`;
                        const roomName = bookedBed.bed?.room?.name || bookedBed.roomName || 'Shared Room';
                        const unavailableDates = availabilityInfo?.unavailableDates || [];
                        const currentCheckout = bookedBed.checkOut || booking.checkOut;
                        
                        return (
                          <div
                            key={bookedBed.id}
                            onClick={() => isAvailable && !alreadyExtended && toggleBedSelection(bookedBed.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '14px',
                              marginBottom: '10px',
                              borderRadius: '6px',
                              border: '2px solid ' + (alreadyExtended ? '#E1E9E2' : (isAvailable ? (isSelected ? '#1A3E2A' : '#D2CDBF') : '#D48A7C')),
                              backgroundColor: alreadyExtended ? '#F0F3EE' : (isAvailable ? (isSelected ? '#F0F3EE' : 'white') : '#F9EEEB'),
                              cursor: (isAvailable && !alreadyExtended) ? 'pointer' : 'not-allowed',
                              opacity: alreadyExtended ? 0.7 : (isAvailable ? 1 : 0.7),
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!isAvailable || alreadyExtended}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (isAvailable && !alreadyExtended) toggleBedSelection(bookedBed.id);
                              }}
                              style={{ 
                                cursor: (isAvailable && !alreadyExtended) ? 'pointer' : 'not-allowed',
                                width: '18px',
                                height: '18px'
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                                {bedName}
                              </div>
                              <div style={{ fontSize: '13px', color: '#5F6B61' }}>
                                {roomName} • Checkout: {new Date(currentCheckout).toLocaleDateString()}
                              </div>
                              <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '2px' }}>
                                Current Cost: ₹{(bookedBed.finalAmount || bookedBed.amount || 0).toFixed(2)}
                                {extensionPreview?.entityCosts?.beds?.[bookedBed.id] && (
                                  <span style={{ color: '#163626', fontWeight: '600', marginLeft: '8px' }}>
                                    • Extension: +₹{extensionPreview.entityCosts.beds[bookedBed.id].toFixed(2)}
                                  </span>
                                )}
                              </div>
                              {alreadyExtended && (
                                <div style={{ fontSize: '12px', color: '#163626', marginTop: '4px', fontWeight: '500' }}>
                                  ✓ Already extends to or beyond selected date
                                </div>
                              )}
                              {unavailableDates.length > 0 && !alreadyExtended && (
                                <div style={{ fontSize: '12px', color: '#8E3628', marginTop: '4px' }}>
                                  Unavailable dates: {unavailableDates.slice(0, 3).map(d => new Date(d).toLocaleDateString()).join(', ')}
                                  {unavailableDates.length > 3 && ` +${unavailableDates.length - 3} more`}
                                </div>
                              )}
                            </div>
                            {alreadyExtended ? (
                              <span style={{ color: '#163626', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                ✓ Extended
                              </span>
                            ) : isAvailable ? (
                              <span style={{ color: '#1A3E2A', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                ✓ Available
                              </span>
                            ) : (
                              <span style={{ color: '#8E3628', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                ✗ Unavailable
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {bookingDetailsResponse.data?.bookedRooms?.length === 0 && bookingDetailsResponse.data?.bookedBeds?.length === 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '24px',
                      color: '#5F6B61',
                      fontSize: '14px'
                    }}>
                      No accommodations found in this booking.
                    </div>
                  )}
                </div>
              )}

              {/* Bulk Mode Summary */}
              {extendMode === 'bulk' && newCheckOutDate && !loadingAvailability && (
                <div style={{
                  backgroundColor: '#F3E6CB',
                  border: '1px solid #C9903C',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '18px' }}><Building2Icon size="1em" aria-hidden="true" /></span>
                    <strong style={{ color: '#684A1F' }}>Bulk Extension Mode</strong>
                  </div>
                  <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#4F3818', lineHeight: '1.5' }}>
                    All available rooms and beds will be extended to the new checkout date.
                  </p>
                  <div style={{ fontSize: '14px', color: '#684A1F', lineHeight: '1.6' }}>
                    <div>• Total Rooms: <strong>{bookingDetailsResponse.data?.bookedRooms?.length || 0}</strong></div>
                    <div>• Total Beds: <strong>{bookingDetailsResponse.data?.bookedBeds?.length || 0}</strong></div>
                    {Object.keys(entityAvailability.rooms).length > 0 && (
                      <div>• Available Rooms: <strong style={{ color: '#163626' }}>
                        {Object.values(entityAvailability.rooms).filter(r => r.available).length}
                      </strong></div>
                    )}
                    {Object.keys(entityAvailability.beds).length > 0 && (
                      <div>• Available Beds: <strong style={{ color: '#163626' }}>
                        {Object.values(entityAvailability.beds).filter(b => b.available).length}
                      </strong></div>
                    )}
                  </div>
                </div>
              )}

              {/* Extension Preview */}
              {extensionPreview && !calculatingExtendPrice && extensionPreview.totalCost === 0 && (
                <div style={{
                  backgroundColor: '#F3E6CB',
                  border: '2px solid #B68235',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}><InfoIcon size="1em" aria-hidden="true" /></div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '17px', fontWeight: '600', color: '#684A1F' }}>
                    No Extension Needed
                  </h4>
                  <p style={{ margin: 0, color: '#4F3818', fontSize: '14px' }}>
                    All selected entities already extend to or beyond {formatDate(extensionPreview.newCheckOutDate)}.
                    No additional charges required.
                  </p>
                </div>
              )}

              {extensionPreview && !calculatingExtendPrice && extensionPreview.totalCost > 0 && (
                <div style={{
                  backgroundColor: '#F0F3EE',
                  border: '2px solid #1A3E2A',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '17px', fontWeight: '600', color: '#0F2619' }}>
                    Extension Summary
                  </h4>
                  
                  <div style={{ fontSize: '14px', lineHeight: '2' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E1E9E2' }}>
                      <span style={{ color: '#36433B' }}>New Checkout Date:</span>
                      <strong style={{ color: '#0F2619' }}>{formatDate(extensionPreview.newCheckOutDate)}</strong>
                    </div>
                    
                    {extendMode === 'individual' && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E1E9E2' }}>
                          <span style={{ color: '#36433B' }}>Rooms to Extend:</span>
                          <strong style={{ color: '#0F2619' }}>{selectedRoomsToExtend.length}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E1E9E2' }}>
                          <span style={{ color: '#36433B' }}>Beds to Extend:</span>
                          <strong style={{ color: '#0F2619' }}>{selectedBedsToExtend.length}</strong>
                        </div>
                      </>
                    )}
                    
                    {extendMode === 'bulk' && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E1E9E2' }}>
                        <span style={{ color: '#36433B' }}>Extending:</span>
                        <strong style={{ color: '#0F2619' }}>
                          {Object.values(entityAvailability.rooms).filter(r => r.available).length} Rooms + {' '}
                          {Object.values(entityAvailability.beds).filter(b => b.available).length} Beds
                        </strong>
                      </div>
                    )}
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '2px solid #1A3E2A'
                    }}>
                      <span style={{ fontSize: '17px', fontWeight: '600', color: '#36433B' }}>
                        Additional Cost:
                      </span>
                      <strong style={{ fontSize: '20px', color: '#163626' }}>
                        ₹{extensionPreview.totalCost.toFixed(2)}
                      </strong>
                    </div>
                  </div>
                </div>
              )}

              {/* Calculating Price Loading */}
              {calculatingExtendPrice && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '24px',
                  marginBottom: '24px',
                  color: '#5F6B61',
                  backgroundColor: '#F5F3EB',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <div style={{ display: 'inline-block', width: 20, height: 20, border: '2px solid #E3DFD3', borderTop: '2px solid #1A3E2A', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '12px' }}></div>
                  <div style={{ fontSize: '14px' }}>Calculating extension price...</div>
                </div>
              )}

              {/* Error Message */}
              {extendPriceError && (
                <div style={{
                  backgroundColor: '#F9EEEB',
                  border: '1px solid #D48A7C',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ fontSize: '20px', marginTop: '2px' }}><BanIcon size="1em" aria-hidden="true" /></span>
                    <div>
                      <div style={{ color: '#8E3628', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                        Extension Not Available
                      </div>
                      <div style={{ color: '#481B14', fontSize: '14px', lineHeight: '1.5' }}>
                        {extendPriceError}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                ...mobileButtonRowStyle,
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: '2px solid #E3DFD3'
              }}>
                <button
                  onClick={executeExtendBooking}
                  disabled={
                    saving || 
                    !newCheckOutDate || 
                    calculatingExtendPrice || 
                    loadingAvailability ||
                    (extendMode === 'individual' && selectedRoomsToExtend.length === 0 && selectedBedsToExtend.length === 0) ||
                    !extensionPreview
                  }
                  style={{
                    flex: 1,
                    backgroundColor: (
                      saving || 
                      !newCheckOutDate || 
                      calculatingExtendPrice || 
                      (extendMode === 'individual' && selectedRoomsToExtend.length === 0 && selectedBedsToExtend.length === 0) ||
                      !extensionPreview
                    ) ? '#A5A89B' : '#1A3E2A',
                    color: 'white',
                    border: 'none',
                    padding: '14px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: (
                      saving || 
                      !newCheckOutDate || 
                      calculatingExtendPrice || 
                      (extendMode === 'individual' && selectedRoomsToExtend.length === 0 && selectedBedsToExtend.length === 0) ||
                      !extensionPreview
                    ) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    ...mobileActionButtonStyle
                  }}
                >
                  {saving ? (
                    <>
                      <span style={{
                        display: 'inline-block',
                        width: 16,
                        height: 16,
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      Extend Booking
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => {
                    setNewCheckOutDate('');
                    setExtendPrice(0);
                    setExtensionPreview(null);
                    setEntityAvailability({ rooms: {}, beds: {} });
                    setSelectedRoomsToExtend([]);
                    setSelectedBedsToExtend([]);
                    setExtendPriceError('');
                  }}
                  disabled={saving}
                  style={{
                    backgroundColor: '#5F6B61',
                    color: 'white',
                    border: 'none',
                    padding: '14px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    ...mobileActionButtonStyle
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          )}

          {/* Early Check-in Section */}
          {activeSection === 'early-checkin' && (
            <div>
              <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                Early Check-in
              </h3>
              
              <div style={{
                backgroundColor: '#E1E9E2',
                border: '1px solid #163626',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px' }}><AlarmClockIcon size="1em" aria-hidden="true" /></span>
                  <strong style={{ color: '#0B1D13' }}>Early Check-in Available</strong>
                </div>
                <p style={{ color: '#0B1D13', margin: '0', fontSize: '14px' }}>
                  Move check-in date earlier. Choose to apply early check-in to all entities (bulk) or select specific rooms/beds (individual).
                </p>
              </div>

              {/* Mode Selector */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ ...mobileButtonRowStyle, marginBottom: '12px' }}>
                  <button
                    onClick={() => handleEarlyCheckinModeChange('bulk')}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: earlyCheckinMode === 'bulk' ? '2px solid #1A3E2A' : '2px solid #D2CDBF',
                      borderRadius: '8px',
                      backgroundColor: earlyCheckinMode === 'bulk' ? '#E1E9E2' : 'white',
                      color: earlyCheckinMode === 'bulk' ? '#0F2619' : '#5F6B61',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}><PackageIcon size="1em" aria-hidden="true" /></span>
                      <span>Bulk Mode</span>
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 'normal', marginTop: '4px' }}>
                      Apply to all rooms & beds
                    </div>
                  </button>
                  <button
                    onClick={() => handleEarlyCheckinModeChange('individual')}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: earlyCheckinMode === 'individual' ? '2px solid #1A3E2A' : '2px solid #D2CDBF',
                      borderRadius: '8px',
                      backgroundColor: earlyCheckinMode === 'individual' ? '#E1E9E2' : 'white',
                      color: earlyCheckinMode === 'individual' ? '#0F2619' : '#5F6B61',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}><TargetIcon size="1em" aria-hidden="true" /></span>
                      <span>Individual Mode</span>
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 'normal', marginTop: '4px' }}>
                      Select specific entities
                    </div>
                  </button>
                </div>
                <p style={{ margin: '0', fontSize: '13px', color: '#5F6B61', textAlign: 'center' }}>
                  {earlyCheckinMode === 'bulk' 
                    ? 'All rooms and beds will check in on the same earlier date' 
                    : 'Choose specific rooms or beds to check in earlier'}
                </p>
              </div>

              {/* Current Booking Dates Card */}
              <div style={{ 
                backgroundColor: '#F5F3EB', 
                padding: '16px', 
                borderRadius: '8px', 
                marginBottom: '24px',
                border: '1px solid #E3DFD3'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                  Current Booking Dates
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '13px', color: '#5F6B61', marginBottom: '4px' }}>Check-in</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#24332A' }}>
                      {format(new Date(booking.checkIn), 'MMM dd, yyyy')}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', color: '#5F6B61', marginBottom: '4px' }}>Check-out</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#24332A' }}>
                      {format(new Date(booking.checkOut), 'MMM dd, yyyy')}
                    </div>
                  </div>
                </div>
              </div>

              {/* New Check-in Date Picker */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#36433B' 
                }}>
                  Select New Check-in Date *
                </label>
                <input
                  type="date"
                  value={newCheckInDate}
                  max={(() => {
                    const earliestCheckIn = bookingDetailsResponse?.data?.bookedRooms?.length > 0 || bookingDetailsResponse?.data?.bookedBeds?.length > 0
                      ? Math.min(
                          ...[
                            ...(bookingDetailsResponse.data.bookedRooms || []).map(r => new Date(r.checkIn).getTime()),
                            ...(bookingDetailsResponse.data.bookedBeds || []).map(b => new Date(b.checkIn).getTime())
                          ].filter(t => !isNaN(t))
                        )
                      : new Date(booking.checkIn).getTime();
                    
                    const maxDate = new Date(earliestCheckIn - 24 * 60 * 60 * 1000);
                    return maxDate.toISOString().split('T')[0];
                  })()}
                  onChange={(e) => handleMultiEntityEarlyCheckinDateChange(e.target.value)}
                  disabled={loadingEarlyCheckinAvailability}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#5F6B61' }}>
                  Choose a date earlier than the current check-in to prepone the booking
                </p>
              </div>

              {/* Loading State */}
              {loadingEarlyCheckinAvailability && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
                  <div style={{ display: 'inline-block', width: 20, height: 20, border: '2px solid #E3DFD3', borderTop: '2px solid #1A3E2A', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  <div style={{ marginTop: '12px' }}>Checking availability and calculating prices...</div>
                </div>
              )}

              {/* Error State */}
              {earlyCheckinError && (
                <div style={{
                  backgroundColor: '#F9EEEB',
                  border: '1px solid #A13F2F',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#8E3628' }}>
                    <span style={{ fontSize: '18px' }}><AlertTriangleIcon size="1em" aria-hidden="true" /></span>
                    <strong>{earlyCheckinError}</strong>
                  </div>
                </div>
              )}

              {/* Rooms & Beds List with Availability */}
              {newCheckInDate && !loadingEarlyCheckinAvailability && earlyCheckinEntityAvailability && (
                <div style={{ marginBottom: '24px' }}>
                  {/* Rooms Section */}
                  {bookingDetailsResponse?.data?.bookedRooms?.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        Rooms ({bookingDetailsResponse.data.bookedRooms.length})
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {bookingDetailsResponse.data.bookedRooms.map((bookedRoom) => {
                          const roomAvailability = earlyCheckinEntityAvailability.rooms?.[bookedRoom.id];
                          const isAvailable = roomAvailability?.available || false;
                          const alreadyEarlier = roomAvailability?.alreadyEarlier || false;
                          const unavailableDates = roomAvailability?.unavailableDates || [];
                          const isSelected = selectedRoomsForEarlyCheckin.includes(bookedRoom.id);
                          const roomName = bookedRoom.room?.name || bookedRoom.roomName || `Room ${bookedRoom.room?.id || bookedRoom.roomId}`;
                          const currentCheckIn = bookedRoom.checkIn;

                          return (
                            <div
                              key={bookedRoom.id}
                              onClick={() => {
                                if (earlyCheckinMode === 'individual' && (isAvailable || alreadyEarlier) && !alreadyEarlier) {
                                  toggleRoomEarlyCheckinSelection(bookedRoom.id);
                                }
                              }}
                              style={{
                                padding: '16px',
                                borderRadius: '8px',
                                border: `2px solid ${
                                  alreadyEarlier ? '#163626' :
                                  isSelected ? '#1A3E2A' : 
                                  isAvailable ? '#1A3E2A' : '#A13F2F'
                                }`,
                                backgroundColor: alreadyEarlier ? '#F0F3EE' :
                                  isSelected ? '#E1E9E2' : 
                                  isAvailable ? '#F0F3EE' : '#F9EEEB',
                                cursor: earlyCheckinMode === 'individual' && (isAvailable || alreadyEarlier) && !alreadyEarlier ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                opacity: (!isAvailable && !alreadyEarlier) ? 0.6 : 1
                              }}
                            >
                              {earlyCheckinMode === 'individual' && !alreadyEarlier && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (isAvailable) toggleRoomEarlyCheckinSelection(bookedRoom.id);
                                  }}
                                  disabled={!isAvailable}
                                  style={{ 
                                    cursor: isAvailable ? 'pointer' : 'not-allowed',
                                    width: '18px',
                                    height: '18px'
                                  }}
                                />
                              )}
                              {alreadyEarlier && (
                                <span style={{ fontSize: '20px', marginTop: '-2px' }}>✓</span>
                              )}
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                                  {roomName}
                                </div>
                                <div style={{ fontSize: '13px', color: '#5F6B61' }}>
                                  Current Check-in: {new Date(currentCheckIn).toLocaleDateString()}
                                </div>
                                <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '2px' }}>
                                  Current Cost: ₹{(bookedRoom.finalAmount || bookedRoom.amount || 0).toFixed(2)}
                                  {earlyCheckinPreview?.entityCosts?.rooms?.[bookedRoom.id] && (
                                    <span style={{ color: '#1A3E2A', fontWeight: '600', marginLeft: '8px' }}>
                                      • Early Check-in: +₹{earlyCheckinPreview.entityCosts.rooms[bookedRoom.id].toFixed(2)}
                                    </span>
                                  )}
                                </div>
                                {alreadyEarlier && (
                                  <div style={{ fontSize: '12px', color: '#163626', marginTop: '4px', fontWeight: '500' }}>
                                    ✓ Already checking in on or before selected date
                                  </div>
                                )}
                                {unavailableDates.length > 0 && !alreadyEarlier && (
                                  <div style={{ fontSize: '12px', color: '#8E3628', marginTop: '4px' }}>
                                    Unavailable dates: {unavailableDates.slice(0, 3).map(d => new Date(d).toLocaleDateString()).join(', ')}
                                    {unavailableDates.length > 3 && ` +${unavailableDates.length - 3} more`}
                                  </div>
                                )}
                              </div>
                              {alreadyEarlier ? (
                                <span style={{ color: '#163626', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                  ✓ Earlier
                                </span>
                              ) : isAvailable ? (
                                <span style={{ color: '#1A3E2A', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                  ✓ Available
                                </span>
                              ) : (
                                <span style={{ color: '#A13F2F', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                  ✗ Unavailable
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Beds Section */}
                  {bookingDetailsResponse?.data?.bookedBeds?.length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        Beds ({bookingDetailsResponse.data.bookedBeds.length})
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {bookingDetailsResponse.data.bookedBeds.map((bookedBed) => {
                          const bedAvailability = earlyCheckinEntityAvailability.beds?.[bookedBed.id];
                          const isAvailable = bedAvailability?.available || false;
                          const alreadyEarlier = bedAvailability?.alreadyEarlier || false;
                          const unavailableDates = bedAvailability?.unavailableDates || [];
                          const isSelected = selectedBedsForEarlyCheckin.includes(bookedBed.id);
                          const bedName = bookedBed.bed?.name || bookedBed.bedName || `Bed ${bookedBed.bed?.id || bookedBed.bedId}`;
                          const currentCheckIn = bookedBed.checkIn;

                          return (
                            <div
                              key={bookedBed.id}
                              onClick={() => {
                                if (earlyCheckinMode === 'individual' && (isAvailable || alreadyEarlier) && !alreadyEarlier) {
                                  toggleBedEarlyCheckinSelection(bookedBed.id);
                                }
                              }}
                              style={{
                                padding: '16px',
                                borderRadius: '8px',
                                border: `2px solid ${
                                  alreadyEarlier ? '#163626' :
                                  isSelected ? '#1A3E2A' : 
                                  isAvailable ? '#1A3E2A' : '#A13F2F'
                                }`,
                                backgroundColor: alreadyEarlier ? '#F0F3EE' :
                                  isSelected ? '#E1E9E2' : 
                                  isAvailable ? '#F0F3EE' : '#F9EEEB',
                                cursor: earlyCheckinMode === 'individual' && (isAvailable || alreadyEarlier) && !alreadyEarlier ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                opacity: (!isAvailable && !alreadyEarlier) ? 0.6 : 1
                              }}
                            >
                              {earlyCheckinMode === 'individual' && !alreadyEarlier && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (isAvailable) toggleBedEarlyCheckinSelection(bookedBed.id);
                                  }}
                                  disabled={!isAvailable}
                                  style={{ 
                                    cursor: isAvailable ? 'pointer' : 'not-allowed',
                                    width: '18px',
                                    height: '18px'
                                  }}
                                />
                              )}
                              {alreadyEarlier && (
                                <span style={{ fontSize: '20px', marginTop: '-2px' }}>✓</span>
                              )}
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                                  {bedName}
                                </div>
                                <div style={{ fontSize: '13px', color: '#5F6B61' }}>
                                  Current Check-in: {new Date(currentCheckIn).toLocaleDateString()}
                                </div>
                                <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '2px' }}>
                                  Current Cost: ₹{(bookedBed.finalAmount || bookedBed.amount || 0).toFixed(2)}
                                  {earlyCheckinPreview?.entityCosts?.beds?.[bookedBed.id] && (
                                    <span style={{ color: '#1A3E2A', fontWeight: '600', marginLeft: '8px' }}>
                                      • Early Check-in: +₹{earlyCheckinPreview.entityCosts.beds[bookedBed.id].toFixed(2)}
                                    </span>
                                  )}
                                </div>
                                {alreadyEarlier && (
                                  <div style={{ fontSize: '12px', color: '#163626', marginTop: '4px', fontWeight: '500' }}>
                                    ✓ Already checking in on or before selected date
                                  </div>
                                )}
                                {unavailableDates.length > 0 && !alreadyEarlier && (
                                  <div style={{ fontSize: '12px', color: '#8E3628', marginTop: '4px' }}>
                                    Unavailable dates: {unavailableDates.slice(0, 3).map(d => new Date(d).toLocaleDateString()).join(', ')}
                                    {unavailableDates.length > 3 && ` +${unavailableDates.length - 3} more`}
                                  </div>
                                )}
                              </div>
                              {alreadyEarlier ? (
                                <span style={{ color: '#163626', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                  ✓ Earlier
                                </span>
                              ) : isAvailable ? (
                                <span style={{ color: '#1A3E2A', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                  ✓ Available
                                </span>
                              ) : (
                                <span style={{ color: '#A13F2F', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                  ✗ Unavailable
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cost Preview */}
              {earlyCheckinPreview && earlyCheckinPrice !== null && (
                <div style={{ 
                  backgroundColor: '#F0F3EE', 
                  border: '1px solid #1A3E2A', 
                  borderRadius: '8px', 
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#0B1D13' }}>
                    Cost Preview
                  </h4>
                  
                  {earlyCheckinPrice === 0 ? (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#F0F3EE', 
                      border: '1px solid #1A3E2A',
                      borderRadius: '6px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0F2619' }}>
                        <span style={{ fontSize: '18px' }}><InfoIcon size="1em" aria-hidden="true" /></span>
                        <span style={{ fontWeight: '600' }}>
                          {earlyCheckinMode === 'bulk'
                            ? 'All entities are already checking in on or before the selected date'
                            : 'Selected entities are already checking in on or before the selected date'}
                        </span>
                      </div>
                      <p style={{ margin: '8px 0 0 26px', fontSize: '13px', color: '#122E20' }}>
                        No additional cost will be applied for this early check-in operation.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Entity-wise Cost Breakdown */}
                      <div style={{ marginBottom: '16px' }}>
                        {earlyCheckinPreview.entityCosts?.rooms && Object.keys(earlyCheckinPreview.entityCosts.rooms).length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
                              Room Costs:
                            </div>
                            {Object.entries(earlyCheckinPreview.entityCosts.rooms).map(([roomId, cost]) => {
                              const room = bookingDetailsResponse?.data?.bookedRooms?.find(r => r.id === roomId);
                              const roomName = room?.room?.name || room?.roomName || `Room ${room?.room?.id || room?.roomId}`;
                              return (
                                <div key={roomId} style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  padding: '8px 12px',
                                  backgroundColor: 'white',
                                  borderRadius: '4px',
                                  marginBottom: '4px',
                                  fontSize: '14px'
                                }}>
                                  <span style={{ color: '#5F6B61' }}>{roomName}</span>
                                  <span style={{ color: '#1A3E2A', fontWeight: '600' }}>+₹{cost.toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {earlyCheckinPreview.entityCosts?.beds && Object.keys(earlyCheckinPreview.entityCosts.beds).length > 0 && (
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
                              Bed Costs:
                            </div>
                            {Object.entries(earlyCheckinPreview.entityCosts.beds).map(([bedId, cost]) => {
                              const bed = bookingDetailsResponse?.data?.bookedBeds?.find(b => b.id === bedId);
                              const bedName = bed?.bed?.name || bed?.bedName || `Bed ${bed?.bed?.id || bed?.bedId}`;
                              return (
                                <div key={bedId} style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  padding: '8px 12px',
                                  backgroundColor: 'white',
                                  borderRadius: '4px',
                                  marginBottom: '4px',
                                  fontSize: '14px'
                                }}>
                                  <span style={{ color: '#5F6B61' }}>{bedName}</span>
                                  <span style={{ color: '#1A3E2A', fontWeight: '600' }}>+₹{cost.toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Total Additional Cost */}
                      <div style={{ 
                        paddingTop: '12px', 
                        borderTop: '2px solid #1A3E2A',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#0B1D13' }}>
                          Total Additional Cost:
                        </span>
                        <span style={{ fontSize: '18px', fontWeight: '700', color: '#0B1D13' }}>
                          +₹{earlyCheckinPrice.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              {newCheckInDate && !loadingEarlyCheckinAvailability && earlyCheckinEntityAvailability && (
                <div style={{ ...mobileButtonRowStyle, justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                  <button
                    onClick={() => {
                      setNewCheckInDate('');
                      setEarlyCheckinEntityAvailability(null);
                      setEarlyCheckinPreview(null);
                      setEarlyCheckinPrice(null);
                      setSelectedRoomsForEarlyCheckin([]);
                      setSelectedBedsForEarlyCheckin([]);
                      setEarlyCheckinError('');
                    }}
                    style={{
                      padding: '10px 20px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      color: '#36433B',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      ...mobileActionButtonStyle
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeEarlyCheckin}
                    disabled={
                      processingEarlyCheckin || 
                      (earlyCheckinMode === 'individual' && selectedRoomsForEarlyCheckin.length === 0 && selectedBedsForEarlyCheckin.length === 0) ||
                      (earlyCheckinMode === 'bulk' && earlyCheckinPrice === 0)
                    }
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: (
                        processingEarlyCheckin || 
                        (earlyCheckinMode === 'individual' && selectedRoomsForEarlyCheckin.length === 0 && selectedBedsForEarlyCheckin.length === 0) ||
                        (earlyCheckinMode === 'bulk' && earlyCheckinPrice === 0)
                      ) ? '#A5A89B' : '#1A3E2A',
                      color: 'white',
                      cursor: (
                        processingEarlyCheckin || 
                        (earlyCheckinMode === 'individual' && selectedRoomsForEarlyCheckin.length === 0 && selectedBedsForEarlyCheckin.length === 0) ||
                        (earlyCheckinMode === 'bulk' && earlyCheckinPrice === 0)
                      ) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      ...mobileActionButtonStyle
                    }}
                  >
                    {processingEarlyCheckin ? (
                      <>
                        <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                        Processing...
                      </>
                    ) : (
                      <>Process Early Check-in</>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Early Checkout Section */}
          {activeSection === 'early-checkout' && (
            <div>
              <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                Early Checkout
              </h3>
              
              <div style={{
                backgroundColor: '#F3E6CB',
                border: '1px solid #B68235',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px' }}><DoorOpenIcon size="1em" aria-hidden="true" /></span>
                  <strong style={{ color: '#684A1F' }}>Process Early Checkout</strong>
                </div>
                <p style={{ color: '#684A1F', margin: '0', fontSize: '14px' }}>
                  Process checkout before the scheduled date. This will update the booking status and may affect billing.
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#F5F3EB',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#36433B',
                      marginBottom: '4px' 
                    }}>
                      Current Checkout Date
                    </label>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#24332A',
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px'
                    }}>
                      {format(new Date(booking.checkOut), 'MMMM dd, yyyy')}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#36433B',
                      marginBottom: '4px' 
                    }}>
                      Current Status
                    </label>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#24332A',
                      padding: '8px 12px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px'
                    }}>
                      {booking.status}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#36433B',
                    marginBottom: '8px' 
                  }}>
                    Select Early Checkout Date *
                  </label>
                  <input
                    type="date"
                    value={earlyCheckOutDate}
                    onChange={(e) => setEarlyCheckOutDate(e.target.value)}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    max={format(new Date(booking.checkOut), 'yyyy-MM-dd')}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '8px',
                      fontSize: '16px',
                      backgroundColor: 'white'
                    }}
                  />
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#5F6B61', 
                    margin: '4px 0 0 0',
                    fontStyle: 'italic' 
                  }}>
                    Must be after today and before the original checkout date
                  </p>
                </div>

                {earlyCheckOutDate && (
                  <div style={{
                    backgroundColor: '#F3E6CB',
                    border: '1px solid #B68235',
                    borderRadius: '6px',
                    padding: '16px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ color: '#684A1F', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Early Checkout Summary
                    </div>
                    <div style={{ color: '#684A1F', fontSize: '14px', marginBottom: '8px' }}>
                      <strong>New Checkout Date:</strong> {format(new Date(earlyCheckOutDate), 'MMMM dd, yyyy')} at 10:00 AM
                    </div>
                    <div style={{ color: '#684A1F', fontSize: '14px' }}>
                      <strong>Note:</strong> This will immediately process the checkout and update the booking status. 
                      Any billing adjustments may need to be handled separately.
                    </div>
                  </div>
                )}

                <div style={mobileButtonRowStyle}>
                  <button
                    onClick={handleEarlyCheckOut}
                    disabled={!earlyCheckOutDate || processingEarlyCheckOut}
                    style={{
                      backgroundColor: !earlyCheckOutDate || processingEarlyCheckOut ? '#A5A89B' : '#B68235',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: !earlyCheckOutDate || processingEarlyCheckOut ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      ...mobileActionButtonStyle
                    }}
                  >
                    {processingEarlyCheckOut ? (
                      <>
                        <span style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #ffffff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          display: 'inline-block'
                        }}></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        Process Early Checkout
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setEarlyCheckOutDate('');
                      setActiveSection('overview');
                    }}
                    disabled={processingEarlyCheckOut}
                    style={{
                      backgroundColor: '#5F6B61',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: processingEarlyCheckOut ? 'not-allowed' : 'pointer',
                      ...mobileActionButtonStyle
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Room Exchange Section */}
          {activeSection === 'exchange' && (
            <div>
              <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                Room Exchange
              </h3>
              
              <div style={{
                backgroundColor: '#FAF4E8',
                border: '1px solid #C9903C',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px' }}><RefreshCwIcon size="1em" aria-hidden="true" /></span>
                  <strong style={{ color: '#9E712E' }}>Room/Bed Exchange</strong>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: '#825D26', lineHeight: '1.5' }}>
                  You can exchange the current room or bed assignment for this booking. Price differences will be calculated automatically.
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '24px'
              }}>
                {/* Current Assignment */}
                <div style={{
                  backgroundColor: '#F5F3EB',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                    Current Assignment
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Room:</strong> 
                      <div style={{ color: '#163626', fontWeight: '600', fontSize: '16px' }}>
                        {booking.room?.roomNumber || 'No room assigned'}
                      </div>
                    </div>
                    {booking.bed && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Bed:</strong>
                        <div style={{ color: '#975534', fontWeight: '600', fontSize: '16px' }}>
                          Bed {booking.bed.bedNumber}
                        </div>
                      </div>
                    )}
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Type:</strong>
                      <div style={{ color: '#5F6B61' }}>
                        {booking.room?.roomType?.name || 'Unknown type'}
                      </div>
                    </div>
                    <div>
                      <strong>Daily Rate:</strong>
                      <div style={{ color: '#163626', fontWeight: '600' }}>
                        ₹{booking.room?.roomType?.pricePerNight || booking.bed?.price || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Exchange Options */}
                <div style={{
                  backgroundColor: '#F5F3EB',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                    Exchange Options
                  </h4>
                  
                  {/* Exchange Type Selection */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
                      Select Exchange Type:
                    </label>
                    <div style={mobileButtonRowStyle}>
                      <button
                        onClick={() => {
                          setSelectedNewRoom(null);
                          setSelectedNewBed(null);
                          setRoomExchangeDifference(0);
                          // Set exchange type to private
                          setExchangeType('private');
                        }}
                        style={{
                          backgroundColor: exchangeType === 'private' ? '#1A3E2A' : '#EEEBE1',
                          color: exchangeType === 'private' ? 'white' : '#36433B',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          ...mobileActionButtonStyle
                        }}
                      >
                        Private Room
                      </button>
                      <button
                        onClick={() => {
                          setSelectedNewRoom(null);
                          setSelectedNewBed(null);
                          setRoomExchangeDifference(0);
                          // Set exchange type to shared
                          setExchangeType('shared');
                        }}
                        style={{
                          backgroundColor: exchangeType === 'shared' ? '#1A3E2A' : '#EEEBE1',
                          color: exchangeType === 'shared' ? 'white' : '#36433B',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          ...mobileActionButtonStyle
                        }}
                      >
                        Shared Room/Bed
                      </button>
                    </div>
                  </div>

                  {loadingRooms || loadingBeds ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#5F6B61' }}>
                      <div style={{ fontSize: '20px', marginBottom: '8px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
                      Loading available options...
                    </div>
                  ) : (
                    <div>
                      {/* Private Room Exchange */}
                      {exchangeType === 'private' && (
                        <div>
                          <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                            Select Private Room:
                          </h5>
                          {availableRooms.filter(room => room.roomType?.isShared === false || !room.roomType?.isShared).length > 0 ? (
                            <div style={{
                              maxHeight: '300px',
                              overflowY: 'auto',
                              border: '1px solid #D2CDBF',
                              borderRadius: '6px',
                              backgroundColor: 'white'
                            }}>
                              {availableRooms.filter(room => room.roomType?.isShared === false || !room.roomType?.isShared).map(room => {
                                const isSelected = selectedNewRoom?.id === room.id;
                                const priceDiff = room.roomType?.pricePerNight - (booking.room?.roomType?.pricePerNight || 0);
                                
                                return (
                                  <div
                                    key={room.id}
                                    onClick={() => handleRoomSelect(room)}
                                    style={{
                                      padding: '12px',
                                      borderBottom: '1px solid #EEEBE1',
                                      cursor: 'pointer',
                                      backgroundColor: isSelected ? '#F0F3EE' : 'white',
                                      borderLeft: isSelected ? '3px solid #1A3E2A' : '3px solid transparent',
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div>
                                        <div style={{ fontWeight: '600', color: '#24332A' }}>
                                          Room {room.roomNumber}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#5F6B61' }}>
                                          {room.roomType?.name} • Private Room
                                        </div>
                                      </div>
                                      
                                      <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                                          ₹{room.roomType?.pricePerNight}/night
                                        </div>
                                        {priceDiff !== 0 && (
                                          <div style={{ 
                                            fontSize: '12px', 
                                            color: priceDiff > 0 ? '#8E3628' : '#163626',
                                            fontWeight: '600'
                                          }}>
                                            {priceDiff > 0 ? '+' : ''}₹{priceDiff}/night
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {isSelected && (
                                      <div style={{ 
                                        marginTop: '8px', 
                                        fontSize: '12px', 
                                        color: '#163626',
                                        fontWeight: '600' 
                                      }}>
                                        ✓ Selected for exchange
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#5F6B61' }}>
                              <div style={{ fontSize: '24px', marginBottom: '8px' }}><HomeIcon size="1em" aria-hidden="true" /></div>
                              <p>No private rooms available for exchange</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Shared Room/Bed Exchange */}
                      {exchangeType === 'shared' && (
                        <div>
                          {/* Step 1: Select Room */}
                          <div style={{ marginBottom: '20px' }}>
                            <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                              Step 1: Select Shared Room:
                            </h5>
                            {availableRooms.filter(room => room.roomType?.isShared === true).length > 0 ? (
                              <div style={{
                                maxHeight: '200px',
                                overflowY: 'auto',
                                border: '1px solid #D2CDBF',
                                borderRadius: '6px',
                                backgroundColor: 'white'
                              }}>
                                {availableRooms.filter(room => room.roomType?.isShared === true).map(room => {
                                  const isSelected = selectedNewRoom?.id === room.id;
                                  
                                  return (
                                    <div
                                      key={room.id}
                                      onClick={() => {
                                        console.log('Selected room:', room);
                                        console.log('All available beds:', availableBeds);
                                        console.log('Beds for this room:', availableBeds.filter(bed => {
                                          const bedRoomId = bed.room?.id || bed.roomId;
                                          return bedRoomId === room.id;
                                        }));
                                        setSelectedNewRoom(room);
                                        setSelectedNewBed(null); // Reset bed selection
                                        // Load beds for this room
                                        loadBedsForRoom(room.id);
                                      }}
                                      style={{
                                        padding: '12px',
                                        borderBottom: '1px solid #EEEBE1',
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#F0F3EE' : 'white',
                                        borderLeft: isSelected ? '3px solid #1A3E2A' : '3px solid transparent',
                                        transition: 'all 0.2s ease'
                                      }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                          <div style={{ fontWeight: '600', color: '#24332A' }}>
                                            Room {room.roomNumber}
                                          </div>
                                          <div style={{ fontSize: '12px', color: '#5F6B61' }}>
                                            {room.roomType?.name} • Shared Room
                                          </div>
                                        </div>
                                        
                                        <div style={{ textAlign: 'right' }}>
                                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                                            ₹{room.roomType?.pricePerNight}/night
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {isSelected && (
                                        <div style={{ 
                                          marginTop: '8px', 
                                          fontSize: '12px', 
                                          color: '#163626',
                                          fontWeight: '600' 
                                        }}>
                                          ✓ Room selected - Now select a bed below
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ textAlign: 'center', padding: '20px', color: '#5F6B61' }}>
                                <div style={{ fontSize: '24px', marginBottom: '8px' }}><HomeIcon size="1em" aria-hidden="true" /></div>
                                <p>No shared rooms available for exchange</p>
                              </div>
                            )}
                          </div>

                          {/* Step 2: Select Bed (only if room is selected) */}
                          {selectedNewRoom && exchangeType === 'shared' && (
                            <div>
                              <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                                Step 2: Select Bed in Room {selectedNewRoom.roomNumber}:
                              </h5>
                              {(() => {
                                const roomBeds = availableBeds.filter(bed => {
                                  // Check both bed.room.id and bed.roomId for compatibility
                                  const bedRoomId = bed.room?.id || bed.roomId;
                                  return bedRoomId === selectedNewRoom.id;
                                });
                                console.log(`Filtering beds for room ${selectedNewRoom.id} (${selectedNewRoom.roomNumber}):`, roomBeds);
                                console.log('Selected room object:', selectedNewRoom);
                                console.log('All beds with room info:', availableBeds.map(bed => ({ 
                                  bedId: bed.id, 
                                  bedNumber: bed.bedNumber, 
                                  roomId: bed.room?.id || bed.roomId, 
                                  roomNumber: bed.room?.roomNumber,
                                  roomObject: bed.room
                                })));
                                return roomBeds.length > 0;
                              })() ? (
                                <div style={{
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  border: '1px solid #D2CDBF',
                                  borderRadius: '6px',
                                  backgroundColor: 'white'
                                }}>
                                  {availableBeds.filter(bed => {
                                    const bedRoomId = bed.room?.id || bed.roomId;
                                    return bedRoomId === selectedNewRoom.id;
                                  }).map(bed => {
                                    const isSelected = selectedNewBed?.id === bed.id;
                                    const priceDiff = bed.price - (booking.bed?.price || 0);
                                    
                                    return (
                                      <div
                                        key={bed.id}
                                        onClick={() => handleBedSelect(bed)}
                                        style={{
                                          padding: '12px',
                                          borderBottom: '1px solid #EEEBE1',
                                          cursor: 'pointer',
                                          backgroundColor: isSelected ? '#F8F0EA' : 'white',
                                          borderLeft: isSelected ? '3px solid #975534' : '3px solid transparent',
                                          transition: 'all 0.2s ease'
                                        }}
                                      >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div>
                                            <div style={{ fontWeight: '600', color: '#24332A' }}>
                                              Bed {bed.bedNumber}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#5F6B61' }}>
                                              Available bed in this room
                                            </div>
                                          </div>
                                          
                                          <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                                              ₹{bed.price}/night
                                            </div>
                                            {priceDiff !== 0 && (
                                              <div style={{ 
                                                fontSize: '12px', 
                                                color: priceDiff > 0 ? '#8E3628' : '#163626',
                                                fontWeight: '600'
                                              }}>
                                                {priceDiff > 0 ? '+' : ''}₹{priceDiff}/night
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {isSelected && (
                                          <div style={{ 
                                            marginTop: '8px', 
                                            fontSize: '12px', 
                                            color: '#975534',
                                            fontWeight: '600' 
                                          }}>
                                            ✓ Selected for exchange
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#5F6B61' }}>
                                  <div style={{ fontSize: '24px', marginBottom: '8px' }}><BedDoubleIcon size="1em" aria-hidden="true" /></div>
                                  <p>No available beds in this room</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* No options available */}
                      {((exchangeType === 'private' && availableRooms.filter(room => room.roomType?.isShared === false || !room.roomType?.isShared).length === 0) || 
                        (exchangeType === 'shared' && availableRooms.filter(room => room.roomType?.isShared === true).length === 0)) && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
                          <div style={{ fontSize: '48px', marginBottom: '16px' }}><HomeIcon size="1em" aria-hidden="true" /></div>
                          <p>No alternative accommodations available</p>
                          <p style={{ fontSize: '14px' }}>
                            All other {exchangeType} rooms are occupied for the booking dates.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Exchange Price Impact */}
              {(calculatingExchangePrice && (selectedNewRoom || selectedNewBed)) && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '20px',
                  marginTop: '24px',
                  backgroundColor: '#F5F3EB',
                  border: '1px solid #E3DFD3',
                  borderRadius: '8px',
                  color: '#5F6B61'
                }}>
                  <div style={{ fontSize: '20px', marginBottom: '8px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
                  Calculating price difference...
                </div>
              )}

              {exchangePriceError && (
                <div style={{
                  backgroundColor: '#F9EEEB',
                  border: '2px solid #A13F2F',
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ fontSize: '20px', marginTop: '2px' }}><XCircleIcon size="1em" aria-hidden="true" /></span>
                    <div>
                      <div style={{ color: '#8E3628', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                        Exchange Not Available
                      </div>
                      <div style={{ color: '#481B14', fontSize: '14px', lineHeight: '1.5' }}>
                        {exchangePriceError}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!calculatingExchangePrice && !exchangePriceError && roomExchangeDifference !== 0 && (selectedNewRoom || selectedNewBed) && (
                <div style={{
                  backgroundColor: roomExchangeDifference > 0 ? '#FAF4E8' : '#F0F3EE',
                  border: `1px solid ${roomExchangeDifference > 0 ? '#C9903C' : '#C4D4C8'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>
                      {roomExchangeDifference > 0 ? '' : ''}
                    </span>
                    <strong style={{ color: roomExchangeDifference > 0 ? '#9E712E' : '#122E20' }}>
                      Price {roomExchangeDifference > 0 ? 'Increase' : 'Decrease'}
                    </strong>
                  </div>
                  <div style={{ 
                    color: roomExchangeDifference > 0 ? '#825D26' : '#0F2619', 
                    fontSize: '16px', 
                    fontWeight: '600' 
                  }}>
                    ₹{Math.abs(roomExchangeDifference)}
                  </div>
                  <div style={{ 
                    color: roomExchangeDifference > 0 ? '#4F3818' : '#0B1D13', 
                    fontSize: '14px', 
                    marginTop: '4px' 
                  }}>
                    This {roomExchangeDifference > 0 ? 'additional amount will be added to' : 'amount will be deducted from'} the total booking cost.
                  </div>
                </div>
              )}

              {/* Exchange Action Buttons */}
              {(selectedNewRoom || selectedNewBed) && !calculatingExchangePrice && !exchangePriceError && (
                <div style={{
                  marginTop: '24px',
                  padding: '20px',
                  backgroundColor: '#F0F3EE',
                  border: '1px solid #1A3E2A',
                  borderRadius: '8px'
                }}>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ color: '#0F2619', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Ready to Exchange
                    </div>
                    <div style={{ color: '#0F2619', fontSize: '14px' }}>
                      {selectedNewRoom && `Exchange to Room ${selectedNewRoom.roomNumber}`}
                      {selectedNewBed && `Exchange to Room ${selectedNewBed.room?.roomNumber} - Bed ${selectedNewBed.bedNumber}`}
                    </div>
                  </div>
                  
                  <div style={mobileButtonRowStyle}>
                    <button
                      onClick={selectedNewRoom ? handlePrivateRoomExchange : handleSharedBedExchange}
                      disabled={calculatingExchangePrice}
                      style={{
                        backgroundColor: calculatingExchangePrice ? '#A5A89B' : '#1A3E2A',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: calculatingExchangePrice ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        ...mobileActionButtonStyle
                      }}
                    >
                      {calculatingExchangePrice ? (
                        <>
                          <span style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid #ffffff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            display: 'inline-block'
                          }}></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          Confirm Exchange
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => {
                        setSelectedNewRoom(null);
                        setSelectedNewBed(null);
                        setRoomExchangeDifference(0);
                        setActiveSection('overview');
                      }}
                      disabled={calculatingExchangePrice}
                      style={{
                        backgroundColor: '#5F6B61',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                          cursor: calculatingExchangePrice ? 'not-allowed' : 'pointer',
                          ...mobileActionButtonStyle
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Course Allocation Section */}
          {activeSection === 'courses' && (
            <div>
              <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                Course Allocation
              </h3>
              
              <div style={{
                backgroundColor: '#F3E6CB',
                border: '1px solid #C9903C',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px' }}><LightbulbIcon size="1em" aria-hidden="true" /></span>
                  <strong style={{ color: '#684A1F' }}>Course Allocation</strong>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: '#4F3818', lineHeight: '1.5' }}>
                  {booking.course 
                    ? `This booking currently includes "${booking.course.name}" (₹${booking.course.fee}). You can change to a different course or remove the course allocation.`
                    : 'This is currently a staycation-only booking. You can add a course to convert it into a course + accommodation package.'
                  }
                </p>
              </div>

              {loadingCourses ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
                  Loading available courses...
                </div>
              ) : (
                <>
                  {/* Current Course (if any) */}
                  {booking.course && (
                    <div style={{ marginBottom: '32px' }}>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        Current Course
                      </h4>
                      <div style={{
                        border: selectedCourse?.id === booking.course.id ? '2px solid #1A3E2A' : '2px solid #E3DFD3',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: selectedCourse?.id === booking.course.id ? '#F0F3EE' : '#F5F3EB'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#24332A' }}>
                            {booking.course.name}
                          </h4>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <div style={{
                              fontSize: '16px',
                              fontWeight: '700',
                              color: '#1A3E2A',
                              padding: '4px 10px',
                              backgroundColor: '#E1E9E2',
                              borderRadius: '6px'
                            }}>
                              ₹{booking.course.fee || 0}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#163626',
                              padding: '2px 8px',
                              backgroundColor: '#E1E9E2',
                              borderRadius: '4px'
                            }}>
                              CURRENT
                            </div>
                          </div>
                        </div>
                        
                        {booking.course.description && (
                          <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#5F6B61', lineHeight: '1.4' }}>
                            {booking.course.description}
                          </p>
                        )}
                        
                        {/* Course Count Editor */}
                        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#FAF4E8', borderRadius: '6px', border: '1px solid #C9903C' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#684A1F' }}>Course Enrollments</span>
                            <CounterInput
                              value={editableCourseCount}
                              onChange={setEditableCourseCount}
                              min={1}
                              max={10}
                            />
                          </div>
                          <div style={{ fontSize: '12px', color: '#4F3818', lineHeight: '1.4' }}>
                            <div>Unit cost: ₹{booking.course.fee || 0}</div>
                            <div style={{ fontWeight: '600', marginTop: '4px' }}>
                              Total course cost: ₹{((booking.course.fee || 0) * editableCourseCount).toFixed(2)}
                              {editableCourseCount !== (booking.courseCount || 1) && (
                                <span style={{ color: '#8E3628', marginLeft: '8px' }}>
                                  (was ₹{((booking.course.fee || 0) * (booking.courseCount || 1)).toFixed(2)})
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Available Courses */}
                  <div>
                    <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                      Available Courses
                    </h4>
                    
                    {courses.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}><Flower2Icon size="1em" aria-hidden="true" /></div>
                        <p>No upcoming courses available</p>
                        <p style={{ fontSize: '14px' }}>
                          Courses will appear here when they are scheduled for future dates.
                        </p>
                      </div>
                    ) : (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                        gap: '16px'
                      }}>
                        {courses.filter(course => course.id !== booking.course?.id).map(course => {
                          const isSelected = selectedCourse?.id === course.id;
                          
                          return (
                            <div
                              key={course.id}
                              style={{
                                border: isSelected ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
                                borderRadius: '8px',
                                padding: '16px',
                                backgroundColor: isSelected ? '#F0F3EE' : 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => handleCourseSelect(course)}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
                                  {course.name}
                                </h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    color: course.fee > 0 ? '#1A3E2A' : '#5F6B61',
                                    padding: '2px 8px',
                                    backgroundColor: isSelected ? '#E1E9E2' : '#EEEBE1',
                                    borderRadius: '4px'
                                  }}>
                                    ₹{course.fee || 0}
                                  </div>
                                  {course.fee === 0 && (
                                    <div style={{
                                      fontSize: '10px',
                                      fontWeight: '600',
                                      color: '#163626',
                                      padding: '2px 6px',
                                      backgroundColor: '#E1E9E2',
                                      borderRadius: '3px'
                                    }}>
                                      FREE
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {course.description && (
                                <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#5F6B61', lineHeight: '1.4' }}>
                                  {course.description.length > 120 
                                    ? course.description.substring(0, 120) + '...' 
                                    : course.description}
                                </p>
                              )}
                              
                              {/* Course Count Editor for Selected New Course */}
                              {isSelected && (
                                <div style={{ 
                                  marginBottom: '12px', 
                                  padding: '8px', 
                                  backgroundColor: '#F0F3EE', 
                                  borderRadius: '4px',
                                  border: '1px solid #C4D4C8'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#0F2619' }}>Course Enrollments</span>
                                    <CounterInput
                                      value={editableCourseCount}
                                      onChange={setEditableCourseCount}
                                      min={1}
                                      max={10}
                                    />
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#0B1D13' }}>
                                    Total: ₹{((course.fee || 0) * editableCourseCount).toFixed(2)}
                                    {editableCourseCount > 1 && ` (${editableCourseCount} × ₹${course.fee || 0})`}
                                  </div>
                                </div>
                              )}
                              
                              <div style={{ fontSize: '12px', color: isSelected ? '#163626' : '#5F6B61', fontWeight: '600' }}>
                                {isSelected ? '✓ Selected for allocation' : 'Click to allocate this course'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Course Allocation Actions */}
                  {booking.course && (
                    <div style={{
                      marginTop: '24px',
                      padding: '16px',
                      backgroundColor: '#F9EEEB',
                      border: '1px solid #E5B3A9',
                      borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '18px' }}><AlertTriangleIcon size="1em" aria-hidden="true" /></span>
                        <strong style={{ color: '#8E3628' }}>Remove Course</strong>
                      </div>
                      <p style={{ margin: 0, fontSize: '14px', color: '#481B14', lineHeight: '1.4' }}>
                        You can remove the current course allocation to convert this back to a staycation-only booking.
                      </p>
                      <button
                        onClick={() => {
                          console.log('Converting to staycation only - removing course');
                          setSelectedCourse(null);
                          // When removing course, set course count to 0 (staycation booking)
                          setEditableCourseCount(0);
                        }}
                        style={{
                          backgroundColor: '#8E3628',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Convert to Staycation Only
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Add-ons Section */}
          {activeSection === 'addons' && (
            <div>
              <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                Add-on Services
              </h3>
              
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
                  Loading add-on services...
                </div>
              ) : (
                <>
                  {/* ── Booking Add-ons (User's Used Services) ── */}
                  {(() => {
                    const bookingAddons = selectedAddons.filter(a => a.bookingAddonId);
                    if (bookingAddons.length === 0) return null;
                    return (
                      <div style={{ marginBottom: '32px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
                            Booked Add-ons
                          </h4>
                          <span style={{
                            fontSize: '12px',
                            padding: '2px 8px',
                            backgroundColor: '#E1E9E2',
                            color: '#122E20',
                            borderRadius: '12px',
                            fontWeight: '600'
                          }}>
                            {bookingAddons.length}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {bookingAddons.map(addon => {
                            const rawAddon = existingBookingAddons.find(ea => ea.id === addon.bookingAddonId);
                            return (
                              <div
                                key={addon.bookingAddonId}
                                style={{
                                  border: '1px solid #E1E9E2',
                                  borderRadius: '8px',
                                  padding: '16px',
                                  backgroundColor: '#F0F3EE',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                      <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
                                        {addon.name}
                                      </h4>
                                      <span style={{
                                        fontSize: '11px',
                                        padding: '2px 6px',
                                        backgroundColor: '#E1E9E2',
                                        color: '#163626',
                                        borderRadius: '4px',
                                        fontWeight: '600'
                                      }}>
                                        Active
                                      </span>
                                    </div>
                                    {addon.addOnService?.description && (
                                      <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#5F6B61', lineHeight: '1.4' }}>
                                        {addon.addOnService.description}
                                      </p>
                                    )}
                                    {/* Linked Item Details */}
                                    {(addon.item || rawAddon?.item) && (
                                      <div style={{
                                        marginBottom: '8px',
                                        padding: '8px 12px',
                                        backgroundColor: '#F0F3EE',
                                        borderRadius: '6px',
                                        border: '1px solid #C4D4C8',
                                        fontSize: '13px'
                                      }}>
                                        <div style={{ fontWeight: '600', color: '#0F2619', marginBottom: '4px' }}>
                                          Linked Item
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', color: '#36433B' }}>
                                          <span><strong>Name:</strong> {(addon.item || rawAddon?.item)?.name}</span>
                                          {(addon.item || rawAddon?.item)?.sku && (
                                            <span><strong>SKU:</strong> {(addon.item || rawAddon?.item).sku}</span>
                                          )}
                                          {(addon.item || rawAddon?.item)?.unitPrice != null && (
                                            <span><strong>Unit Price:</strong> ₹{(addon.item || rawAddon?.item).unitPrice}</span>
                                          )}
                                          {(addon.item || rawAddon?.item)?.quantity != null && (
                                            <span><strong>Stock:</strong> {(addon.item || rawAddon?.item).quantity}</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {/* Pricing Details */}
                                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                                      <div style={{ flex: '0 0 auto' }}>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#5F6B61', marginBottom: '2px' }}>Qty</label>
                                        <input
                                          type="number"
                                          min="1"
                                          value={addon.quantity}
                                          onChange={(e) => handleAddonQuantityChange(addon.bookingAddonId || addon.id, e.target.value)}
                                          style={{
                                            width: '70px',
                                            padding: '4px 8px',
                                            border: '1px solid #D2CDBF',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                          }}
                                        />
                                      </div>
                                      <div style={{ flex: '0 0 auto' }}>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#5F6B61', marginBottom: '2px' }}>Price/unit (₹)</label>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={addon.price}
                                          onChange={(e) => handleAddonPriceChange(addon.bookingAddonId || addon.id, e.target.value)}
                                          style={{
                                            width: '100px',
                                            padding: '4px 8px',
                                            border: '1px solid #D2CDBF',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                          }}
                                        />
                                      </div>
                                      <div style={{ flex: '0 0 auto' }}>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#5F6B61', marginBottom: '2px' }}>Subtotal</label>
                                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#163626', padding: '4px 0' }}>
                                          ₹{(addon.price * addon.quantity).toFixed(2)}
                                        </div>
                                      </div>
                                      {addon.taxAmount > 0 && (
                                        <div style={{ flex: '0 0 auto' }}>
                                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#5F6B61', marginBottom: '2px' }}>Tax</label>
                                          <div style={{ fontSize: '14px', color: '#5F6B61', padding: '4px 0' }}>
                                            ₹{addon.taxAmount?.toFixed(2)}
                                          </div>
                                        </div>
                                      )}
                                      {addon.totalWithTax > 0 && addon.totalWithTax !== addon.totalPrice && (
                                        <div style={{ flex: '0 0 auto' }}>
                                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#5F6B61', marginBottom: '2px' }}>Total (with tax)</label>
                                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#24332A', padding: '4px 0' }}>
                                            ₹{addon.totalWithTax?.toFixed(2)}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {/* Delete button - only for SUPERADMIN and PROPERTYADMIN */}
                                  {(isSuperAdmin || isPropertyAdmin) && (
                                    <button
                                      onClick={() => {
                                        setAddonToRemove({ id: addon.id, bookingAddonId: addon.bookingAddonId, name: addon.name, description: addon.addOnService?.description });
                                        setShowAddonRemovalConfirm(true);
                                      }}
                                      style={{
                                        background: 'none',
                                        border: '1px solid #D48A7C',
                                        borderRadius: '6px',
                                        padding: '6px 10px',
                                        cursor: 'pointer',
                                        color: '#8E3628',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        marginLeft: '12px',
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                      title="Remove this add-on"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── Available Add-ons (Not Yet Added) ── */}
                  {(() => {
                    const bookingAddonServiceIds = selectedAddons.filter(a => a.bookingAddonId).map(a => a.id);
                    const availableAddons = addons.filter(a => !bookingAddonServiceIds.includes(a.id));
                    const newlySelectedIds = selectedAddons.filter(a => !a.bookingAddonId).map(a => a.id);

                    return (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
                            Available Add-ons
                          </h4>
                          {availableAddons.length > 0 && (
                            <span style={{
                              fontSize: '12px',
                              padding: '2px 8px',
                              backgroundColor: '#EEEBE1',
                              color: '#5F6B61',
                              borderRadius: '12px',
                              fontWeight: '600'
                            }}>
                              {availableAddons.length}
                            </span>
                          )}
                        </div>
                        {availableAddons.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '24px', color: '#5F6B61', backgroundColor: '#F5F3EB', borderRadius: '8px' }}>
                            <p style={{ margin: 0 }}>All available services are already added to this booking.</p>
                          </div>
                        ) : (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '16px'
                          }}>
                            {availableAddons.map(addon => {
                              const selectedAddon = selectedAddons.find(a => a.id === addon.id);
                              const isSelected = !!selectedAddon;
                              
                              return (
                                <div
                                  key={addon.id}
                                  style={{
                                    border: isSelected ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: isSelected ? '#F0F3EE' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onClick={() => handleAddonToggle(addon)}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
                                      {addon.name}
                                    </h4>
                                    <div style={{
                                      fontSize: '16px',
                                      fontWeight: '700',
                                      color: addon.price ? '#1A3E2A' : '#5F6B61',
                                      padding: '2px 8px',
                                      backgroundColor: isSelected ? '#E1E9E2' : '#EEEBE1',
                                      borderRadius: '4px'
                                    }}>
                                      {addon.price ? `₹${addon.price}` : 'Custom Price'}
                                    </div>
                                  </div>
                                  
                                  {addon.description && (
                                    <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#5F6B61', lineHeight: '1.4' }}>
                                      {addon.description}
                                    </p>
                                  )}
                                  
                                  {isSelected && (
                                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E1E9E2' }}>
                                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                                        <div style={{ flex: 1 }}>
                                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                                            Quantity:
                                          </label>
                                          <input
                                            type="number"
                                            min="1"
                                            value={selectedAddon.quantity}
                                            onChange={(e) => handleAddonQuantityChange(addon.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                              width: '100%',
                                              padding: '4px 8px',
                                              border: '1px solid #D2CDBF',
                                              borderRadius: '4px',
                                              fontSize: '14px'
                                            }}
                                          />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                                            Price per unit (₹):
                                          </label>
                                          <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={selectedAddon.price}
                                            onChange={(e) => handleAddonPriceChange(addon.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                              width: '100%',
                                              padding: '4px 8px',
                                              border: '1px solid #D2CDBF',
                                              borderRadius: '4px',
                                              fontSize: '14px'
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#163626', marginTop: '8px', fontWeight: '600' }}>
                                        Subtotal: ₹{(selectedAddon.price * selectedAddon.quantity).toFixed(2)}
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div style={{ marginTop: '8px', fontSize: '12px', color: isSelected ? '#163626' : '#5F6B61' }}>
                                    {isSelected ? '✓ Selected' : 'Click to add'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Other Edits Section */}
          {activeSection === 'other-edits' && (
            <OtherEditsTab
              booking={booking}
              onUpdate={onUpdate}
              onClose={onClose}
            />
          )}

          {/* Payments Section */}
          {activeSection === 'payments' && (
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                marginBottom: isMobile ? '16px' : '24px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '0'
              }}>
                <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '20px', fontWeight: '600', color: '#24332A' }}>
                  Payment Management
                </h3>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  {isSuperAdmin && (
                    <button
                      onClick={() => setIsEditingPaymentAmount(!isEditingPaymentAmount)}
                      style={{
                        backgroundColor: isEditingPaymentAmount ? '#8E3628' : '#A9613C',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <span style={{ fontSize: '16px' }}><CrownIcon size="1em" aria-hidden="true" /></span>
                      {isEditingPaymentAmount ? 'Cancel Edit' : 'Edit Amounts'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleAddPayment}
                    style={{
                      backgroundColor: '#1A3E2A',
                      color: 'white',
                      border: 'none',
                      padding: isMobile ? '10px 12px' : '10px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: isMobile ? '1' : 'none'
                    }}
                  >
                    <span style={{ fontSize: '16px' }}><CreditCardIcon size="1em" aria-hidden="true" /></span>
                    {isMobile ? 'Add Payment' : 'Add Payment'}
                  </button>
                  <button
                    type="button"
                    onClick={handleAddRefund}
                    style={{
                      backgroundColor: '#8E3628',
                      color: 'white',
                      border: 'none',
                      padding: isMobile ? '10px 12px' : '10px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: isMobile ? '1' : 'none'
                    }}
                  >
                    <span style={{ fontSize: '16px' }}><BanknoteIcon size="1em" aria-hidden="true" /></span>
                    {isMobile ? 'Add Refund' : 'Add Refund'}
                  </button>
                </div>
              </div>

              {/* Super Admin Payment Amount Editor */}
              {(isSuperAdmin || isPropertyAdmin) && isEditingPaymentAmount && (
                <div style={{
                  backgroundColor: '#F3E6CB',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #B68235',
                  marginBottom: '24px'
                }}>
                  <div style={{ marginBottom: '16px', fontSize: '14px', fontWeight: '600', color: '#684A1F' }}>
                    Super Admin: Edit Booking Amounts
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                        Total Price (₹)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editableTotalPrice}
                        onChange={(e) => setEditableTotalPrice(parseFloat(e.target.value) || 0)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #D2CDBF',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                        placeholder="Enter total price"
                      />
                      {editableTotalPrice !== (booking.totalPrice || 0) && (
                        <div style={{ fontSize: '12px', color: '#9E712E', marginTop: '4px' }}>
                          Original: ₹{(booking.totalPrice || 0).toFixed(2)}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
                        Final Amount (₹)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editableFinalAmount}
                        onChange={(e) => setEditableFinalAmount(parseFloat(e.target.value) || 0)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #D2CDBF',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                        placeholder="Enter final amount"
                      />
                      {editableFinalAmount !== (booking.finalAmount || booking.totalPrice || 0) && (
                        <div style={{ fontSize: '12px', color: '#9E712E', marginTop: '4px' }}>
                          Original: ₹{(booking.finalAmount || booking.totalPrice || 0).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: '12px', fontSize: '12px', color: '#5F6B61' }}>
                    <strong>Note:</strong> Changes will be applied when you click the save button below.
                  </div>
                </div>
              )}

              {/* Super Admin Save Button in Payment Section */}
              {(isSuperAdmin || isPropertyAdmin) && isEditingPaymentAmount && (
                <div style={{ 
                  marginTop: '16px', 
                  padding: '16px',
                  backgroundColor: '#F5F3EB',
                  borderRadius: '8px',
                  border: '1px solid #E3DFD3',
                  textAlign: 'center'
                }}>
                  <button
                    onClick={handleSuperAdminUpdate}
                    disabled={updatingBooking}
                    style={{
                      backgroundColor: updatingBooking ? '#A5A89B' : '#163626',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: updatingBooking ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {updatingBooking ? (
                      <>
                        <span style={{ 
                          display: 'inline-block',
                          width: '16px',
                          height: '16px',
                          border: '2px solid #ffffff40',
                          borderTop: '2px solid #ffffff',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        Save Payment Amount Changes
                      </>
                    )}
                  </button>
                </div>
              )}
              
              <PaymentHistory 
                key={paymentHistoryKey} // Use key to force refresh
                booking={booking}
                onRefund={handleRefundPayment}
                user={user}
              />
            </div>
          )}

          {/* Audit Trail Section */}
          {activeSection === 'audit-trail' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                  Audit Trail
                </h3>
                <p style={{ margin: 0, color: '#5F6B61', fontSize: '14px' }}>
                  Complete history of all changes and activities for this booking
                </p>
              </div>

              {/* Loading State */}
              {loadingAuditLogs && (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <div style={{ 
                    display: 'inline-block', 
                    width: '40px', 
                    height: '40px', 
                    border: '4px solid #E3DFD3', 
                    borderTop: '4px solid #1A3E2A', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></div>
                  <div style={{ marginTop: '16px', color: '#5F6B61', fontSize: '14px' }}>
                    Loading audit logs...
                  </div>
                </div>
              )}

              {/* Error State */}
              {auditLogsError && !loadingAuditLogs && (
                <div style={{
                  backgroundColor: '#F9EEEB',
                  border: '1px solid #E5B3A9',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#8E3628' }}>
                    <span style={{ fontSize: '18px' }}><AlertTriangleIcon size="1em" aria-hidden="true" /></span>
                    <strong>{auditLogsError}</strong>
                  </div>
                  <button
                    onClick={loadAuditLogs}
                    style={{
                      marginTop: '12px',
                      backgroundColor: '#1A3E2A',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}

              {/* Audit Logs List */}
              {!loadingAuditLogs && !auditLogsError && (
                <div>
                  {auditLogs.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      backgroundColor: '#F5F3EB',
                      borderRadius: '8px',
                      border: '1px solid #E3DFD3'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}><ScrollTextIcon size="1em" aria-hidden="true" /></div>
                      <div style={{ color: '#5F6B61', fontSize: '14px' }}>
                        No audit logs found for this booking
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {auditLogs.map((log, index) => {
                        // Extract meaningful data from metadata
                        const metadata = log.metadata || {};
                        const action = log.action || 'UNKNOWN_ACTION';
                        const user = log.user || {};
                        const timestamp = new Date(log.createdAt);

                        // Get room/bed names from metadata if available
                        const getRoomName = () => {
                          if (metadata.roomName) return metadata.roomName;
                          if (metadata.roomNumber) return `Room ${metadata.roomNumber}`;
                          if (metadata.roomId && bookingDetailsResponse?.data?.bookedRooms) {
                            const room = bookingDetailsResponse.data.bookedRooms.find(r => r.room?.id === metadata.roomId);
                            if (room?.room?.name) return room.room.name;
                          }
                          return null;
                        };

                        const getBedName = () => {
                          if (metadata.bedName) return metadata.bedName;
                          if (metadata.bedNumber) return `Bed ${metadata.bedNumber}`;
                          if (metadata.bedId && bookingDetailsResponse?.data?.bookedBeds) {
                            const bed = bookingDetailsResponse.data.bookedBeds.find(b => b.bed?.id === metadata.bedId);
                            if (bed?.bed?.name) return bed.bed.name;
                          }
                          return null;
                        };

                        // Format action for display
                        const formatAction = (action) => {
                          return action
                            .replace(/_/g, ' ')
                            .toLowerCase()
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');
                        };

                        // Get action icon and color
                        const getActionStyle = (action) => {
                          const actionLower = action.toLowerCase();
                          if (actionLower.includes('created')) return { color: '#163626', bg: '#E1E9E2' };
                          if (actionLower.includes('updated') || actionLower.includes('modified')) return { color: '#163626', bg: '#E1E9E2' };
                          if (actionLower.includes('deleted') || actionLower.includes('removed')) return { color: '#8E3628', bg: '#F2D9D3' };
                          if (actionLower.includes('payment') || actionLower.includes('paid')) return { color: '#975534', bg: '#F1E0D4' };
                          if (actionLower.includes('checkin') || actionLower.includes('check-in')) return { color: '#975534', bg: '#F1E0D4' };
                          if (actionLower.includes('checkout') || actionLower.includes('check-out')) return { color: '#975534', bg: '#F1E0D4' };
                          if (actionLower.includes('extended') || actionLower.includes('extend')) return { color: '#163626', bg: '#E1E9E2' };
                          if (actionLower.includes('refund')) return { color: '#8E3628', bg: '#F2D9D3' };
                          if (actionLower.includes('confirmed')) return { icon: '✓', color: '#163626', bg: '#E1E9E2' };
                          if (actionLower.includes('cancelled')) return { icon: '✕', color: '#8E3628', bg: '#F2D9D3' };
                          return { color: '#5F6B61', bg: '#EEEBE1' };
                        };

                        const style = getActionStyle(action);
                        const roomName = getRoomName();
                        const bedName = getBedName();

                        return (
                          <div
                            key={log.id || index}
                            style={{
                              backgroundColor: 'white',
                              border: '1px solid #E3DFD3',
                              borderRadius: '8px',
                              padding: '16px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                              e.currentTarget.style.borderColor = '#D2CDBF';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.borderColor = '#E3DFD3';
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                              {/* Icon */}
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                backgroundColor: style.bg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                flexShrink: 0
                              }}>
                                <HistoryIcon size="1em" color={style.color} aria-hidden="true" />
                              </div>

                              {/* Content */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Action and timestamp */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '12px' }}>
                                  <div>
                                    <div style={{ 
                                      fontSize: '15px', 
                                      fontWeight: '600', 
                                      color: '#24332A',
                                      marginBottom: '4px'
                                    }}>
                                      {formatAction(action)}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#5F6B61' }}>
                                      {timestamp.toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  </div>
                                  {user.name && (
                                    <div style={{
                                      backgroundColor: '#EEEBE1',
                                      padding: '4px 10px',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      color: '#36433B',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {user.name}
                                    </div>
                                  )}
                                </div>

                                {/* Metadata details */}
                                {(roomName || bedName || Object.keys(metadata).length > 0) && (
                                  <div style={{
                                    backgroundColor: '#F5F3EB',
                                    borderRadius: '6px',
                                    padding: '12px',
                                    fontSize: '13px'
                                  }}>
                                    {roomName && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <span style={{ color: '#5F6B61' }}>Room:</span>{' '}
                                        <span style={{ color: '#24332A', fontWeight: '600' }}>{roomName}</span>
                                      </div>
                                    )}
                                    {bedName && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <span style={{ color: '#5F6B61' }}>Bed:</span>{' '}
                                        <span style={{ color: '#24332A', fontWeight: '600' }}>{bedName}</span>
                                      </div>
                                    )}
                                    {metadata.amount && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <span style={{ color: '#5F6B61' }}>Amount:</span>{' '}
                                        <span style={{ color: '#163626', fontWeight: '600' }}>₹{metadata.amount}</span>
                                      </div>
                                    )}
                                    {metadata.oldValue !== undefined && metadata.newValue !== undefined && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <span style={{ color: '#5F6B61' }}>Change:</span>{' '}
                                        <span style={{ color: '#8E3628', textDecoration: 'line-through' }}>
                                          {JSON.stringify(metadata.oldValue)}
                                        </span>
                                        {' → '}
                                        <span style={{ color: '#163626', fontWeight: '600' }}>
                                          {JSON.stringify(metadata.newValue)}
                                        </span>
                                      </div>
                                    )}
                                    {metadata.status && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <span style={{ color: '#5F6B61' }}>Status:</span>{' '}
                                        <span style={{ 
                                          color: metadata.status === 'CONFIRMED' ? '#163626' : '#5F6B61',
                                          fontWeight: '600' 
                                        }}>
                                          {metadata.status}
                                        </span>
                                      </div>
                                    )}
                                    {metadata.description && (
                                      <div style={{ color: '#5F6B61', marginTop: metadata.amount || metadata.status ? '8px' : '0' }}>
                                        {metadata.description}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Custom Breakdowns Section */}
          {activeSection === 'custom-breakdowns' && isSuperAdmin && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                  Custom Price Breakdowns
                </h3>
                <p style={{ margin: 0, color: '#5F6B61', fontSize: '14px' }}>
                  Apply custom pricing, discounts, or surcharges to specific rooms or beds in this booking
                </p>
              </div>

              {/* Loading State */}
              {loadingCustomBreakdowns && (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <div style={{ 
                    display: 'inline-block', 
                    width: '40px', 
                    height: '40px', 
                    border: '4px solid #E3DFD3', 
                    borderTop: '4px solid #1A3E2A', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></div>
                  <div style={{ marginTop: '16px', color: '#5F6B61', fontSize: '14px' }}>
                    Loading custom breakdowns...
                  </div>
                </div>
              )}

              {/* Content */}
              {!loadingCustomBreakdowns && customBreakdowns && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  
                  {/* Booked Rooms Section */}
                  {customBreakdowns.bookedRooms && customBreakdowns.bookedRooms.length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#24332A', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span><HomeIcon size="1em" aria-hidden="true" /></span> Rooms
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {customBreakdowns.bookedRooms.map((room) => (
                          <div
                            key={room.id}
                            style={{
                              backgroundColor: 'white',
                              border: '1px solid #E3DFD3',
                              borderRadius: '8px',
                              padding: '16px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {/* Room Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                              <div>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#24332A' }}>
                                  Room {room.roomNumber} - {room.roomTypeName}
                                </div>
                                <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '4px' }}>
                                  Base Price: ₹{room.basePrice?.toLocaleString()} • Final: ₹{room.finalAmount?.toLocaleString()}
                                </div>
                              </div>
                              <button
                                onClick={() => handleOpenAddBreakdown('BOOKED_ROOM', room.id, `Room ${room.roomNumber}`)}
                                style={{
                                  backgroundColor: '#1A3E2A',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <span>+</span> Add Breakdown
                              </button>
                            </div>

                            {/* Custom Breakdowns List */}
                            {room.priceBreakdowns && room.priceBreakdowns.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {room.priceBreakdowns.map((breakdown) => (
                                  <div
                                    key={breakdown.id}
                                    style={{
                                      backgroundColor: '#F5F3EB',
                                      borderRadius: '6px',
                                      padding: '12px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#24332A' }}>
                                        {breakdown.name}
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '4px' }}>
                                        <span style={{ 
                                          backgroundColor: '#E3DFD3', 
                                          padding: '2px 8px', 
                                          borderRadius: '4px',
                                          marginRight: '8px'
                                        }}>
                                          {breakdown.breakdownType}
                                        </span>
                                        <span>
                                          {breakdown.calculationType === 'PERCENTAGE' 
                                            ? `${breakdown.value}%` 
                                            : `₹${breakdown.value}`
                                          }
                                        </span>
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <span style={{ 
                                        fontSize: '15px', 
                                        fontWeight: '600',
                                        color: breakdown.amount >= 0 ? '#8E3628' : '#163626'
                                      }}>
                                        {breakdown.amount >= 0 ? '+' : ''}₹{breakdown.amount?.toLocaleString()}
                                      </span>
                                      <button
                                        onClick={() => handleDeleteBreakdown(breakdown.id, breakdown.name)}
                                        style={{
                                          backgroundColor: 'transparent',
                                          border: '1px solid #8E3628',
                                          color: '#8E3628',
                                          padding: '6px 10px',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                ))}
                                <div style={{ 
                                  textAlign: 'right', 
                                  fontSize: '14px', 
                                  fontWeight: '600', 
                                  color: '#24332A',
                                  marginTop: '4px',
                                  paddingTop: '8px',
                                  borderTop: '1px solid #E3DFD3'
                                }}>
                                  Total Breakdown Impact: 
                                  <span style={{ 
                                    color: room.totalBreakdownAmount >= 0 ? '#8E3628' : '#163626',
                                    marginLeft: '8px'
                                  }}>
                                    {room.totalBreakdownAmount >= 0 ? '+' : ''}₹{room.totalBreakdownAmount?.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '20px', 
                                color: '#A5A89B', 
                                fontSize: '13px',
                                backgroundColor: '#F5F3EB',
                                borderRadius: '6px'
                              }}>
                                No custom breakdowns for this room
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Booked Beds Section */}
                  {customBreakdowns.bookedBeds && customBreakdowns.bookedBeds.length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#24332A', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span><BedDoubleIcon size="1em" aria-hidden="true" /></span> Beds
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {customBreakdowns.bookedBeds.map((bed) => (
                          <div
                            key={bed.id}
                            style={{
                              backgroundColor: 'white',
                              border: '1px solid #E3DFD3',
                              borderRadius: '8px',
                              padding: '16px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {/* Bed Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                              <div>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#24332A' }}>
                                  {bed.bedName || `Bed in Room ${bed.roomNumber}`}
                                </div>
                                <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '4px' }}>
                                  Base Price: ₹{bed.basePrice?.toLocaleString()} • Final: ₹{bed.finalAmount?.toLocaleString()}
                                </div>
                              </div>
                              <button
                                onClick={() => handleOpenAddBreakdown('BOOKED_BED', bed.id, bed.bedName || `Bed in Room ${bed.roomNumber}`)}
                                style={{
                                  backgroundColor: '#1A3E2A',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <span>+</span> Add Breakdown
                              </button>
                            </div>

                            {/* Custom Breakdowns List */}
                            {bed.priceBreakdowns && bed.priceBreakdowns.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {bed.priceBreakdowns.map((breakdown) => (
                                  <div
                                    key={breakdown.id}
                                    style={{
                                      backgroundColor: '#F5F3EB',
                                      borderRadius: '6px',
                                      padding: '12px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#24332A' }}>
                                        {breakdown.name}
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '4px' }}>
                                        <span style={{ 
                                          backgroundColor: '#E3DFD3', 
                                          padding: '2px 8px', 
                                          borderRadius: '4px',
                                          marginRight: '8px'
                                        }}>
                                          {breakdown.breakdownType}
                                        </span>
                                        <span>
                                          {breakdown.calculationType === 'PERCENTAGE' 
                                            ? `${breakdown.value}%` 
                                            : `₹${breakdown.value}`
                                          }
                                        </span>
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <span style={{ 
                                        fontSize: '15px', 
                                        fontWeight: '600',
                                        color: breakdown.amount >= 0 ? '#8E3628' : '#163626'
                                      }}>
                                        {breakdown.amount >= 0 ? '+' : ''}₹{breakdown.amount?.toLocaleString()}
                                      </span>
                                      <button
                                        onClick={() => handleDeleteBreakdown(breakdown.id, breakdown.name)}
                                        style={{
                                          backgroundColor: 'transparent',
                                          border: '1px solid #8E3628',
                                          color: '#8E3628',
                                          padding: '6px 10px',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                ))}
                                <div style={{ 
                                  textAlign: 'right', 
                                  fontSize: '14px', 
                                  fontWeight: '600', 
                                  color: '#24332A',
                                  marginTop: '4px',
                                  paddingTop: '8px',
                                  borderTop: '1px solid #E3DFD3'
                                }}>
                                  Total Breakdown Impact: 
                                  <span style={{ 
                                    color: bed.totalBreakdownAmount >= 0 ? '#8E3628' : '#163626',
                                    marginLeft: '8px'
                                  }}>
                                    {bed.totalBreakdownAmount >= 0 ? '+' : ''}₹{bed.totalBreakdownAmount?.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '20px', 
                                color: '#A5A89B', 
                                fontSize: '13px',
                                backgroundColor: '#F5F3EB',
                                borderRadius: '6px'
                              }}>
                                No custom breakdowns for this bed
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Booking Addons Section */}
                  {customBreakdowns.bookingAddOns && customBreakdowns.bookingAddOns.length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#24332A', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span><GiftIcon size="1em" aria-hidden="true" /></span> Add-on Services
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {customBreakdowns.bookingAddOns.map((addon) => (
                          <div
                            key={addon.id}
                            style={{
                              backgroundColor: 'white',
                              border: '1px solid #E3DFD3',
                              borderRadius: '8px',
                              padding: '16px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {/* Addon Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                              <div>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#24332A' }}>
                                  {addon.addOnServiceName}
                                  {addon.itemName && (
                                    <span style={{ color: '#5F6B61', fontWeight: '400', marginLeft: '8px' }}>
                                      • {addon.itemName}
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '4px' }}>
                                  Quantity: {addon.quantity} • Price: ₹{addon.price?.toLocaleString()} • Final: ₹{addon.finalAmount?.toLocaleString()}
                                </div>
                              </div>
                              <button
                                onClick={() => handleOpenAddBreakdown('BOOKING_ADDON', addon.id, addon.itemName ? `${addon.addOnServiceName} - ${addon.itemName}` : addon.addOnServiceName)}
                                style={{
                                  backgroundColor: '#1A3E2A',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <span>+</span> Add Breakdown
                              </button>
                            </div>

                            {/* Custom Breakdowns List */}
                            {addon.priceBreakdowns && addon.priceBreakdowns.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {addon.priceBreakdowns.map((breakdown) => (
                                  <div
                                    key={breakdown.id}
                                    style={{
                                      backgroundColor: '#F5F3EB',
                                      borderRadius: '6px',
                                      padding: '12px',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#24332A' }}>
                                        {breakdown.name}
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '4px' }}>
                                        <span style={{ 
                                          backgroundColor: '#E3DFD3', 
                                          padding: '2px 8px', 
                                          borderRadius: '4px',
                                          marginRight: '8px'
                                        }}>
                                          {breakdown.breakdownType}
                                        </span>
                                        <span>
                                          {breakdown.calculationType === 'PERCENTAGE' 
                                            ? `${breakdown.value}%` 
                                            : `₹${breakdown.value}`
                                          }
                                        </span>
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <span style={{ 
                                        fontSize: '15px', 
                                        fontWeight: '600',
                                        color: breakdown.amount >= 0 ? '#8E3628' : '#163626'
                                      }}>
                                        {breakdown.amount >= 0 ? '+' : ''}₹{breakdown.amount?.toLocaleString()}
                                      </span>
                                      <button
                                        onClick={() => handleDeleteBreakdown(breakdown.id, breakdown.name)}
                                        style={{
                                          backgroundColor: 'transparent',
                                          border: '1px solid #8E3628',
                                          color: '#8E3628',
                                          padding: '6px 10px',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                ))}
                                <div style={{ 
                                  textAlign: 'right', 
                                  fontSize: '14px', 
                                  fontWeight: '600', 
                                  color: '#24332A',
                                  marginTop: '4px',
                                  paddingTop: '8px',
                                  borderTop: '1px solid #E3DFD3'
                                }}>
                                  Total Breakdown Impact: 
                                  <span style={{ 
                                    color: addon.totalBreakdownAmount >= 0 ? '#8E3628' : '#163626',
                                    marginLeft: '8px'
                                  }}>
                                    {addon.totalBreakdownAmount >= 0 ? '+' : ''}₹{addon.totalBreakdownAmount?.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '20px', 
                                color: '#A5A89B', 
                                fontSize: '13px',
                                backgroundColor: '#F5F3EB',
                                borderRadius: '6px'
                              }}>
                                No custom breakdowns for this add-on
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {(!customBreakdowns.bookedRooms || customBreakdowns.bookedRooms.length === 0) &&
                   (!customBreakdowns.bookedBeds || customBreakdowns.bookedBeds.length === 0) &&
                   (!customBreakdowns.bookingAddOns || customBreakdowns.bookingAddOns.length === 0) && (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      backgroundColor: '#F5F3EB',
                      borderRadius: '8px',
                      border: '1px solid #E3DFD3'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}><WalletIcon size="1em" aria-hidden="true" /></div>
                      <div style={{ color: '#5F6B61', fontSize: '14px', marginBottom: '8px' }}>
                        No rooms, beds, or add-ons found in this booking
                      </div>
                      <div style={{ color: '#A5A89B', fontSize: '13px' }}>
                        Custom breakdowns can only be applied to bookings with rooms, beds, or add-on services
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Summary Section */}
          {activeSection === 'summary' && (
            <div>
              <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                Booking Summary
              </h3>
              
              <div style={{
                backgroundColor: '#F5F3EB',
                padding: '24px',
                borderRadius: '8px',
                border: '1px solid #E3DFD3'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#36433B' }}>
                  Price Breakdown
                </h4>
                
                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Original Booking Amount:</span>
                    <span>₹{originalPrice.toFixed(2)}</span>
                  </div>
                  
                  {/* Room/Accommodation Charges */}
                  {(() => {
                    // Calculate accommodation charges based on available data
                    let accommodationCharges = 0;
                    let ratePerNight = 0;
                    let accommodationType = 'Accommodation';
                    
                    // First check if accommodationPrice exists in booking
                    if (booking.accommodationPrice && booking.accommodationPrice > 0) {
                      accommodationCharges = booking.accommodationPrice;
                      ratePerNight = accommodationCharges / calculateNights();
                    }
                    // Otherwise calculate from room/bed pricing
                    else if (booking.room?.roomType?.pricePerNight) {
                      ratePerNight = booking.room.roomType.pricePerNight;
                      accommodationCharges = ratePerNight * calculateNights();
                      accommodationType = `Room ${booking.room.roomNumber}`;
                    }
                    else if (booking.bed?.price) {
                      ratePerNight = booking.bed.price;
                      accommodationCharges = ratePerNight * calculateNights();
                      accommodationType = `Bed ${booking.bed.bedNumber}`;
                    }
                    // Fallback: calculate from totalPrice minus course fee
                    else if (booking.totalPrice) {
                      const courseFee = booking.course ? (booking.course.fee || 0) * (booking.courseCount || 1) : 0;
                      accommodationCharges = booking.totalPrice - courseFee;
                      ratePerNight = accommodationCharges / calculateNights();
                      if (booking.room) {
                        accommodationType = `Room ${booking.room.roomNumber}`;
                      } else if (booking.bed) {
                        accommodationType = `Bed ${booking.bed.bedNumber}`;
                      }
                    }
                    
                    // Only show if we have valid accommodation charges
                    if (accommodationCharges > 0) {
                      return (
                        <>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#36433B', margin: '16px 0 8px 0' }}>
                            Accommodation Charges:
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px' }}>
                            <span>
                              {accommodationType}
                              {' '}({calculateNights()} night{calculateNights() !== 1 ? 's' : ''})
                              {ratePerNight > 0 && (
                                <span style={{ color: '#5F6B61', fontSize: '13px' }}>
                                  {' '}@ ₹{ratePerNight.toFixed(2)}/night
                                </span>
                              )}
                            </span>
                            <span>₹{accommodationCharges.toFixed(2)}</span>
                          </div>
                        </>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Course Changes */}
                  {selectedCourse && selectedCourse.id !== booking.course?.id && (
                    <>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#36433B', margin: '16px 0 8px 0' }}>
                        Course Changes:
                      </div>
                      {booking.course && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px', color: '#8E3628' }}>
                          <span>Remove: {booking.course.name} (×{booking.courseCount || 1})</span>
                          <span>-₹{((booking.course.fee || 0) * (booking.courseCount || 1)).toFixed(2)}</span>
                        </div>
                      )}
                      {selectedCourse && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px', color: '#163626' }}>
                          <span>Add: {selectedCourse.name} (×{editableCourseCount})</span>
                          <span>+₹{((selectedCourse.fee || 0) * editableCourseCount).toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Course Count Changes (same course, different count) */}
                  {selectedCourse && selectedCourse.id === booking.course?.id && editableCourseCount !== booking.courseCount && (
                    <>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#36433B', margin: '16px 0 8px 0' }}>
                        Course Enrollment Changes:
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px', color: '#8E3628' }}>
                        <span>Original: {booking.course.name} (×{booking.courseCount || 1})</span>
                        <span>₹{((booking.course.fee || 0) * (booking.courseCount || 1)).toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px', color: '#163626' }}>
                        <span>Updated: {booking.course.name} (×{editableCourseCount})</span>
                        <span>₹{((booking.course.fee || 0) * editableCourseCount).toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px', color: editableCourseCount > booking.courseCount ? '#163626' : '#8E3628', fontWeight: '600' }}>
                        <span>Course Fee Difference:</span>
                        <span>{editableCourseCount > booking.courseCount ? '+' : ''}₹{(((booking.course.fee || 0) * editableCourseCount) - ((booking.course.fee || 0) * (booking.courseCount || 1))).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  
                  {/* Remove course entirely */}
                  {!selectedCourse && booking.course && (
                    <>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#36433B', margin: '16px 0 8px 0' }}>
                        Course Removal:
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px', color: '#8E3628' }}>
                        <span>Remove: {booking.course.name} (×{booking.courseCount || 1})</span>
                        <span>-₹{((booking.course.fee || 0) * (booking.courseCount || 1)).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  
                  {selectedAddons.length > 0 && (
                    <>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#36433B', margin: '16px 0 8px 0' }}>
                        Add-on Services:
                      </div>
                      {selectedAddons.map(addon => (
                        <div key={addon.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px' }}>
                          <span>{addon.name} (x{addon.quantity})</span>
                          <span>₹{(addon.price * addon.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  
                  {/* Booking Extension */}
                  {extendPrice > 0 && (
                    <>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#36433B', margin: '16px 0 8px 0' }}>
                        Booking Extension:
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px', color: '#163626' }}>
                        <span>
                          Extended to {formatDate(newCheckOutDate)} 
                          ({Math.ceil((new Date(newCheckOutDate) - new Date(booking.checkOut)) / (1000 * 60 * 60 * 24))} additional nights)
                        </span>
                        <span>+₹{extendPrice.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  
                  {/* Room Exchange */}
                  {roomExchangeDifference !== 0 && (selectedNewRoom || selectedNewBed) && (
                    <>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#36433B', margin: '16px 0 8px 0' }}>
                        Room Exchange:
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', paddingLeft: '16px', color: roomExchangeDifference > 0 ? '#B68235' : '#163626' }}>
                        <span>
                          {booking.room?.roomNumber ? `Room ${booking.room.roomNumber}` : 'Current Room'}
                          {booking.bed && ` Bed ${booking.bed.bedNumber}`}
                          {' → '}
                          {selectedNewRoom && `Room ${selectedNewRoom.roomNumber}`}
                          {selectedNewBed && ` Bed ${selectedNewBed.bedNumber}`}
                        </span>
                        <span>{roomExchangeDifference > 0 ? '+' : ''}₹{roomExchangeDifference.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  
                  <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #D2CDBF' }} />
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: '700', color: '#24332A' }}>
                    <span>New Total Amount:</span>
                    <span style={{ color: totalPrice >= originalPrice ? '#163626' : '#8E3628' }}>₹{totalPrice.toFixed(2)}</span>
                  </div>
                  
                  {totalPrice !== originalPrice && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: totalPrice >= originalPrice ? '#163626' : '#8E3628', marginTop: '8px' }}>
                      <span>{totalPrice >= originalPrice ? 'Additional' : 'Reduced'} Amount:</span>
                      <span>{totalPrice >= originalPrice ? '+' : ''}₹{(totalPrice - originalPrice).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* ID Proof Section */}
          {activeSection === 'id-proof' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                  ID Proof Documents
                </h3>
                <p style={{ margin: 0, color: '#5F6B61', fontSize: '14px' }}>
                  Upload and manage ID proof documents for this booking
                </p>
              </div>

              {/* Upload Section */}
              <div style={{
                backgroundColor: '#F5F3EB',
                border: '1px solid #E3DFD3',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#24332A' }}>
                  Upload New ID Proof
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* ID Type Selection */}
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#36433B' 
                    }}>
                      ID Type <span style={{ color: '#8E3628' }}>*</span>
                    </label>
                    <select
                      value={selectedIdType}
                      onChange={(e) => setSelectedIdType(e.target.value)}
                      disabled={uploadingIdProof}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="AADHAAR">Aadhaar Card</option>
                      <option value="PASSPORT">Passport</option>
                      <option value="DRIVERS_LICENSE">Driver License</option>
                      <option value="NATIONAL_ID">National ID</option>
                      <option value="VOTER_ID">Voter ID</option>
                      <option value="PAN_CARD">PAN Card</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  {/* File Input */}
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#36433B' 
                    }}>
                      Select Image <span style={{ color: '#8E3628' }}>*</span>
                    </label>
                    <p style={{ 
                      fontSize: '12px', 
                      color: '#5F6B61', 
                      marginBottom: '8px',
                      marginTop: '-4px'
                    }}>
                      Large images will be automatically compressed to meet the 1MB server limit
                    </p>
                    <input
                      id="id-proof-file-input"
                      type="file"
                      accept="image/*"
                      onChange={handleIdProofFileChange}
                      disabled={uploadingIdProof}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px dashed #D2CDBF',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    />
                    {selectedIdProofFile && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '13px', 
                        color: '#163626',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>✓</span>
                        <span>
                          Selected: {selectedIdProofFile.name} ({(selectedIdProofFile.size / 1024).toFixed(0)} KB)
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Description Input */}
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#36433B' 
                    }}>
                      Description (Optional)
                    </label>
                    <input
                      type="text"
                      value={idProofDescription}
                      onChange={(e) => setIdProofDescription(e.target.value)}
                      placeholder="e.g., Aadhar front side"
                      disabled={uploadingIdProof}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                  </div>

                  {/* Error Message */}
                  {idProofError && (
                    <div style={{
                      backgroundColor: '#F9EEEB',
                      border: '1px solid #E5B3A9',
                      borderRadius: '6px',
                      padding: '12px',
                      color: '#8E3628',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span><AlertTriangleIcon size="1em" aria-hidden="true" /></span>
                      <span>{idProofError}</span>
                    </div>
                  )}

                  {/* Upload Button */}
                  <button
                    onClick={handleUploadIdProof}
                    disabled={!selectedIdProofFile || uploadingIdProof}
                    style={{
                      backgroundColor: (!selectedIdProofFile || uploadingIdProof) ? '#A5A89B' : '#1A3E2A',
                      color: 'white',
                      border: 'none',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: (!selectedIdProofFile || uploadingIdProof) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {uploadingIdProof ? (
                      <>
                        <div style={{ 
                          width: '14px', 
                          height: '14px', 
                          border: '2px solid white', 
                          borderTop: '2px solid transparent', 
                          borderRadius: '50%', 
                          animation: 'spin 1s linear infinite' 
                        }}></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <span><UploadIcon size="1em" aria-hidden="true" /></span>
                        Upload ID Proof
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Uploaded ID Proofs List */}
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  margin: '0 0 16px 0',
                  flexWrap: 'wrap'
                }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#24332A',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    Uploaded ID Proofs
                    {idProofs.length > 0 && (
                      <span style={{
                        backgroundColor: '#1A3E2A',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {idProofs.length}
                      </span>
                    )}
                  </h4>
                  {!loadingIdProofs && idProofs.length > 0 && (
                    <button
                      onClick={handleDownloadAllIdProofs}
                      disabled={downloadingAllIdProofs}
                      style={{
                        backgroundColor: downloadingAllIdProofs ? '#A5A89B' : '#163626',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: downloadingAllIdProofs ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        opacity: downloadingAllIdProofs ? 0.7 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!downloadingAllIdProofs) {
                          e.target.style.backgroundColor = '#122E20';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!downloadingAllIdProofs) {
                          e.target.style.backgroundColor = '#163626';
                        }
                      }}
                    >
                      {downloadingAllIdProofs ? 'Downloading...' : `Download All (${idProofs.length})`}
                    </button>
                  )}
                </div>

                {/* Loading State */}
                {loadingIdProofs && (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <div style={{ 
                      display: 'inline-block', 
                      width: '40px', 
                      height: '40px', 
                      border: '4px solid #E3DFD3', 
                      borderTop: '4px solid #1A3E2A', 
                      borderRadius: '50%', 
                      animation: 'spin 1s linear infinite' 
                    }}></div>
                    <div style={{ marginTop: '16px', color: '#5F6B61', fontSize: '14px' }}>
                      Loading ID proofs...
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {!loadingIdProofs && idProofs.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    backgroundColor: '#F5F3EB',
                    borderRadius: '8px',
                    border: '1px solid #E3DFD3'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}><ContactIcon size="1em" aria-hidden="true" /></div>
                    <div style={{ color: '#5F6B61', fontSize: '14px', marginBottom: '4px' }}>
                      No ID proofs uploaded yet
                    </div>
                    <div style={{ color: '#A5A89B', fontSize: '13px' }}>
                      Upload ID proof documents using the form above
                    </div>
                  </div>
                )}

                {/* ID Proofs Grid */}
                {!loadingIdProofs && idProofs.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '20px'
                  }}>
                    {idProofs.map((proof) => (
                      <div
                        key={proof.id}
                        style={{
                          backgroundColor: 'white',
                          border: '1px solid #E3DFD3',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.borderColor = '#D2CDBF';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.borderColor = '#E3DFD3';
                        }}
                      >
                        {/* Image Preview */}
                        <div style={{
                          width: '100%',
                          height: '180px',
                          backgroundColor: '#EEEBE1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}>
                          <img
                            src={proof.url}
                            alt={proof.fileName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>

                        {/* Details */}
                        <div style={{ padding: '16px' }}>
                          {/* ID Type Badge */}
                          <div style={{
                            display: 'inline-block',
                            backgroundColor: '#1A3E2A',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            marginBottom: '10px',
                            textTransform: 'uppercase'
                          }}>
                            {proof.idType.replace(/_/g, ' ')}
                          </div>

                          {/* File Name */}
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#24332A',
                            marginBottom: '6px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {proof.fileName}
                          </div>

                          {/* Description */}
                          {proof.description && (
                            <div style={{
                              fontSize: '13px',
                              color: '#5F6B61',
                              marginBottom: '10px',
                              lineHeight: '1.4'
                            }}>
                              {proof.description}
                            </div>
                          )}

                          {/* Meta Info */}
                          <div style={{
                            fontSize: '12px',
                            color: '#A5A89B',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap'
                          }}>
                            <span>{(proof.fileSize / 1024).toFixed(0)} KB</span>
                            <span>•</span>
                            <span>{new Date(proof.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}</span>
                          </div>

                          {/* Action Buttons */}
                          <div style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center'
                          }}>
                            <a
                              href={proof.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                flex: 1,
                                textAlign: 'center',
                                backgroundColor: '#1A3E2A',
                                color: 'white',
                                padding: '8px 4px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                transition: 'background-color 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#163626';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#1A3E2A';
                              }}
                            >
                              View
                            </a>
                            <button
                              onClick={() => handleDownloadIdProof(proof)}
                              disabled={downloadingIdProof === proof.id}
                              title={`Download as ${getIdProofGuestName()}_${getIdProofFileName(proof)}`}
                              style={{
                                flex: 1,
                                textAlign: 'center',
                                backgroundColor: downloadingIdProof === proof.id ? '#A5A89B' : '#163626',
                                color: 'white',
                                padding: '8px 4px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                border: 'none',
                                whiteSpace: 'nowrap',
                                cursor: downloadingIdProof === proof.id ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s ease',
                                opacity: downloadingIdProof === proof.id ? 0.7 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (downloadingIdProof !== proof.id) {
                                  e.target.style.backgroundColor = '#122E20';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (downloadingIdProof !== proof.id) {
                                  e.target.style.backgroundColor = '#163626';
                                }
                              }}
                            >
                              {downloadingIdProof === proof.id ? 'Saving...' : 'Download'}
                            </button>
                            <button
                              onClick={() => handleDeleteIdProof(proof.id)}
                              disabled={deletingIdProof === proof.id}
                              style={{
                                flex: 1,
                                textAlign: 'center',
                                backgroundColor: deletingIdProof === proof.id ? '#D2CDBF' : '#8E3628',
                                color: 'white',
                                padding: '8px 4px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                border: 'none',
                                whiteSpace: 'nowrap',
                                cursor: deletingIdProof === proof.id ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s ease',
                                opacity: deletingIdProof === proof.id ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (deletingIdProof !== proof.id) {
                                  e.target.style.backgroundColor = '#772D21';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (deletingIdProof !== proof.id) {
                                  e.target.style.backgroundColor = '#8E3628';
                                }
                              }}
                            >
                              {deletingIdProof === proof.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {activeSection === 'delete' && (
            <div>
              <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: '#8E3628' }}>
                Delete Booking
              </h3>
              
              {showDeleteConfirmation ? (
                <div style={{
                  backgroundColor: '#F9EEEB',
                  border: '1px solid #E5B3A9',
                  borderRadius: '8px',
                  padding: '24px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ 
                      fontSize: '20px',
                      color: '#8E3628',
                      marginTop: '2px'
                    }}>
                      <AlertTriangleIcon size="1em" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 style={{ 
                        margin: '0 0 12px 0', 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#5F241B' 
                      }}>
                        Confirm Permanent Deletion
                      </h4>
                      <p style={{ 
                        margin: '0 0 16px 0', 
                        fontSize: '14px', 
                        color: '#481B14',
                        lineHeight: '1.6'
                      }}>
                        This will permanently delete the booking and all associated data. This action cannot be undone.
                      </p>
                      <div style={{
                        backgroundColor: '#C9903C',
                        color: '#684A1F',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        marginBottom: '16px'
                      }}>
                        Booking ID: #{booking.id} | Guest: {booking.guestName} | Dates: {formatDate(booking.checkIn)} - {formatDate(booking.checkOut)}
                      </div>
                      <div style={mobileButtonRowStyle}>
                        <button
                          onClick={() => setShowDeleteConfirmation(false)}
                          disabled={deleting}
                          style={{
                            backgroundColor: '#EEEBE1',
                            color: '#36433B',
                            border: '1px solid #D2CDBF',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: deleting ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            ...mobileActionButtonStyle
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handlePermanentDelete}
                          disabled={deleting}
                          style={{
                            backgroundColor: deleting ? '#A5A89B' : '#8E3628',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: deleting ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease',
                            ...mobileActionButtonStyle
                          }}
                        >
                          {deleting ? (
                            <>
                              <span style={{ 
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                border: '2px solid #ffffff40',
                                borderTop: '2px solid #ffffff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}></span>
                              Deleting...
                            </>
                          ) : (
                            <>
                              Permanently Delete
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  backgroundColor: '#F5F3EB',
                  border: '1px solid #E3DFD3',
                  borderRadius: '8px',
                  padding: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '24px' }}><Trash2Icon size="1em" aria-hidden="true" /></div>
                    <div>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#36433B' 
                      }}>
                        Permanent Deletion
                      </h4>
                      <p style={{ 
                        margin: '0 0 16px 0', 
                        fontSize: '14px', 
                        color: '#5F6B61',
                        lineHeight: '1.6'
                      }}>
                        This will permanently remove the booking and all associated data from the system. 
                        This action is irreversible and should only be used when absolutely necessary.
                      </p>
                      <div style={{
                        backgroundColor: '#C9903C',
                        color: '#684A1F',
                        padding: '12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        marginBottom: '20px'
                      }}>
                        <strong>Warning:</strong> This action is only available to Super Administrators and cannot be undone.
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ 
                    borderTop: '1px solid #E3DFD3',
                    paddingTop: '20px'
                  }}>
                    <h5 style={{ 
                      margin: '0 0 12px 0', 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#36433B' 
                    }}>
                      Booking Details:
                    </h5>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px',
                      fontSize: '13px',
                      color: '#5F6B61'
                    }}>
                      <div>Booking ID: <strong>#{booking.id}</strong></div>
                      <div>Guest: <strong>{booking.guestName}</strong></div>
                      <div>Check-in: <strong>{formatDate(booking.checkIn)}</strong></div>
                      <div>Check-out: <strong>{formatDate(booking.checkOut)}</strong></div>
                      <div>Status: <strong>{booking.status}</strong></div>
                      <div>Total Amount: <strong>₹{booking.totalPrice}</strong></div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setShowDeleteConfirmation(true)}
                    style={{
                      backgroundColor: '#8E3628',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      margin: '20px 0 0 0',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#772D21';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#8E3628';
                    }}
                  >
                    Delete Booking Permanently
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E3DFD3',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'white',
          borderRadius: '0 0 12px 12px',
          position: 'relative',
          zIndex: 10001,
          flexShrink: 0
        }}>
          <div style={{ fontSize: '13px', color: '#5F6B61' }}>
            {totalPrice !== originalPrice || selectedStatus !== booking.status || (newCheckOutDate && newCheckOutDate !== booking.checkOut?.split('T')[0]) || (selectedNewRoom && selectedNewRoom.id !== (booking.roomId || booking.room?.id)) || (selectedNewBed && selectedNewBed.id !== (booking.bedId || booking.bed?.id)) ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                {totalPrice !== originalPrice && (
                  <span>New total: <strong style={{ color: totalPrice >= originalPrice ? '#163626' : '#8E3628' }}>₹{totalPrice.toFixed(2)}</strong> ({totalPrice >= originalPrice ? '+' : ''}₹{(totalPrice - originalPrice).toFixed(2)})</span>
                )}
                {newCheckOutDate && newCheckOutDate !== booking.checkOut?.split('T')[0] && (
                  <span> • Extended to {formatDate(newCheckOutDate)}</span>
                )}
                {(selectedNewRoom && selectedNewRoom.id !== (booking.roomId || booking.room?.id)) && (
                  <span> • Room changed to {selectedNewRoom.roomNumber}</span>
                )}
                {(selectedNewBed && selectedNewBed.id !== (booking.bedId || booking.bed?.id)) && (
                  <span> • Bed changed to {selectedNewBed.bedNumber}</span>
                )}
                {selectedStatus !== booking.status && (
                  <span>Status: <strong style={{ color: '#B68235' }}>{booking.status}</strong> → <strong style={{ color: '#163626' }}>{selectedStatus}</strong></span>
                )}
              </div>
            ) : (
              <span>No changes made</span>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#EEEBE1',
                color: '#36433B',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#E3DFD3';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#EEEBE1';
              }}
            >
              Cancel
            </button>
            
            <button
              type="button"
              onClick={handleDownloadInvoice}
              disabled={downloadingInvoice}
              style={{
                backgroundColor: downloadingInvoice ? '#A5A89B' : '#163626',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: downloadingInvoice ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!downloadingInvoice) {
                  e.target.style.backgroundColor = '#122E20';
                }
              }}
              onMouseLeave={(e) => {
                if (!downloadingInvoice) {
                  e.target.style.backgroundColor = '#163626';
                }
              }}
              title={downloadingInvoice ? 'Downloading...' : 'Download Invoice'}
            >
              {downloadingInvoice ? (
                <>
                  <span style={{ 
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    border: '2px solid #ffffff40',
                    borderTop: '2px solid #ffffff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Downloading...
                </>
              ) : (
                <>Download Invoice</>
              )}
            </button>

            <button
              type="button"
              onClick={handleEmailInvoice}
              disabled={sendingEmail}
              style={{
                backgroundColor: sendingEmail ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: sendingEmail ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!sendingEmail) {
                  e.target.style.backgroundColor = '#163626';
                }
              }}
              onMouseLeave={(e) => {
                if (!sendingEmail) {
                  e.target.style.backgroundColor = '#1A3E2A';
                }
              }}
              title={sendingEmail ? 'Sending...' : 'Mail Invoice'}
            >
              {sendingEmail ? (
                <>
                  <span style={{ 
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    border: '2px solid #ffffff40',
                    borderTop: '2px solid #ffffff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Sending...
                </>
              ) : (
                <>Mail Invoice</>
              )}
            </button>

            <button
              type="button"
              onClick={handleWhatsAppInvoice}
              disabled={sendingWhatsApp}
              style={{
                backgroundColor: sendingWhatsApp ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: sendingWhatsApp ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!sendingWhatsApp) {
                  e.currentTarget.style.backgroundColor = '#163626';
                }
              }}
              onMouseLeave={(e) => {
                if (!sendingWhatsApp) {
                  e.currentTarget.style.backgroundColor = '#1A3E2A';
                }
              }}
              title={sendingWhatsApp ? 'Sending...' : 'Send Invoice via WhatsApp'}
            >
              {sendingWhatsApp ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    border: '2px solid #ffffff40',
                    borderTop: '2px solid #ffffff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Sending...
                </>
              ) : (
                <>WhatsApp Invoice</>
              )}
            </button>
            
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || extendPriceError !== '' || exchangePriceError !== ''}
              style={{
                backgroundColor: saving || extendPriceError !== '' || exchangePriceError !== '' ? '#A5A89B' : '#163626',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: saving || extendPriceError !== '' || exchangePriceError !== '' ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: saving || extendPriceError !== '' || exchangePriceError !== '' ? 'none' : 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!saving && extendPriceError === '') {
                  e.target.style.backgroundColor = '#122E20';
                }
              }}
              onMouseLeave={(e) => {
                if (!saving && extendPriceError === '') {
                  e.target.style.backgroundColor = '#163626';
                }
              }}
            >
              {saving ? (
                <>
                  <span><HourglassIcon size="1em" aria-hidden="true" /></span>
                  Saving...
                </>
              ) : (
                <>
                  <span><SaveIcon size="1em" aria-hidden="true" /></span>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Add Payment Modal */}
      {showAddPaymentModal && (
        <AddPaymentModal
          booking={booking}
          onClose={() => setShowAddPaymentModal(false)}
          onPaymentAdded={handlePaymentAdded}
        />
      )}

      {/* Add Refund Modal */}
      {showAddRefundModal && (
        <AddRefundModal
          booking={booking}
          onClose={() => setShowAddRefundModal(false)}
          onRefundAdded={handleRefundAdded}
        />
      )}

      {/* Addon Removal Confirmation Modal */}
      {showAddonRemovalConfirm && addonToRemove && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
            padding: '20px'
          }}
          onClick={cancelAddonRemoval}
        >
          <div 
            style={{
              backgroundColor: '#E9DFCE',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              backgroundColor: '#F9EEEB',
              borderBottom: '1px solid #E5B3A9'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  fontSize: '32px',
                  lineHeight: 1
                }}>
                  <AlertTriangleIcon size="1em" aria-hidden="true" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#5F241B' }}>
                    Remove Add-on Service?
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#8E3628' }}>
                    This action will remove the service from the booking
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              <div style={{
                backgroundColor: '#F3E6CB',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #D8B26A',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#684A1F', marginBottom: '8px' }}>
                  Service to be removed:
                </div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#4F3818', marginBottom: '8px' }}>
                  {addonToRemove.name}
                </div>
                {addonToRemove.description && (
                  <div style={{ fontSize: '13px', color: '#684A1F', marginBottom: '8px' }}>
                    {addonToRemove.description}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#4F3818', marginTop: '12px' }}>
                  {(() => {
                    const matchedAddon = addonToRemove.bookingAddonId
                      ? selectedAddons.find(a => a.bookingAddonId === addonToRemove.bookingAddonId)
                      : selectedAddons.find(a => a.id === addonToRemove.id);
                    return (
                      <>
                        <div>
                          <span style={{ fontWeight: '500' }}>Quantity:</span>{' '}
                          <span style={{ fontWeight: '700' }}>
                            {matchedAddon?.quantity || 1}
                          </span>
                        </div>
                        <div>
                          <span style={{ fontWeight: '500' }}>Price per unit:</span>{' '}
                          <span style={{ fontWeight: '700' }}>
                            ₹{(matchedAddon?.price || 0).toFixed(2)}
                          </span>
                        </div>
                        <div>
                          <span style={{ fontWeight: '500' }}>Total:</span>{' '}
                          <span style={{ fontWeight: '700' }}>
                            ₹{((matchedAddon?.price || 0) * (matchedAddon?.quantity || 1)).toFixed(2)}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div style={{
                backgroundColor: '#F2D9D3',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #E5B3A9',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '13px', color: '#481B14', lineHeight: '1.6' }}>
                  <strong style={{ color: '#5F241B' }}>Important:</strong> Removing this add-on service will:
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Deduct the service amount from the total booking price</li>
                    <li>Remove all associated charges and taxes</li>
                    <li>Update the final amount due immediately</li>
                    <li>Cannot be undone after saving changes</li>
                  </ul>
                </div>
              </div>

              <div style={{
                backgroundColor: '#F0F3EE',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #C4D4C8'
              }}>
                <div style={{ fontSize: '12px', color: '#0F2619', fontStyle: 'italic' }}>
                  <strong>Tip:</strong> If you accidentally remove this service, you can add it back 
                  by clicking on it again before saving changes.
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#F5F3EB',
              borderTop: '1px solid #E3DFD3',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={cancelAddonRemoval}
                style={{
                  backgroundColor: 'white',
                  color: '#36433B',
                  border: '1px solid #D2CDBF',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#EEEBE1';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmAddonRemoval}
                disabled={removingAddon}
                style={{
                  backgroundColor: removingAddon ? '#A5A89B' : '#8E3628',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: removingAddon ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!removingAddon) e.target.style.backgroundColor = '#772D21';
                }}
                onMouseLeave={(e) => {
                  if (!removingAddon) e.target.style.backgroundColor = '#8E3628';
                }}
              >
                <span>{removingAddon ? '' : ''}</span>
                {removingAddon ? 'Removing...' : 'Yes, Remove Service'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Invoice Modal */}
      {showWhatsAppModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10003,
            padding: '20px'
          }}
          onClick={() => { if (!sendingWhatsApp) setShowWhatsAppModal(false); }}
        >
          <div
            style={{
              backgroundColor: '#E9DFCE',
              borderRadius: '12px',
              padding: '28px',
              width: '100%',
              maxWidth: '440px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <span style={{ fontSize: '24px' }}><MessageSquareIcon size="1em" aria-hidden="true" /></span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
                Send Invoice via WhatsApp
              </h3>
            </div>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#5F6B61' }}>
              Confirm or edit the WhatsApp number to send the invoice to.
            </p>

            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
              WhatsApp Number
            </label>
            <input
              type="tel"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value.replace(/[^\d]/g, ''))}
              placeholder="e.g. 919518864166"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                marginBottom: '6px'
              }}
              disabled={sendingWhatsApp}
              autoFocus
            />
            <p style={{ margin: '0 0 20px', fontSize: '12px', color: '#A5A89B' }}>
              Enter country code + number with <strong>no + sign, no spaces, no dashes</strong>.<br />
              Example: <code style={{ backgroundColor: '#EEEBE1', padding: '1px 4px', borderRadius: '3px' }}>919518864166</code> (India 91 + 9518864166)
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowWhatsAppModal(false)}
                disabled={sendingWhatsApp}
                style={{
                  backgroundColor: '#EEEBE1',
                  color: '#36433B',
                  border: 'none',
                  padding: '9px 18px',
                  borderRadius: '7px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: sendingWhatsApp ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendWhatsAppInvoice}
                disabled={sendingWhatsApp || !whatsappNumber}
                style={{
                  backgroundColor: sendingWhatsApp || !whatsappNumber ? '#A5A89B' : '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '9px 18px',
                  borderRadius: '7px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: sendingWhatsApp || !whatsappNumber ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {sendingWhatsApp ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '12px',
                      height: '12px',
                      border: '2px solid #ffffff40',
                      borderTop: '2px solid #ffffff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    Sending...
                  </>
                ) : (
                  <>Send</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Invoice Modal */}
      {showEmailInvoiceModal && (
        <EmailInvoiceModal
          booking={booking}
          onClose={handleCloseEmailModal}
          onSend={handleSendEmail}
        />
      )}

      {/* Add Custom Breakdown Modal */}
      {showAddBreakdownModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
            padding: '20px'
          }}
          onClick={() => setShowAddBreakdownModal(false)}
        >
          <div
            style={{
              backgroundColor: '#E9DFCE',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '600px',
              padding: '24px',
              boxShadow: '0 25px 75px rgba(0, 0, 0, 0.4)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#24332A' }}>
                Add Custom Price Breakdown
              </h3>
              <p style={{ margin: 0, color: '#5F6B61', fontSize: '14px' }}>
                Target: <strong>{selectedBreakdownTarget?.name}</strong>
              </p>
            </div>

            <form onSubmit={handleCreateBreakdown}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Breakdown Name */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                    Breakdown Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={newBreakdown.name}
                    onChange={(e) => setNewBreakdown({ ...newBreakdown, name: e.target.value })}
                    placeholder="e.g., VIP Guest Discount, Special Event Surcharge"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Breakdown Type */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                    Breakdown Type *
                  </label>
                  <select
                    required
                    value={newBreakdown.breakdownType}
                    onChange={(e) => setNewBreakdown({ ...newBreakdown, breakdownType: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="DISCOUNT">Discount</option>
                    <option value="SURCHARGE">Surcharge</option>
                    <option value="TAX">Tax</option>
                    <option value="FEE">Fee</option>
                    <option value="ADJUSTMENT">Adjustment</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                {/* Calculation Type */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                    Calculation Type *
                  </label>
                  <select
                    required
                    value={newBreakdown.calculationType}
                    onChange={(e) => setNewBreakdown({ ...newBreakdown, calculationType: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="FIXED">Fixed Amount</option>
                    <option value="PERCENTAGE">Percentage</option>
                  </select>
                </div>

                {/* Value and Amount */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      {newBreakdown.calculationType === 'PERCENTAGE' ? 'Percentage (%)' : 'Value (₹)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newBreakdown.value}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewBreakdown({ ...newBreakdown, value });
                      }}
                      placeholder={newBreakdown.calculationType === 'PERCENTAGE' ? '10' : '500'}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Final Amount (₹) * 
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={newBreakdown.amount}
                      onChange={(e) => setNewBreakdown({ ...newBreakdown, amount: e.target.value })}
                      placeholder={newBreakdown.breakdownType === 'DISCOUNT' ? '-500' : '500'}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                    <div style={{ fontSize: '11px', color: '#5F6B61', marginTop: '4px' }}>
                      {newBreakdown.breakdownType === 'DISCOUNT' ? 'Use negative for discounts (e.g., -500)' : 'Positive for charges (e.g., 500)'}
                    </div>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                    Reason (Optional)
                  </label>
                  <textarea
                    value={newBreakdown.reason}
                    onChange={(e) => setNewBreakdown({ ...newBreakdown, reason: e.target.value })}
                    placeholder="Explain why this custom breakdown is being applied..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Info Box */}
                <div style={{
                  backgroundColor: '#F0F3EE',
                  border: '1px solid #1A3E2A',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '13px',
                  color: '#0F2619'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>Important Notes:</div>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                    <li>This will modify the final amount for <strong>{selectedBreakdownTarget?.name}</strong> only</li>
                    <li>The booking total will be automatically recalculated</li>
                    <li>All changes are logged in the audit trail</li>
                  </ul>
                </div>
              </div>

              {/* Actions */}
              <div style={{ ...mobileButtonRowStyle, marginTop: '24px', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddBreakdownModal(false)}
                  disabled={savingBreakdown}
                  style={{
                    backgroundColor: 'white',
                    color: '#36433B',
                    border: '1px solid #D2CDBF',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: savingBreakdown ? 'not-allowed' : 'pointer',
                    ...mobileActionButtonStyle
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingBreakdown}
                  style={{
                    backgroundColor: savingBreakdown ? '#A5A89B' : '#1A3E2A',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: savingBreakdown ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    ...mobileActionButtonStyle
                  }}
                >
                  {savingBreakdown ? (
                    <>
                      <div style={{ 
                        width: '14px', 
                        height: '14px', 
                        border: '2px solid white', 
                        borderTop: '2px solid transparent', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite' 
                      }}></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <span><SaveIcon size="1em" aria-hidden="true" /></span>
                      Create Breakdown
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default EditBookingModal;
